import React, { useState, useEffect, useRef, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import {
  FileSpreadsheet,
  Download,
  Loader2,
  Database,
  CheckCircle2,
  AlertCircle,
  Search,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
  BarChart3,
  ArrowDownToLine,
  ArrowUpFromLine,
  LayoutDashboard,
  Settings,
  ArrowLeft,
  Clock,
  Target,
  List,
  LayoutGrid,
  MapPin,
  X,
  Plus,
  Minus,
  ShieldCheck,
  Calendar,
  Briefcase,
  Check,
  ExternalLink,
  Lock,
  LogOut,
  User,
  ArrowRight,
  Image as ImageIcon,
  Info,
  FileText,
  Hash,
  TrendingUp,
  TrendingDown,
  LineChart,
  Share2,
  AlertTriangle,
  ChevronLeft,
  Filter,
  ClipboardList,
  PlusCircle,
  PackageCheck,
  Warehouse,
  Box,
  Package,
  Edit as EditIcon,
  Menu,
  Trash2,
  Construction,
} from 'lucide-react';
const NewProject = React.lazy(() => import('./tNewProject').then(m => ({ default: m.NewProject })));
const UserManagement = React.lazy(() => import('./src/components/management pages/UserManagement').then(m => ({ default: m.UserManagement })));
const ClientPreferences = React.lazy(() => import('./src/components/management pages/ClientPreferences').then(m => ({ default: m.ClientPreferences })));
const DataExports = React.lazy(() => import('./src/components/pages/DataExports').then(m => ({ default: m.DataExports })));
const Reports = React.lazy(() => import('./src/components/Reports/Reports'));
const ExecutiveReport = React.lazy(() => import('./src/components/Reports/ExecutiveReport'));
const StoreExecutiveReport = React.lazy(() => import('./src/components/Reports/StoreExecutiveReport'));
const StoreExecutiveReportCopy = React.lazy(() => import('./src/components/Reports/StoreExecutiveReportCopy'));
const StockOnHandReport = React.lazy(() => import('./src/components/Reports/StockOnHandReport'));
const StockMovementInReport = React.lazy(() => import('./src/components/Reports/StockMovementInReport'));
const StockMovementOutReport = React.lazy(() => import('./src/components/Reports/StockMovementOutReport'));
const CallPlanning = React.lazy(() => import('./src/components/CallPlanning').then(m => ({ default: m.CallPlanning })));
const ClientCompliance = React.lazy(() => import('./src/components/pages/ClientCompliance').then(m => ({ default: m.ComplianceTab })));
const LosCompliance = React.lazy(() => import('./src/components/pages/Compliance').then(m => ({ default: m.Compliance })));
const SalesCompliance = React.lazy(() => import('./src/components/pages/SalesCompliance').then(m => ({ default: m.SalesCompliance })));

const IssueReports = React.lazy(() => import('./src/components/IssueTracker/IssueReports').then(m => ({ default: m.IssueReports })));
import { ProjectSummary, ReportData, ProjectIssue, WarehouseAvailability as WarehouseAvailabilityType, Project, ProjectStats, UserProfile } from './types';
import { LineProgress } from './src/components/LineProgress';
import { Pagination } from './src/components/Pagination';
import { SearchableDropdown } from './src/components/SearchableDropdown';
import { DateRangePicker } from './src/components/DateRangePicker';

import { fetchAllProjectsSummary, fetchProjectData, updateProjectIssues, updateProjectWarehouseAvailability, supabase, updateProjectDetails, getCurrentUserProfile, getAllClients, fetchProjectsStats, fetchProjectsMetadata } from './src/services/supabaseMock';
import { exportToExcel } from './src/services/excelExport';
import { exportSingleStorePDF, exportProjectPDF, SingleStoreExportData } from './src/services/pdfExport';
import { getProjectQuestionnaire, updateProjectQuestionnaire, getAllProjectQuestionnaires } from './src/services/teamhaven';
import { QuestionnaireBuilder } from './src/components/management pages/QuestionnaireBuilder';
import { getFieldforceProducts } from './src/services/fieldforce';
import { getAllWarehouses } from './src/services/supabaseMock';

// ─── IndexedDB Utility ────────────────────────────────────────────────────────
const DB_NAME = 'VWorxOfflineCache';
const DB_VERSION = 1;
const STORES = {
  PROJECT_DETAILS: 'project_details'
};

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORES.PROJECT_DETAILS)) db.createObjectStore(STORES.PROJECT_DETAILS);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const dbGet = async (store: string, key: string): Promise<any> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(store, 'readonly');
      const request = transaction.objectStore(store).get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (e) {
    console.warn(`[DB] Failed to get ${key} from ${store}:`, e);
    return null;
  }
};

const dbSet = async (store: string, key: string, value: any): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(store, 'readwrite');
      transaction.objectStore(store).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (e) {
    console.error(`[DB] Failed to set ${key} in ${store}:`, e);
  }
};

const dbClear = async (): Promise<void> => {
  const db = await openDB();
  const stores = [STORES.PROJECT_DETAILS];
  stores.forEach(s => {
    const transaction = db.transaction(s, 'readwrite');
    transaction.objectStore(s).clear();
  });
};

const safeSetItem = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (e: any) {
    if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
      console.warn('[Storage] Quota exceeded. Purging non-essential caches...');
      const keys = Object.keys(localStorage);
      keys.forEach(k => {
        if (k.startsWith('vworx_project_detail_')) {
          localStorage.removeItem(k);
        }
      });
      try {
        localStorage.setItem(key, value);
      } catch (retryError) {
        console.error('[Storage] Critical: Failed to save even after purge.', retryError);
      }
    } else {
      console.error('[Storage] Unexpected error:', e);
    }
  }
};

// ─── Warehouse Mapping Utility ────────────────────────────────────────────────
const mapWarehouseToCode = (name: string): string => {
  if (!name) return '';
  const n = name.toLowerCase();
  if (n.includes('johannesburg') || n.includes('jhb') || n.includes('joburg') || n.includes('gauteng')) return 'Jhb';
  if (n.includes('cape town') || n.includes('cpt') || n.includes('western cape') || n.includes('epping') || n.includes('montague')) return 'Cpt';
  if (n.includes('durban') || n.includes('dbn') || n.includes('kzn') || n.includes('pinetown')) return 'Dbn';
  if (n.includes('port elizabeth') || n.includes('pe') || n.includes('gqeberha') || n.includes('elizabeth')) return 'Pe';
  if (n.includes('george') || n.includes('grg')) return 'Grg';
  if (n.includes('east london') || n.includes('el')) return 'El';
  if (n.includes('bloemfontein') || n.includes('bfn') || n.includes('free state') || n.includes('bloem')) return 'Bfn';
  if (n.includes('polokwane') || n.includes('plk') || n.includes('limpopo') || n.includes('pietersburg')) return 'Plk';
  if (n.includes('nelspruit') || n.includes('mbombela') || n.includes('mpumalanga') || n.includes('nls') || n.includes('nsl')) return 'Nsl';
  return name;
};

const getInStockProductIDsFromDB = async (
  warehouseCode: string,
  branchName: string,
  watchedProducts: any[]
): Promise<number[]> => {
  if (!watchedProducts || watchedProducts.length === 0) return [];

  const skuList = watchedProducts.map(p => p.SKU).filter(Boolean);
  if (skuList.length === 0) return [];

  // Normalize lookup: check both warehouse_code and warehouse_name for robustness
  const { data, error } = await supabase
    .from('warehouse_inventory')
    .select('sku, warehouse_code, warehouse_name')
    .eq('branch_name', branchName)
    .in('sku', skuList)
    .gt('qty', 0)
    .or(`warehouse_code.eq."${warehouseCode}",warehouse_name.eq."${warehouseCode}"`);

  if (error) {
    console.error(`[StockQuery] Error for ${warehouseCode}@${branchName}:`, error);
    return [];
  }

  const inStockSKUs = new Set((data || []).map(p => p.sku));

  return watchedProducts
    .filter(wp => inStockSKUs.has(wp.SKU))
    .map(wp => wp.ProductID);
};

const calculateWarehouseStockPerc = async (
  warehouseCode: string,
  branchName: string,
  watchedProducts: any[],
  receivedElementIds: string[] = []
): Promise<{ percentage: number, availableCount: number, totalCount: number, breakdown: any[] }> => {
  if (!watchedProducts || watchedProducts.length === 0) return { percentage: 0, availableCount: 0, totalCount: 0, breakdown: [] };

  const inStockIDs = await getInStockProductIDsFromDB(
    warehouseCode,
    branchName,
    watchedProducts
  );

  // Combine live stock IDs with manually received IDs (avoiding duplicates)
  const readyProductIDs = new Set([
    ...inStockIDs,
    ...(receivedElementIds || []).map(id => String(id))
  ]);

  // Only count those that are actually in the watchedProducts list
  let readyCount = 0;
  const breakdown = watchedProducts.map(wp => {
    const available = readyProductIDs.has(String(wp.ProductID));
    if (available) readyCount++;
    return { product: wp, available };
  });

  return {
    percentage: Math.round((readyCount / watchedProducts.length) * 100),
    availableCount: readyCount,
    totalCount: watchedProducts.length,
    breakdown
  };
};

const getInStockProductIDs = async (
  warehouseCode: string,
  branchName: string,
  watchedProducts: any[]
): Promise<number[]> => {
  return getInStockProductIDsFromDB(warehouseCode, branchName, watchedProducts);
};

// ─── WarehouseStockBadge ──────────────────────────────────────────────────────
const WarehouseStockBadge: React.FC<{
  warehouseCode: string;
  branchName: string;
  watchedProducts: any[];
  receivedElementIds?: string[];
  showPerc?: boolean;
  variant?: 'compact' | 'full';
  cache?: Record<string, Map<string, number>>;
}> = ({ warehouseCode, branchName, watchedProducts, receivedElementIds = [], showPerc = true, variant = 'compact', cache }) => {
  const [localData, setLocalData] = useState<{ percentage: number, availableCount: number, totalCount: number, breakdown: any[] } | null>(null);
  const [loading, setLoading] = useState(false);

  // If cache is provided, use it synchronously
  const getSyncData = () => {
    if (!cache || Object.keys(cache).length === 0) return null;
    if (!watchedProducts || watchedProducts.length === 0) return { percentage: 0, availableCount: 0, totalCount: 0, breakdown: [] };
    // Normalize lookup branch
    const cleanBranch = (branchName || '').split(' + ')[0].trim();
    const key = `${cleanBranch}-${warehouseCode}`;
    const availableSKUs = cache[key];

    // If no cache entry for this warehouse, we try mapping it first (robustness)
    let effectiveAvailableSKUs = availableSKUs;
    if (!effectiveAvailableSKUs) {
      const mappedCode = mapWarehouseToCode(warehouseCode);
      effectiveAvailableSKUs = cache[`${cleanBranch}-${mappedCode}`];
    }

    const safeAvailableSKUs = effectiveAvailableSKUs || new Map<string, number>();

    let availableCount = 0;
    const breakdown = watchedProducts.map(wp => {
      const isManual = (receivedElementIds || []).map(id => String(id)).includes(String(wp.ProductID));
      const cleanSku = (wp.SKU || '').trim();
      const available = isManual || safeAvailableSKUs.has(cleanSku);
      if (available) availableCount++;
      return { product: wp, available };
    });

    return {
      percentage: Math.round((availableCount / watchedProducts.length) * 100),
      availableCount,
      totalCount: watchedProducts.length,
      breakdown
    };
  };

  const cachedData = getSyncData();
  const effectiveData = cachedData !== null ? cachedData : (localData ?? { percentage: 0, availableCount: 0, totalCount: 0, breakdown: [] });
  const isPending = cachedData === null && localData === null;

  const watchedProductsStr = JSON.stringify(watchedProducts);
  const receivedElementIdsStr = JSON.stringify(receivedElementIds || []);

  useEffect(() => {
    // Only fetch individually if no global cache is available
    if ((cache && Object.keys(cache).length > 0) || !watchedProducts || watchedProducts.length === 0) return;

    let isMounted = true;
    const fetchStock = async () => {
      setLoading(true);
      try {
        const data = await calculateWarehouseStockPerc(warehouseCode, branchName, watchedProducts, receivedElementIds);
        if (isMounted) setLocalData(data);
      } catch (e) {
        console.error("Error fetching stock:", e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStock();
    return () => { isMounted = false; };
  }, [warehouseCode, branchName, watchedProductsStr, receivedElementIdsStr, cache]);

  const hasWatched = watchedProducts && watchedProducts.length > 0;
  const isManualReceived = receivedElementIds && (receivedElementIds.length > 0);
  const isFull = hasWatched ? effectiveData.percentage === 100 : isManualReceived;
  const someAvailable = hasWatched ? effectiveData.percentage > 0 : false;

  const style = isFull
    ? 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20'
    : someAvailable
      ? 'bg-orange-50 text-orange-600 border-orange-100'
      : 'bg-slate-50 text-slate-400 border-slate-200';

  if ((loading || isPending) && cachedData === null) {
    return variant === 'compact' ? (
      <div className="px-2 py-0.5 text-[8px] font-bold rounded border bg-slate-50 text-slate-300 border-slate-100 animate-pulse">
        {warehouseCode} ...
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center text-center animate-pulse">
        <span className="text-[13px] font-semibold text-[#5C5D60]">{branchName || warehouseCode}</span>
        <p className="text-[11px] text-slate-200 font-medium mt-0.5">...</p>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={`px-2 py-0.5 ${!isFull ? 'border' : ''} ${!isFull ? style : ''}`}
        style={{
          fontSize: '9px',
          fontWeight: 400,
          borderRadius: '99px',
          boxShadow: 'none',
          background: isFull ? '#f4f9f0' : 'transparent',
          color: isFull ? '#5a8b34' : undefined,
          border: isFull ? 0 : undefined
        }}
      >
        {warehouseCode} {hasWatched && showPerc && `(${effectiveData.availableCount}/${effectiveData.totalCount})`}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <span className="text-[13px] font-semibold text-[#5C5D60]">{branchName || warehouseCode}</span>
      {hasWatched && showPerc && (
        <p className="text-[11px] text-slate-400 font-medium mt-0.5">
          {effectiveData.availableCount}/{effectiveData.totalCount} - {effectiveData.percentage}%
        </p>
      )}
    </div>
  );
};



// ─── ReadinessIndicator ──────────────────────────────────────────────────────
const ReadinessIndicator: React.FC<{
  warehouseCode: string;
  branchName: string;
  product: any;
  receivedElementIds: string[];
  cache?: Record<string, Map<string, number>>;
  onToggle?: () => void;
}> = ({ warehouseCode, branchName, product, receivedElementIds, cache, onToggle }) => {
  const [localQty, setLocalQty] = useState<number | null>(null);

  const expectedQty = product.expected_quantity ?? product.Expected ?? product.Quantity ?? 1;

  useEffect(() => {
    if (cache && Object.keys(cache).length > 0) return; // Use sync check if cache exists

    let isMounted = true;
    const checkStock = async () => {
      const isManual = (receivedElementIds || []).map(id => String(id)).includes(String(product.ProductID));
      if (isManual) {
        if (isMounted) setLocalQty(expectedQty);
        return;
      }

      const liveStock = await getInStockProductIDs(warehouseCode, branchName, [product]);
      if (isMounted) setLocalQty(liveStock.length > 0 ? expectedQty : 0);
    };
    checkStock();
    return () => { isMounted = false; };
  }, [warehouseCode, branchName, product, receivedElementIds, cache, expectedQty]);

  const getSyncQty = () => {
    if (!cache || Object.keys(cache).length === 0) return null;
    const isManual = (receivedElementIds || []).map(id => String(id)).includes(String(product.ProductID));
    if (isManual) return expectedQty;

    const cleanBranch = (branchName || '').split(' + ')[0].trim();
    const key = `${cleanBranch}-${warehouseCode}`;
    const availableSKUs = cache[key];

    // If not found by warehouseCode, try mapping it first (robustness)
    if (!availableSKUs) {
      const mappedCode = mapWarehouseToCode(warehouseCode);
      const keyMapped = `${cleanBranch}-${mappedCode}`;
      const availableSKUsMapped = cache[keyMapped];
      if (availableSKUsMapped) return availableSKUsMapped.get(product.SKU) || (availableSKUsMapped.has(product.SKU) ? 1 : 0);
      return 0;
    }

    return availableSKUs.get(product.SKU) || (availableSKUs.has(product.SKU) ? 1 : 0);
  };

  const syncQty = getSyncQty();
  const deliveredQty = syncQty !== null ? syncQty : localQty;

  if (deliveredQty === null) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[9px] bg-slate-50 text-slate-300 animate-pulse">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
        <span className="text-[11px] font-semibold">...</span>
      </div>
    );
  }

  const isReady = deliveredQty >= expectedQty;
  const isPartial = deliveredQty > 0 && deliveredQty < expectedQty;

  return (
    <div
      onClick={(e) => {
        if (onToggle) {
          e.stopPropagation();
          onToggle();
        }
      }}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-[9px] transition-all ${onToggle ? 'cursor-pointer hover:scale-105 active:scale-95' : ''} ${isReady ? 'bg-[#96c36d]/10 text-[#5a8b34]' : isPartial ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-500'}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${isReady ? 'bg-[#96c36d]' : isPartial ? 'bg-blue-400' : 'bg-amber-400 animate-pulse'}`} />
      <span className="text-[11px] font-semibold">{deliveredQty}/{expectedQty}</span>
    </div>
  );
};

// ─── Display/POS Availability Tab Component ──────────────────────────────────────────
const ElementReadinessTab: React.FC<{
  project: Project;
  onSave?: (availability: WarehouseAvailabilityType[]) => Promise<void>;
  stockCache?: Record<string, Map<string, number>>;
}> = ({ project, onSave, stockCache }) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit mode state — mirrors NewProject.tsx pattern
  const [supabaseWarehouses, setSupabaseWarehouses] = useState<{ id: string; name: string; code: string }[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [watchedProducts, setWatchedProducts] = useState<{ ProductID: string; Name: string; SKU: string; warehouses: string[] }[]>([]);
  const [ffProducts, setFfProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    let isMounted = true;
    if (supabaseWarehouses.length === 0) {
      getAllWarehouses().then(({ data }) => {
        if (isMounted && data) {
          setSupabaseWarehouses(data);
        }
      });
    }
    return () => { isMounted = false; };
  }, []);

  // The branch name comes from the project's existing warehouse_availability, but will be stateful so we can fall back to the Client's default branch if missing.
  const [activeBranch, setActiveBranch] = useState(() => {
    const primary = project.warehouse_availability?.[0]?.branch_name;
    if (primary) return primary;
    // Fallback logic for branch will be handled in a useEffect
    return '';
  });

  const [availableBranches, setAvailableBranches] = useState<string[]>([]);

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingWarehouses(true);
      try {
        // 1. Get all unique decorated branch names from inventory
        const { data: invData } = await supabase
          .from('warehouse_inventory')
          .select('branch_name');

        const decoratedNames = [...new Set((invData || []).map(i => i.branch_name))];

        // 2. Get the client's associated branches
        const clientName = project.client || project.description;
        const { data: clientData } = await supabase
          .from('clients')
          .select('associated_branches')
          .ilike('name', clientName)
          .single();

        const cleanBranches = clientData?.associated_branches || [];

        // 3. Match them up
        const matched = decoratedNames.filter(dec => {
          const clean = dec.split(' + ')[0].trim();
          return cleanBranches.includes(clean);
        });

        setAvailableBranches(matched.length > 0 ? matched : decoratedNames);
        if (!activeBranch && matched.length > 0) {
          setActiveBranch(matched[0]);
        }
      } catch (e) {
        console.error("[Readiness] Failed to match branches:", e);
      } finally {
        setLoadingWarehouses(false);
      }
    };
    loadBranches();
  }, [project.client, project.description]);

  // Open edit mode: hydrate local state from saved warehouse_availability
  const openEdit = async () => {
    setEditMode(true);
    setLoadingWarehouses(true);
    setLoadingProducts(true);

    const withTimeout = <T,>(prom: Promise<T>, ms: number, label: string): Promise<T> => {
      return Promise.race([
        prom,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} API timed out after ${ms}ms`)), ms))
      ]);
    };

    try {
      // 1. Load warehouses from Supabase
      const { data: whData } = await withTimeout(getAllWarehouses(), 12000, "Warehouses");
      setSupabaseWarehouses(whData || []);

      // 2. Derive currently selected warehouse codes from saved data
      const existing = project.warehouse_availability || [];
      const existingCodes = existing.filter(w => w.is_expecting).map(w => w.warehouse_code);
      setSelectedWarehouses(existingCodes);

      // 3. Flatten existing watched_products back into per-product warehouse assignments
      const productMap = new Map<string, { ProductID: string; Name: string; SKU: string; warehouses: string[] }>();
      existing.forEach(wh => {
        (wh.watched_products || []).forEach((p: any) => {
          if (!productMap.has(p.ProductID)) {
            productMap.set(p.ProductID, { ProductID: p.ProductID, Name: p.Name, SKU: p.SKU, warehouses: [] });
          }
          if (wh.is_expecting) {
            productMap.get(p.ProductID)!.warehouses.push(wh.warehouse_code);
          }
        });
      });
      setWatchedProducts(Array.from(productMap.values()));

      let currentBranch = activeBranch;

      // If branch name is missing, attempt to fetch it dynamically via the project's client name (description)
      if (!currentBranch && project.description) {
        const { data: clientsData } = await withTimeout(getAllClients(), 12000, "Clients");
        const linkedClient = clientsData.find((c: any) => c.name === project.description);
        if (linkedClient && linkedClient.associated_branches && linkedClient.associated_branches.length > 0) {
          currentBranch = linkedClient.associated_branches[0];
          setActiveBranch(currentBranch);
        }
      }

      // 4. Load products from Fieldforce if we have a branch
      if (currentBranch) {
        const pRes = await withTimeout(getFieldforceProducts(currentBranch), 15000, "Fieldforce");
        let extracted: any[] = [];
        if (pRes?.data && Array.isArray(pRes.data)) extracted = pRes.data.flatMap((c: any) => (c.branches || []).flatMap((b: any) => b.products || []));
        else if (pRes?.Items && Array.isArray(pRes.Items)) extracted = pRes.Items;
        else if (Array.isArray(pRes)) extracted = pRes;
        setFfProducts(extracted);
      }
    } catch (e) {
      console.error("openEdit Error:", e);
    } finally {
      setLoadingWarehouses(false);
      setLoadingProducts(false);
    }
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      // Build warehouse_availability in the same shape as NewProject.tsx
      const availability: WarehouseAvailabilityType[] = selectedWarehouses.map(code => {
        const existing = (project.warehouse_availability || []).find(w => w.warehouse_code === code);
        return {
          warehouse_code: code,
          branch_name: activeBranch,
          is_expecting: true,
          received_element_ids: existing?.received_element_ids || [],
          watched_products: watchedProducts
            .filter(p => p.warehouses.includes(code))
            .map(p => ({ ProductID: p.ProductID, Name: p.Name, SKU: p.SKU }))
        };
      });
      await onSave(availability);
      setEditMode(false);
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  const toggleProductWarehouse = (productId: string, whCode: string) => {
    setWatchedProducts(prev => prev.map(p => {
      if (p.ProductID !== productId) return p;
      const has = p.warehouses.includes(whCode);
      return { ...p, warehouses: has ? p.warehouses.filter(c => c !== whCode) : [...p.warehouses, whCode] };
    }));
  };

  const selectAllWarehouses = (productId: string) => {
    setWatchedProducts(prev => prev.map(p => {
      if (p.ProductID !== productId) return p;
      const allSelected = p.warehouses.length === selectedWarehouses.length;
      return { ...p, warehouses: allSelected ? [] : [...selectedWarehouses] };
    }));
  };

  const toggleProduct = (p: any) => {
    const pIdStr = String(p.product_id);
    const isSelected = watchedProducts.some(wp => wp.ProductID === pIdStr);
    if (isSelected) {
      setWatchedProducts(prev => prev.filter(wp => wp.ProductID !== pIdStr));
    } else {
      setWatchedProducts(prev => [...prev, {
        ProductID: pIdStr,
        Name: p.product,
        SKU: p.sku || '',
        warehouses: selectedWarehouses.length === 1 ? [selectedWarehouses[0]] : []
      }]);
    }
  };

  const toggleWarehouse = (code: string) => {
    setSelectedWarehouses(prev => {
      if (prev.includes(code)) {
        // Remove warehouse and also remove it from all products
        setWatchedProducts(wps => wps.map(p => ({ ...p, warehouses: p.warehouses.filter(c => c !== code) })));
        return prev.filter(c => c !== code);
      }
      return [...prev, code];
    });
  };

  // ── Edit Mode ────────────────────────────────────────────────────────────────
  if (editMode) {
    const filteredProducts = ffProducts.filter(p =>
      !productSearch ||
      (p.product || '').toLowerCase().includes(productSearch.toLowerCase()) ||
      (p.sku || '').toLowerCase().includes(productSearch.toLowerCase())
    );

    return (
      <div className="space-y-8 animate-in fade-in duration-300">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-bold text-[#1e2a32]">Edit Display/POS Availability</p>
              <p className="text-[12px] text-slate-400 font-medium">Configure which products to track per warehouse</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setEditMode(false)} className="px-4 py-2 text-[12px] font-semibold text-slate-400 rounded-[9px] hover:text-slate-600">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !onSave} className="px-5 py-2 bg-[#96c36d] text-white text-[12px] font-bold rounded-[9px] hover:bg-[#86b160] flex items-center gap-2 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>

        {/* Step 1: Warehouse selection */}
        <div>
          <div className="flex items-center gap-3 mb-4 border-b border-[#e9e9e9] pb-4">
            <h4 className="text-sm font-semibold text-[#1e2a32]">Required warehouses</h4>
          </div>
          {loadingWarehouses ? (
            <div className="flex items-center gap-3 text-slate-300">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-[12px] font-semibold">Loading warehouses...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {supabaseWarehouses.map((w) => (
                <label key={w.id} className={`flex flex-col gap-2 p-4 rounded-[9px] transition-all cursor-pointer bg-white ${selectedWarehouses.includes(w.code) ? 'ring-2 ring-[#96c36d]' : ''
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-[#1e2a32]">{w.name}</span>
                    <input
                      type="checkbox"
                      checked={selectedWarehouses.includes(w.code)}
                      onChange={() => toggleWarehouse(w.code)}
                      className="w-4 h-4 rounded-[3px] border-slate-300 text-[#96c36d] focus:ring-[#96c36d]"
                    />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-400">{w.code}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Step 2: Product selection (same two-panel layout as NewProject) */}
        <div>
          <div className="flex items-center justify-between mb-4 border-b border-[#e9e9e9] pb-4">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold text-[#1e2a32]">Watched products (POS elements)</h4>
            </div>
            <div className="text-[11px] font-semibold text-slate-400 bg-white px-3 py-1 rounded-[9px]">
              {watchedProducts.length} products to track
            </div>
          </div>
          {!activeBranch && (
            <div className="bg-amber-50 rounded-[9px] p-4 text-[12px] font-semibold text-amber-600 mb-4">
              No branch linked to this project — product list unavailable
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left: search + product list */}
            <div className="bg-white p-6 rounded-[9px] space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <input
                  type="text"
                  placeholder={activeBranch ? "Search products by name or SKU..." : "Unavailable..."}
                  className="w-full h-11 bg-[#fbfbfa] rounded-[9px] px-4 pl-10 text-sm font-medium text-[#1e2a32] focus:ring-1 focus:ring-[#96c36d] outline-none disabled:bg-slate-50 disabled:text-slate-400"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  disabled={!activeBranch}
                />
              </div>
              <div className="h-[360px] overflow-y-auto custom-scrollbar bg-[#fbfbfa] rounded-[9px]">
                {loadingProducts ? (
                  <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-400">
                    <Loader2 className="w-7 h-7 animate-spin opacity-20" />
                    <p className="text-[12px] font-semibold">Fetching products...</p>
                  </div>
                ) : ffProducts.length === 0 ? (
                  <div className="p-10 text-center space-y-2">
                    <Box className="w-8 h-8 text-slate-100 mx-auto" />
                    <p className="text-[12px] font-semibold text-slate-400">No products found</p>
                    {!activeBranch && <p className="text-[11px] text-slate-300">Set a branch on the project first</p>}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {filteredProducts.map((p: any, idx: number) => {
                      const pIdStr = String(p.product_id);
                      const isSelected = watchedProducts.some(wp => wp.ProductID === pIdStr);
                      return (
                        <div key={`${pIdStr}-${idx}`} className="p-4 flex items-center justify-between hover:bg-slate-50/50">
                          <div className="flex-1 min-w-0 pr-4">
                            <p className="text-sm font-semibold text-[#1e2a32] truncate">{p.product}</p>
                            <p className="text-[11px] font-medium text-slate-400 mt-0.5">{p.sku || 'No SKU'}</p>
                          </div>
                          <button
                            onClick={() => toggleProduct(p)}
                            className={`px-4 py-1.5 rounded-[9px] text-[11px] font-bold transition-all ${isSelected ? 'bg-slate-200 text-slate-500' : 'bg-[#96c36d]/10 text-[#5a8b34] hover:bg-[#96c36d] hover:text-white'
                              }`}
                          >
                            {isSelected ? 'Remove' : 'Select'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right: warehouse assignment per product */}
            <div className="space-y-4">
              <h5 className="text-[12px] font-semibold text-slate-400">Tracking configuration</h5>
              {watchedProducts.length === 0 ? (
                <div className="rounded-[9px] bg-white p-10 text-center">
                  <Box className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-xs text-slate-400 font-medium">Select products from the left to configure warehouse monitoring</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {watchedProducts.map((wp) => (
                    <div key={wp.ProductID} className="bg-white rounded-[9px] p-5 space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-bold text-[#1e2a32]">{wp.Name}</p>
                          <p className="text-[11px] font-medium text-slate-400 mt-1">{wp.SKU}</p>
                        </div>
                        <button
                          onClick={() => setWatchedProducts(prev => prev.filter(x => x.ProductID !== wp.ProductID))}
                          className="text-slate-300 hover:text-red-400 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-[11px] font-semibold text-slate-400">Assign to warehouses</label>
                          <button
                            onClick={() => selectAllWarehouses(wp.ProductID)}
                            className="text-[11px] font-semibold text-[#96c36d] hover:underline"
                          >
                            {wp.warehouses.length === selectedWarehouses.length ? 'Clear all' : 'Select all active'}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedWarehouses.map(whCode => (
                            <button
                              key={whCode}
                              onClick={() => toggleProductWarehouse(wp.ProductID, whCode)}
                              className={`px-3 py-1 text-[11px] font-semibold rounded-[9px] transition-all ${wp.warehouses.includes(whCode)
                                ? 'bg-[#96c36d] text-white'
                                : 'bg-[#fbfbfa] text-slate-400 hover:text-slate-500'
                                }`}
                            >
                              {whCode}
                            </button>
                          ))}
                          {selectedWarehouses.length === 0 && (
                            <p className="text-[12px] text-amber-500 italic">Select warehouses above first</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Read Mode ────────────────────────────────────────────────────────────────
  const readAvailability = project.warehouse_availability || [];
  const activeWarehouses = readAvailability.filter(wh => wh.is_expecting);
  const elementsMap = new Map<string, { product: any, warehouseConfigs: any[] }>();
  readAvailability.forEach(wh => {
    (wh.watched_products || []).forEach(product => {
      const pid = product.ProductID;
      if (!elementsMap.has(pid)) elementsMap.set(pid, { product, warehouseConfigs: [] });
      elementsMap.get(pid)!.warehouseConfigs.push(wh);
    });
  });
  const elementsList = Array.from(elementsMap.values());

  const handleToggleManual = async (warehouseCode: string, productId: string) => {
    if (!onSave) return;
    const current = project.warehouse_availability || [];
    const updated = current.map(wh => {
      if (wh.warehouse_code !== warehouseCode) return wh;
      const ids = [...(wh.received_element_ids || [])];
      const idx = ids.indexOf(productId);
      if (idx > -1) ids.splice(idx, 1);
      else ids.push(productId);
      return { ...wh, received_element_ids: ids };
    });
    await onSave(updated);
  };

  return (
    <div className="bg-[#fbfbfa] rounded-[9px]">
      <div className="px-[15px] pt-[30px] pb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-[#1e2a32]">PoS Readiness/ Availability</h3>
        </div>
        {onSave && (
          <button
            onClick={openEdit}
            className="flex items-center gap-2 px-4 py-2 rounded-[9px] text-[12px] font-semibold text-slate-500 hover:text-[#96c36d] hover:bg-[#96c36d]/5 transition-all"
          >
            <EditIcon className="w-3.5 h-3.5" /> Manage setup
          </button>
        )}
      </div>
      <hr className="border-[#e9e9e9] mx-[15px]" />

      <div className="px-[15px] pt-[24px] pb-[15px]">
        {elementsList.length === 0 ? (
          <div className="bg-white p-20 rounded-[9px] text-center space-y-4">
            <Package className="w-12 h-12 text-slate-200 mx-auto" />
            <p className="text-[13px] font-medium text-slate-400 leading-loose">No element tracking configured for this project</p>
            {onSave && (
              <button onClick={openEdit} className="mt-4 px-6 py-3 bg-[#1e2a32] hover:bg-[#2f3d47] text-white text-[12px] font-bold rounded-[9px] transition-all">
                Configure element tracking
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-[9px] overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-0">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#fbfbfa]">
                    <th className="px-8 py-5 border-b border-[#E7E7E4] min-w-[350px] sticky left-0 z-20 bg-[#fbfbfa] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] align-middle">
                      <div className="flex flex-col gap-1">
                        <span className="text-[16px] font-bold text-[#1e2a32]">
                          Expected POS <br />
                          <span className="text-[13px] text-slate-500 font-normal">Across {activeWarehouses.length} warehouses</span>
                        </span>
                      </div>
                    </th>
                    {activeWarehouses.map(wh => {
                      const matchedWh = supabaseWarehouses.find(sw => sw.code === wh.warehouse_code);
                      const finalName = matchedWh?.name || wh.branch_name || wh.warehouse_code;
                      return (
                        <th key={wh.warehouse_code} className="px-6 py-5 border-b border-[#E7E7E4] whitespace-nowrap align-middle">
                          <div className="flex justify-center">
                            <WarehouseStockBadge
                              warehouseCode={wh.warehouse_code}
                              branchName={finalName}
                              watchedProducts={wh.watched_products || []}
                              receivedElementIds={wh.received_element_ids}
                              variant="full"
                              cache={stockCache}
                            />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {elementsList.map(({ product, warehouseConfigs }, index) => {
                    const expectedCount = warehouseConfigs.filter(c => c.is_expecting).length;
                    let readyCount = 0;
                    warehouseConfigs.forEach(wh => {
                      if (wh.is_expecting) {
                        const isManual = (wh.received_element_ids || []).map(id => String(id)).includes(String(product.ProductID));
                        if (isManual) {
                          readyCount++;
                        } else if (stockCache) {
                          const cleanBranch = (wh.branch_name || '').split(' + ')[0].trim();
                          let isReady = false;
                          const key = `${cleanBranch}-${wh.warehouse_code}`;
                          const availableSKUs = stockCache[key];
                          if (availableSKUs && availableSKUs.has(product.SKU)) isReady = true;
                          else {
                            // mapping function is external to this component
                            const mappedCode = mapWarehouseToCode(wh.warehouse_code);
                            const keyMapped = `${cleanBranch}-${mappedCode}`;
                            const availableSKUsMapped = stockCache[keyMapped];
                            if (availableSKUsMapped && availableSKUsMapped.has(product.SKU)) isReady = true;
                          }
                          if (isReady) readyCount++;
                        }
                      }
                    });

                    return (
                      <tr key={product.ProductID} className={`group transition-colors ${index % 2 === 0 ? 'bg-[#fbfbfa]' : 'bg-[#f8fafc]'} hover:bg-[#f1f5f9]`}>
                        <td className={`px-8 py-6 border-b border-[rgb(226,231,240)] sticky left-0 z-10 ${index % 2 === 0 ? 'bg-[#fbfbfa]' : 'bg-[#f8fafc]'} group-hover:bg-[#f1f5f9] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]`}>
                          <div className="flex items-start">
                            <div>
                              <p className="text-sm font-semibold text-[#1e2a32] leading-tight mb-1">{product.Name}</p>
                              <div className="flex items-center gap-3">
                                <span className="text-[10px] font-medium text-slate-400">{product.SKU}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        {activeWarehouses.map(wh => {
                          const config = warehouseConfigs.find(c => c.warehouse_code === wh.warehouse_code);
                          const isExpected = config?.is_expecting;
                          return (
                            <td key={wh.warehouse_code} className="px-6 py-6 text-center border-b border-[rgb(226,231,240)]">
                              {!isExpected ? (
                                <span className="text-slate-200 text-[12px]">—</span>
                              ) : (
                                <ReadinessIndicator
                                  warehouseCode={wh.warehouse_code}
                                  branchName={wh.branch_name}
                                  product={product}
                                  receivedElementIds={wh.received_element_ids || []}
                                  cache={stockCache}
                                  onToggle={() => handleToggleManual(wh.warehouse_code, String(product.ProductID))}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Network Status Toast Component ───────────────────────────────────────────
const NetworkStatusToast: React.FC = () => {
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      setIsOnline(window.navigator.onLine);
      const conn = (navigator as any).connection;
      if (conn) {
        setIsSlow(conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g' || (conn.downlink && conn.downlink < 1));
      }
    };
    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    const interval = setInterval(updateStatus, 5000);
    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
      clearInterval(interval);
    };
  }, []);

  if (isOnline && !isSlow) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] animate-in slide-in-from-right-10 duration-500">
      <div className="bg-[#fbfbfa] rounded-[4px] p-4 flex items-center gap-4 max-w-sm">
        <div className={`w-3 h-3 rounded-full shrink-0 ${!isOnline ? 'bg-red-500 animate-pulse' : 'bg-amber-400 animate-pulse'}`} />
        <div className="space-y-0.5 min-w-0">
          <p className="text-xs font-bold text-[#1e2a32] truncate">
            {!isOnline ? 'Check your internet connection' : 'Slow connection detected'}
          </p>
          <p className="text-[10px] text-slate-400 font-medium leading-tight">
            {!isOnline ? 'Slow connection detected.' : 'Syncing might take longer than usual.'}
          </p>
        </div>
      </div>
    </div>
  );
};

let globalOpenPosModal: ((p: any) => void) | null = null;

const PosAvailabilityModal: React.FC<{
  project: any;
  cache?: Record<string, Set<string>>;
  onClose: () => void;
}> = ({ project, cache, onClose }) => {
  const [activeTab, setActiveTab] = useState<'All' | 'Pending' | 'Partial' | 'Full'>('All');
  const [warehouseFilter, setWarehouseFilter] = useState<string[]>([]);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [warehouseMap, setWarehouseMap] = useState<Record<string, string>>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);

  useEffect(() => {
    getAllWarehouses().then(res => {
      if (res.data) {
        const map: Record<string, string> = {};
        res.data.forEach((w: any) => {
          map[w.code] = w.name;
        });
        setWarehouseMap(map);
      }
    });
  }, []);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [onClose]);

  if (!project || typeof document === 'undefined') return null;

  const projectWarehouses = (project.warehouse_availability || []).filter((w: any) => w.is_expecting);

  const allProducts = Array.from(new Set(
    projectWarehouses.flatMap((w: any) => (w.watched_products || []).map((wp: any) => wp.Name))
  )).filter(Boolean).sort() as string[];

  let warehouses = projectWarehouses;
  if (warehouseFilter.length > 0) {
    warehouses = warehouses.filter((w: any) => warehouseFilter.includes(w.warehouse_code));
  }

  const toggleWarehouseFilter = (code: string) => {
    setWarehouseFilter(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const toggleProductFilter = (name: string) => {
    setProductFilter(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const visibleWarehouseOptions = projectWarehouses.filter((w: any) =>
    (warehouseMap[w.warehouse_code] || w.warehouse_code).toLowerCase().includes(warehouseSearch.toLowerCase())
  );

  const visibleProductOptions = allProducts.filter((pName: string) =>
    pName.toLowerCase().includes(productSearch.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0" style={{ zIndex: 99999 }}>
      <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={onClose}></div>
      <div
        className="relative bg-[#fbfbfa] w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[3px] overflow-hidden shadow-xl animate-in zoom-in-95 duration-150"
        onClick={e => {
          e.stopPropagation();
          if (isDropdownOpen) setIsDropdownOpen(false);
        }}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between shrink-0 bg-slate-50/50" style={{ borderBottom: '1px solid #eceff2', margin: '15px', padding: '10px' }}>
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-[#1e2a32]">Detailed POS Availability</h3>
              <p className="text-sm text-slate-500 mt-1">{project.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-8 mt-2 mb-4 shrink-0 border-b border-[#e2e5e7] flex gap-6 text-[14px]">
          {[
            { label: 'All', value: 'All' },
            { label: 'Pending delivery', value: 'Pending' },
            { label: 'Partial delivery', value: 'Partial' },
            { label: 'Delivered in full', value: 'Full' },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value as any)}
              className={`py-2 border-b-2 font-medium transition-colors ${activeTab === tab.value
                ? 'border-[#96c36d] text-[#192229]'
                : 'border-transparent text-slate-500 hover:text-[#192229]'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="px-8 flex items-center gap-4 shrink-0">
          <div className="relative flex-1" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(!isDropdownOpen);
                setIsProductDropdownOpen(false);
              }}
              className="w-full h-9 px-3 text-sm text-left border rounded-[6px] outline-none focus:ring-1 focus:ring-[#96c36d] bg-white flex items-center justify-between"
              style={{ border: '1px solid rgb(236, 239, 242)' }}
            >
              <span className="truncate text-slate-600">
                {warehouseFilter.length === 0 ? 'All Warehouses' : `${warehouseFilter.length} selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e2e5e7] rounded-[6px] shadow-lg z-10 max-h-64 flex flex-col">
                <div className="p-2 border-b border-[#e2e5e7] flex items-center justify-between shrink-0">
                  <input type="text" placeholder="Search..." value={warehouseSearch} onChange={e => setWarehouseSearch(e.target.value)} className="text-sm px-2 py-1 border border-[#e2e5e7] rounded w-2/3" onClick={e => e.stopPropagation()} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setWarehouseFilter(projectWarehouses.map((w: any) => w.warehouse_code))} className="text-xs text-[#96c36d] font-medium hover:underline">All</button>
                    <button type="button" onClick={() => setWarehouseFilter([])} className="text-xs text-slate-500 font-medium hover:underline">None</button>
                  </div>
                </div>
                <div className="overflow-y-auto custom-scrollbar p-1">
                  {visibleWarehouseOptions.map((w: any) => (
                    <label key={w.warehouse_code} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={warehouseFilter.includes(w.warehouse_code)}
                        onChange={() => toggleWarehouseFilter(w.warehouse_code)}
                      />
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${warehouseFilter.includes(w.warehouse_code) ? 'border-[#96c36d] bg-[#96c36d]' : 'border-[#e2e5e7]'}`}>
                        {warehouseFilter.includes(w.warehouse_code) && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <span className="text-sm text-slate-700 truncate">{warehouseMap[w.warehouse_code] || w.warehouse_code}</span>
                    </label>
                  ))}
                  {visibleWarehouseOptions.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No results found</div>}
                </div>
              </div>
            )}
          </div>

          <div className="relative flex-1" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => {
                setIsProductDropdownOpen(!isProductDropdownOpen);
                setIsDropdownOpen(false);
              }}
              className="w-full h-9 px-3 text-sm text-left border rounded-[6px] outline-none focus:ring-1 focus:ring-[#96c36d] bg-white flex items-center justify-between"
              style={{ border: '1px solid rgb(236, 239, 242)' }}
            >
              <span className="truncate text-slate-600">
                {productFilter.length === 0 ? 'All Products' : `${productFilter.length} selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            {isProductDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e2e5e7] rounded-[6px] shadow-lg z-10 max-h-64 flex flex-col">
                <div className="p-2 border-b border-[#e2e5e7] flex items-center justify-between shrink-0">
                  <input type="text" placeholder="Search..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="text-sm px-2 py-1 border border-[#e2e5e7] rounded w-2/3" onClick={e => e.stopPropagation()} />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setProductFilter(allProducts)} className="text-xs text-[#96c36d] font-medium hover:underline">All</button>
                    <button type="button" onClick={() => setProductFilter([])} className="text-xs text-slate-500 font-medium hover:underline">None</button>
                  </div>
                </div>
                <div className="overflow-y-auto custom-scrollbar p-1">
                  {visibleProductOptions.map((pName: string) => (
                    <label key={pName} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={productFilter.includes(pName)}
                        onChange={() => toggleProductFilter(pName)}
                      />
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${productFilter.includes(pName) ? 'border-[#96c36d] bg-[#96c36d]' : 'border-[#e2e5e7]'}`}>
                        {productFilter.includes(pName) && <Check className="w-3 h-3 text-black" />}
                      </div>
                      <span className="text-sm text-slate-700 truncate">{pName}</span>
                    </label>
                  ))}
                  {visibleProductOptions.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No results found</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 pt-4 space-y-6 custom-scrollbar" onClick={() => { setIsDropdownOpen(false); setIsProductDropdownOpen(false); }}>

          {warehouses.map((w: any, idx: number) => {
            let watchedProducts = w.watched_products || [];

            if (productFilter.length > 0) {
              watchedProducts = watchedProducts.filter((wp: any) => productFilter.includes(wp.Name));
            }

            if (watchedProducts.length === 0 && productFilter.length > 0) return null; // hide if filter yields nothing

            let availableCount = 0;
            let expectedTotal = 0;
            let deliveredTotal = 0;

            const cleanBranch = (w.branch_name || '').split(' + ')[0].trim();
            const key = `${cleanBranch}-${w.warehouse_code}`;
            const mappedCode = mapWarehouseToCode(w.warehouse_code || '');
            const safeAvailableSKUs = cache?.[key] || cache?.[`${cleanBranch}-${mappedCode}`] || new Map<string, number>();

            const breakdown = watchedProducts.map((wp: any) => {
              const isManual = (w.received_element_ids || []).map((id: any) => String(id)).includes(String(wp.ProductID));
              const cleanSku = (wp.SKU || '').trim();
              const available = isManual || safeAvailableSKUs.has(cleanSku);
              if (available) availableCount++;

              const qty = wp.expected_quantity ?? wp.Expected ?? wp.Quantity ?? 1;
              expectedTotal += qty;
              if (available) {
                deliveredTotal += qty;
              }

              return { product: wp, available };
            });

            const percentage = expectedTotal === 0 ? 0 : Math.round((deliveredTotal / expectedTotal) * 100);

            if (activeTab === 'Pending' && percentage !== 0) return null;
            if (activeTab === 'Partial' && (percentage === 0 || percentage === 100)) return null;
            if (activeTab === 'Full' && percentage !== 100) return null;

            const radius = 16;
            const circumference = 2 * Math.PI * radius;
            const strokeDashoffset = circumference - (percentage / 100) * circumference;

            const displayName = warehouseMap[w.warehouse_code] || w.warehouse_code;

            return (
              <div key={idx} className="border border-[#e2e5e7] rounded-[9px] overflow-hidden mb-2">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#f8f9fa] border-b border-[#e2e5e7]">
                      <th className="p-[14px_16px] text-[15px] font-bold text-[#192229] border-r border-[#e2e5e7] w-full">
                        <div className="flex items-center justify-between">
                          <span>{displayName}</span>
                          <div className="relative flex items-center justify-center w-10 h-10 ml-2" title={`${percentage}% Delivered`}>
                            <svg className="w-10 h-10 transform -rotate-90">
                              <circle cx="20" cy="20" r={radius} stroke="#eceff2" strokeWidth="3" fill="none" />
                              <circle cx="20" cy="20" r={radius} stroke="rgb(150, 195, 109)" strokeWidth="3" fill="none" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                            </svg>
                            <span className="absolute text-[8px] font-medium text-[#5b6670]">{percentage}%</span>
                          </div>
                        </div>
                      </th>
                      <th className="p-[14px_16px] text-[13px] font-bold text-[#192229] border-r border-[#e2e5e7] whitespace-nowrap">Expected</th>
                      <th className="p-[14px_16px] text-[13px] font-bold text-[#192229] whitespace-nowrap">Delivered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-[14px_16px] text-[14px] text-[#8f9aa3] italic">No products configured</td>
                      </tr>
                    ) : (
                      breakdown.map((item: any, i: number) => (
                        <tr key={i} className="border-b border-[#e2e5e7] last:border-b-0">
                          <td className="p-[14px_16px] text-[14px] text-[#2a343c] border-r border-[#e2e5e7]">{item.product.Name}</td>
                          <td className="p-[14px_16px] text-[14px] text-[#2a343c] border-r border-[#e2e5e7]">
                            {item.product.expected_quantity ?? item.product.Expected ?? item.product.Quantity ?? 1}
                          </td>
                          <td className="p-[14px_16px] text-[14px] text-[#2a343c]">
                            {item.available ? (item.product.expected_quantity ?? item.product.Expected ?? item.product.Quantity ?? 1) : 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Pagination constants ─────────────────────────────────────────────────────
// ─── ProjectTable ─────────────────────────────────────────────────────────────
// ─── UI Helpers ───────────────────────────────────────────────────────────────
const ProjectTable: React.FC<{
  projects: ProjectSummary[];
  onOpenProject: (projectId: number) => void;
  maxHeight?: string;
  loading?: boolean;
  stockCache?: Record<string, Map<string, number>>;
}> = ({ projects, onOpenProject, maxHeight = '75vh', loading = false, stockCache }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [posModalProject, setPosModalProject] = useState<any>(null);
  const TABLE_PAGE_SIZE = 10;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Reset page when projects change
  const projectIds = projects.map(p => p.project_id).join(',');
  const [prevProjectIds, setPrevProjectIds] = useState(projectIds);
  if (projectIds !== prevProjectIds) {
    setPrevProjectIds(projectIds);
    if (currentPage !== 1) setCurrentPage(1);
  }

  const totalPages = Math.ceil(projects.length / TABLE_PAGE_SIZE);
  const paginatedProjects = projects.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of the card containing the table and filters
    const card = document.getElementById('project-filters-card');
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (tableContainerRef.current) {
      tableContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (projects.length === 0 && !loading) return (
    <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
      <CheckCircle2 className="w-8 h-8 text-[#96c36d]/30" />
      <p className="text-sm text-slate-400 font-medium tracking-tight">No projects found in this category</p>
    </div>
  );

  return (
    <div ref={tableContainerRef}>
      <div className="w-full">
        <table className="w-full text-left border-separate border-spacing-0">
          <thead className="sticky top-[60px] z-10">
            <tr className="bg-white">
              <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-left">Project name</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px] bg-[#F8FAFC80]">Brief</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px]">CE</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px] bg-[#F8FAFC80]">PO</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px]">Pilot</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px] bg-[#F8FAFC80]">Invoiced</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px]">Targets</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[80px] bg-[#F8FAFC80]">Issues</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#e7e7e4] text-center w-[90px]">Completed</th>
              <th className="px-2 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-center w-[120px]">Progress</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProjects.map((p, index) => (
              <tr key={p.project_id} onClick={() => onOpenProject(p.project_id)}
                className="cursor-pointer group transition-colors align-top bg-white hover:bg-slate-50/50">
                <td
                  className="px-4 pr-8 pt-6 border-b border-[#e7e7e4] min-w-[200px] whitespace-normal"
                  style={{ paddingBottom: (p.warehouse_availability && Array.isArray(p.warehouse_availability) && p.warehouse_availability.some(w => w.is_expecting)) ? '45px' : '24px' }}
                >
                  <div className="font-semibold text-[#1e2a32] text-sm leading-snug group-hover:text-[#96c36d] transition-colors break-words">{p.name}</div>
                  <div className="text-[9px] text-slate-400 mt-1 font-normal truncate">{p.description || 'Client not specified'}</div>
                  {/* Warehouse badges */}
                  {p.warehouse_availability && Array.isArray(p.warehouse_availability) && p.warehouse_availability.some(w => w.is_expecting) && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1.5 mb-[10px]">
                        <p className="text-[12px] font-normal text-slate-700">PoS Readiness/ Availability</p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log("Info icon clicked for:", p.name);
                            if (globalOpenPosModal) globalOpenPosModal(p);
                          }}
                          className="p-1 -m-1 rounded-full hover:bg-slate-100 transition-colors z-10 relative"
                        >
                          <Info className="w-3.5 h-3.5 text-slate-400 cursor-pointer hover:text-slate-600 transition-colors" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5" style={{ position: 'absolute' }}>
                        {p.warehouse_availability.filter(w => w.is_expecting).map((warehouse, widx) => (
                          <div
                            key={`${warehouse.warehouse_code}-${widx}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (globalOpenPosModal) globalOpenPosModal(p);
                            }}
                            className="cursor-pointer"
                          >
                            <WarehouseStockBadge
                              warehouseCode={warehouse.warehouse_code}
                              branchName={(warehouse as any).branch_name}
                              watchedProducts={(warehouse as any).watched_products || []}
                              receivedElementIds={warehouse.received_element_ids}
                              cache={stockCache}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </td>
                {[{ key: 'brief_document_url' }, { key: 'cost_estimate_document_url' }, { key: 'purchase_order_document_url' }].map((doc) => {
                  const isReceived = !!p[doc.key as keyof ProjectSummary];
                  const isGray = doc.key === 'brief_document_url' || doc.key === 'purchase_order_document_url';
                  return (
                    <td key={doc.key} className={`px-4 py-6 text-center border-b border-[#e7e7e4] align-top ${isGray ? 'bg-[#F8FAFC80]' : ''} ${doc.key === 'brief_document_url' ? '' : ''}`}>
                      <div className="pt-[22px] flex justify-center">
                        <div className={`w-4 h-4 rounded-full transition-all ${isReceived ? 'bg-[#9cc16b]' : 'bg-[rgb(232,68,66)]'}`} />
                      </div>
                    </td>
                  );
                })}
                <td className="px-4 py-6 text-center border-b border-[#e7e7e4] align-top">
                  <div className="pt-[22px] flex justify-center">
                    <PilotBadge pilot={p.pilot_installation_required} compact={true} />
                  </div>
                </td>
                <td className="px-4 py-6 text-center border-b border-[#e7e7e4] align-top bg-[#F8FAFC80]">
                  <div className="pt-[22px] flex justify-center">
                    <div className={`w-4 h-4 rounded-full transition-all ${p.invoiced ? 'bg-[#9cc16b]' : 'bg-[#e84442]'}`} />
                  </div>
                </td>
                <td className="px-4 py-6 text-center border-b border-[#e7e7e4] align-top">
                  <div className="pt-5 flex justify-center">
                    {p.isStatsLoading
                      ? <span className="inline-block w-4 h-5 bg-slate-100 rounded-[4px] animate-pulse" />
                      : <span className="text-[13px] font-bold text-[#1e2a32]">{p.stats.totalCalls}</span>
                    }
                  </div>
                </td>
                <td className="px-4 py-6 text-center border-b border-[#e7e7e4] align-top bg-[#F8FAFC80]">
                  <div className="pt-5 flex justify-center">
                    {p.isStatsLoading
                      ? <span className="inline-block w-4 h-5 bg-slate-100 rounded-[4px] animate-pulse" />
                      : <span className="text-[13px] font-bold text-[#1e2a32]">{p.stats.issues}</span>
                    }
                  </div>
                </td>
                <td className="px-4 py-6 text-center border-b border-[#e7e7e4] align-top">
                  <div className="pt-5 flex justify-center">
                    {p.isStatsLoading
                      ? <span className="inline-block w-4 h-5 bg-slate-100 rounded-[4px] animate-pulse" />
                      : <span className="text-[13px] font-bold text-[#1e2a32]">{p.stats.completed}</span>
                    }
                  </div>
                </td>
                <td className="px-4 py-6 border-b border-[#e7e7e4] w-40 align-top">
                  <div className="pt-1">
                    {!p.isStatsLoading && (
                      <div className="flex flex-col gap-2">
                        <LineProgress percentage={parseInt(p.stats.totalPerc)} />

                        <div className="mt-1">
                          <PaceIndicator stats={p.stats} compact={true} />
                        </div>
                      </div>
                    )}
                    {p.isStatsLoading && (
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full w-1/3 bg-slate-200 rounded-full animate-pulse" /></div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(projects.length / TABLE_PAGE_SIZE)}
        totalItems={projects.length}
        pageSize={TABLE_PAGE_SIZE}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

const PaceIndicator: React.FC<{ stats: ProjectStats; compact?: boolean }> = ({ stats, compact = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  if (!stats.minEarliest || !stats.maxLatest) return null;

  const projectStart = new Date(stats.minEarliest);
  const projectEnd = new Date(stats.maxLatest);
  const totalDurationMs = projectEnd.getTime() - projectStart.getTime();
  const now = new Date();
  const elapsedMs = Math.max(0, Math.min(now.getTime() - projectStart.getTime(), totalDurationMs));
  const timeElapsedPerc = totalDurationMs > 0 ? Math.round((elapsedMs / totalDurationMs) * 100) : 0;
  const totalDays = Math.ceil(totalDurationMs / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.ceil(elapsedMs / (1000 * 60 * 60 * 24));
  const completedCount = stats.completed;
  const totalCount = stats.totalCalls;
  const workCompletedPerc = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const variance = workCompletedPerc - timeElapsedPerc;
  const isAhead = variance >= 0;

  const markerPos = `${timeElapsedPerc}%`;
  const progressPos = `${workCompletedPerc}%`;

  return (
    <div className="relative inline-block">
      <div className={`flex items-center ${compact ? 'gap-2' : 'gap-4'} cursor-help`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {/* Stats */}
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 font-bold ${compact ? 'text-[9px]' : 'text-[11px]'} px-2 py-1 rounded-full transition-all ${isAhead ? 'bg-[#96c36d]/10 text-[#5a8b34]' : 'bg-red-50 text-[#ef4444]'}`}>
            {isAhead ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{isAhead ? `+${variance}%` : `${variance}%`}</span>
          </div>
          <span className="text-slate-200 mx-0.5">·</span>
          <div className={`flex flex-col leading-tight font-semibold text-slate-400 ${compact ? 'text-[9px]' : 'text-[11px]'}`}>
            <span>{projectEnd.toLocaleDateString('en-GB', { day: '2-digit' })}</span>
            <span>{projectEnd.toLocaleDateString('en-GB', { month: 'short' })}</span>
          </div>
        </div>
      </div>
      {isHovered && (
        <div className="absolute top-full right-0 mt-3 z-[200] w-64 animate-in fade-in zoom-in duration-200">
          <div className="bg-[rgb(25,34,41)] text-white p-5 rounded-xl border border-white/10 relative ">
            <div className="flex items-center gap-3 mb-4">
              {isAhead ? <CheckCircle2 className="w-5 h-5 text-[#96c36d]" /> : <AlertCircle className="w-5 h-5 text-orange-400" />}
              <h4 className="text-sm font-bold tracking-tight">{isAhead ? 'Ahead' : 'Behind'}</h4>
            </div>
            <p className="text-xs text-slate-300 font-medium mb-5">{Math.abs(variance)}% {isAhead ? 'faster' : 'slower'} than planned</p>
            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="flex justify-between items-center text-[11px]"><span className="text-slate-400">Work:</span><span className="font-bold">{workCompletedPerc}%</span></div>
              <div className="flex justify-between items-center text-[11px]"><span className="text-slate-400">Time:</span><span className="font-bold">{timeElapsedPerc}%</span></div>
            </div>
            <p className="mt-4 text-[9px] text-slate-500 leading-relaxed italic border-t border-white/5 pt-3">{workCompletedPerc}% of work completed in {timeElapsedPerc}% of the scheduled deadline.</p>
            <div className="absolute bottom-full right-6 w-3 h-3 bg-[rgb(25,34,41)] rotate-45 translate-y-1.5 border-l border-t border-white/10"></div>
          </div>
        </div>
      )}

    </div>
  );
};

// ─── PilotBadge ───────────────────────────────────────────────────────────────
const PilotBadge: React.FC<{ pilot: any; compact?: boolean }> = ({ pilot, compact = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const pilotRequired = pilot?.required;
  const pilotStyle = pilotRequired === true
    ? 'text-orange-600 border-orange-200'
    : pilotRequired === false
      ? 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20'
      : 'text-slate-600';

  const hasPilotInfo = pilot?.proposed_date || pilot?.target_store;

  return (
    <div className="relative inline-block">
      <div
        className={`inline-flex items-center justify-center ${compact ? '' : 'px-2 rounded-[4px]'} transition-all cursor-help ${compact ? '' : pilotStyle}`}
        onMouseEnter={() => hasPilotInfo && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {compact ? (
          <div className={`w-4 h-4 rounded-full transition-all ${pilotRequired === true ? 'bg-orange-400'
            : pilotRequired === false ? 'bg-[#9cc16b]'
              : 'bg-[#e84442]'
            }`} />
        ) : pilotRequired === true ? (
          <Info className="w-3.5 h-3.5 opacity-100" />
        ) : pilotRequired === false ? (
          <Check strokeWidth={3} className="w-3 h-3" />
        ) : (
          <X strokeWidth={3} className="w-3 h-3 opacity-100" />
        )}
      </div>
      {isHovered && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 z-[200] w-56 animate-in fade-in zoom-in duration-200">
          <div className="bg-[rgb(25,34,41)] text-white p-4 rounded-xl border border-white/10 relative ">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-orange-400" />
              <h4 className="text-xs font-bold tracking-tight">Pilot Details</h4>
            </div>
            <div className="space-y-3">
              {pilot?.proposed_date && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[12px] text-slate-400 font-bold">Scheduled Date</span>
                  <span className="text-[11px] font-medium">{new Date(pilot.proposed_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {pilot?.target_store && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[12px] text-slate-400 font-bold">Location / Store</span>
                  <span className="text-[11px] font-medium">{pilot.target_store}</span>
                </div>
              )}
            </div>
            <div className="absolute bottom-full left-1/2 w-3 h-3 bg-[rgb(25,34,41)] rotate-45 translate-y-1.5 -translate-x-1/2 border-l border-t border-white/10"></div>
          </div>
        </div>
      )}
    </div>
  );
};


const Logo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg id="Component_1_1" xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 80 80"
    style={{ borderRadius: '3px', background: 'rgb(255 255 255)', padding: '3px', height: '45px', width: '45px' }}>
    <rect id="Rectangle_1" data-name="Rectangle 1" fill="none" width="80" height="80" />
    <path id="Path_1" data-name="Path 1" fill="#96c36d" stroke="#96c46d" strokeWidth="1" d="M276.077,242.234,298.6,280.492l22.206-38.258H309.215L298.6,260.248l-10.88-18.014Z" transform="translate(-269 -219)" />
    <path id="Path_2" data-name="Path 2" fill="rgb(81, 94, 101)" stroke="rgb(81, 94, 101)" strokeWidth="1" d="M313.4,270.533l6.352,10.066,22.279-38.421H330.019Z" transform="translate(-269 -219)" />
  </svg>
);

const getHighResUrl = (url: string) => {
  if (!url) return url;
  // If it's a TeamHaven size-limited URL, upgrade it to 800px for the modal
  if (url.includes('images.teamhaven.com/size/')) {
    return url.replace(/\/size\/\d+\//, '/size/800/');
  }
  return url;
};

// ─── WatermarkViewer ──────────────────────────────────────────────────────────
const WatermarkViewer: React.FC<{
  imageUrl: string; timestamp?: string; storeName?: string; gpsCoordinates?: any; onClose: () => void;
}> = ({ imageUrl, timestamp, storeName, gpsCoordinates, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [canDownload, setCanDownload] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    // Use high-res version for the modal
    img.src = getHighResUrl(imageUrl);
    img.onload = () => {
      canvas.width = img.width; canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      const fontSize = Math.max(20, Math.floor(canvas.width * 0.025));
      ctx.font = `700 ${fontSize}px Arial, sans-serif`;
      const margin = Math.floor(canvas.width * 0.04);
      const xPos = canvas.width - margin;
      let yPos = canvas.height - margin;

      // Setup text style
      ctx.fillStyle = "#fbfbfa";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.shadowColor = "rgba(0,0,0,0.8)";
      ctx.shadowBlur = 6;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;

      let dateStringToDraw = "";
      if (timestamp && timestamp !== '') {
        try {
          const date = new Date(timestamp);
          if (!isNaN(date.getTime())) {
            dateStringToDraw = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
          } else {
            // Fallback to raw string if it's not a valid date but is a string
            dateStringToDraw = String(timestamp);
          }
        } catch (e) {
          dateStringToDraw = String(timestamp);
        }
      }

      let gpsString = "";
      if (gpsCoordinates && gpsCoordinates !== 'undefined') {
        try {
          let parsed = gpsCoordinates;
          // Robust parsing for various formats (JSON string, object, double-encoded string)
          if (typeof parsed === "string") {
            try {
              parsed = JSON.parse(parsed);
              if (typeof parsed === "string") parsed = JSON.parse(parsed);
            } catch (e) { }
          }

          let lat = parsed?.Coordinates?.Lat ?? parsed?.lat ?? parsed?.Lat ?? parsed?.latitude;
          let lng = parsed?.Coordinates?.Lng ?? parsed?.lng ?? parsed?.Lng ?? parsed?.longitude;

          if (lat !== undefined && lng !== undefined) {
            gpsString = `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
          }
        } catch (e) { }
      }

      if (dateStringToDraw) {
        ctx.fillText(dateStringToDraw, xPos, yPos);
      }

      if (gpsString) {
        if (dateStringToDraw) yPos -= fontSize * 1.2;
        ctx.fillText(gpsString, xPos, yPos);
      }
      setImgLoaded(true); setCanDownload(true);
    };
    img.onerror = () => setError("CORS Error or Image URL invalid.");
  }, [imageUrl, timestamp, storeName, gpsCoordinates]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/jpeg", 1.0);
    link.download = `Photo_${(storeName || 'Image').replace(/\s/g, '_')}.jpg`;
    link.click();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90">
      <div className="absolute top-5 right-5 flex gap-3 z-20">
        {canDownload && <button onClick={handleDownload} className="p-3 bg-[#fbfbfa]/10 hover:bg-[#fbfbfa]/20 rounded-lg text-white"><Download className="w-5 h-5" /></button>}
        <button onClick={onClose} className="p-3 bg-[#fbfbfa]/10 hover:bg-[#fbfbfa]/20 rounded-lg text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="w-full h-full flex items-center justify-center overflow-auto p-10">
        {error ? (
          <div className="bg-red-900/20 p-6 rounded-lg border border-red-500 text-red-200"><AlertCircle className="mb-2 mx-auto" /><p>{error}</p></div>
        ) : (
          <canvas ref={canvasRef} className="rounded-sm" style={{ transform: `scale(${zoom})`, maxWidth: zoom <= 1 ? "100%" : "none", maxHeight: zoom <= 1 ? "100%" : "none", transition: "transform 0.1s ease" }} />
        )}
      </div>
      {imgLoaded && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/60 p-2 px-4 rounded-full border border-white/10">
          <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))} className="text-white hover:text-blue-400 font-bold px-2">-</button>
          <span className="text-white text-xs min-w-[50px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(z + 0.2, 3))} className="text-white hover:text-blue-400 font-bold px-2">+</button>
        </div>
      )}
    </div>
  );
};

// ─── TimelineModal ────────────────────────────────────────────────────────────
const TimelineModal: React.FC<{ callData: any; onClose: () => void }> = ({ callData, onClose }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0">
    <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={onClose}></div>
    <div className="relative bg-[#fbfbfa] w-full max-w-2xl rounded-[3px] overflow-hidden">
      <header className="px-8 h-20 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-[#96c36d]" />
          <div>
            <h3 className="text-lg font-semibold text-[#1e2a32]">Visit Timeline</h3>
            <p className="text-[10px] font-semibold text-slate-400 ">{callData.allCalls?.length || 1} Total Visit{callData.allCalls?.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all text-slate-300"><X className="w-5 h-5" /></button>
      </header>
      <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
        {callData.allCalls && callData.allCalls.length > 0 ? (
          <div className="space-y-4">
            {callData.allCalls.map((call: any, idx: number) => (
              <div key={call.call_id} className={`p-6 rounded-[14px] border-2 ${idx === 0 ? 'border-[#96c36d]/30 bg-[#96c36d]/5' : 'border-slate-100 bg-slate-50/50'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-[#96c36d]' : 'bg-slate-200'}`}>
                      <span className="text-white font-bold text-sm">{idx + 1}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-400 ">{idx === 0 ? 'Latest Visit' : `Visit ${idx + 1}`}</p>
                      <p className="text-sm font-semibold text-[#1e2a32] mt-0.5">Call ID: {call.call_id}</p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${String(call.status) === '3' ? 'bg-green-100 text-green-700' : String(call.status) === '2' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    {String(call.status) === '3' ? 'Completed' : String(call.status) === '2' ? 'In Progress' : 'Planned'}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                  {[['Planned', call.planned_start], ['Started', call.actual_start], ['Completed', call.actual_end]].map(([label, val]) => (
                    <div key={label as string}>
                      <p className="text-[9px] font-semibold text-slate-400  mb-1">{label}</p>
                      <p className="text-sm text-[#1e2a32]">{val ? new Date(val as string).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not set'}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10"><Clock className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-400">No visit history available</p></div>
        )}
      </div>
    </div>
  </div>
);

// ─── WarehouseAvailability ────────────────────────────────────────────────────
const WarehouseAvailability: React.FC<{
  availability: WarehouseAvailabilityType[] | null | undefined;
  cache?: Record<string, Map<string, number>>;
}> = ({ availability, cache }) => {
  const displayAvailability = (availability && Array.isArray(availability)) ? availability.filter(w => w.is_expecting) : [];
  return (
    <div>
      <p className="text-[10px] text-[#475763] font-bold text-slate-400  mb-2">Delivery Status</p>
      <div className="flex flex-wrap gap-2.5">
        {displayAvailability.length === 0 ? (
          <div className="flex items-center gap-2 pl-2 pr-3 py-1 text-[10px] font-bold rounded-full border border-solid bg-slate-100 text-slate-500 border-slate-200">
            <Clock className="w-3 h-3" /><span>Not Specified</span>
          </div>
        ) : (
          displayAvailability.map((warehouse, idx) => (
            <WarehouseStockBadge
              key={`${warehouse.warehouse_code}-${idx}`}
              warehouseCode={warehouse.warehouse_code}
              branchName={(warehouse as any).branch_name}
              watchedProducts={(warehouse as any).watched_products || []}
              receivedElementIds={warehouse.received_element_ids}
              variant="full"
              cache={cache}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── DocumentStatus — improved with Pilot badge ───────────────────────────────
const DocumentStatus: React.FC<{ project: ProjectSummary }> = ({ project }) => {
  const documents = [
    { name: 'Brief', received: !!project.brief_document_url, key: 'brief' },
    { name: 'PO', received: !!project.purchase_order_document_url, key: 'po' },
    { name: 'CE', received: !!project.cost_estimate_document_url, key: 'ce' },
  ];

  return (
    <div>
      <p className="text-[10px] text-[#475763] font-bold text-slate-400  mb-3">Document Status</p>
      <div className="flex flex-wrap gap-2">
        {documents.map(doc => (
          <div key={doc.key} className={`flex items-center gap-1.5 pl-2 pr-3 py-1 text-[10px] font-bold rounded-full border border-solid ${doc.received ? 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
            {doc.received ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 opacity-50" />}
            <span>{doc.name} {doc.received ? '✓' : 'Pending'}</span>
          </div>
        ))}
        {/* Pilot badge */}
        <PilotBadge pilot={project.pilot_installation_required} />
      </div>
    </div>
  );
};

// ─── ALL_WAREHOUSES ──────────────────────────────────────────────────────────
const ALL_WAREHOUSES = ["Blm", "Dbn", "Cpt", "El", "Grg", "Jhb", "Nels", "Plk", "Pe"];

// ─── PosManager ───────────────────────────────────────────────────────────────
const PosManager: React.FC<{ project: ProjectSummary; onClose: () => void; onSave: (availability: WarehouseAvailabilityType[]) => Promise<void> }> = ({ project, onClose, onSave }) => {
  const [availability, setAvailability] = useState<WarehouseAvailabilityType[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initial = ALL_WAREHOUSES.map(code => {
      const existing = project.warehouse_availability?.find(w => w.warehouse_code === code);
      return existing || { warehouse_code: code, is_expecting: false, received_element_ids: [] };
    });
    setAvailability(initial);
  }, [project]);

  const handleSave = async () => {
    try { setSaving(true); await onSave(availability); onClose(); } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0">
      <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={onClose}></div>
      <div className="relative bg-[#fbfbfa] w-full max-w-4xl max-h-[90vh] flex flex-col rounded-[3px] overflow-hidden">
        <header className="px-8 h-20 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3"><List className="w-5 h-5 text-[#96c36d]" /><div><h3 className="text-lg font-semibold text-[#1e2a32]">Manage POS Availability</h3><p className="text-[10px] font-semibold text-slate-400 ">{project.name}</p></div></div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all text-slate-300"><X className="w-5 h-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {availability.map(w => (
              <div key={w.warehouse_code} className={`p-6 rounded-[14px] border-2 transition-all ${w.is_expecting ? 'border-[#96c36d]/50 bg-[#fbfbfa]' : 'border-slate-100 bg-slate-50/70'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-bold text-lg text-[#1e2a32]">{w.warehouse_code}</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={w.is_expecting} onChange={() => setAvailability(prev => prev.map(x => x.warehouse_code === w.warehouse_code ? { ...x, is_expecting: !x.is_expecting } : x))} className="sr-only peer" />
                    <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-[#96c36d]"></div>
                  </label>
                </div>
                {w.is_expecting && (
                  <button onClick={() => setAvailability(prev => prev.map(x => x.warehouse_code === w.warehouse_code ? { ...x, received_element_ids: x.received_element_ids.length > 0 ? [] : ['dummy-id'] } : x))} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-[3px] text-xs font-semibold border transition-all mt-4 pt-4 border-t border-slate-100 ${w.received_element_ids.length > 0 ? 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                    {w.received_element_ids.length > 0 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                    {w.received_element_ids.length > 0 ? 'Marked as Received' : 'Mark as Received'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        <footer className="p-6 bg-slate-50/50 border-t border-slate-50 shrink-0 flex justify-end gap-4">
          <button onClick={onClose} className="bg-[#fbfbfa] text-slate-500 px-8 h-11 rounded-[3px] font-semibold text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="bg-[#192229] hover:bg-[#000] disabled:opacity-50 text-white px-8 h-11 rounded-[3px] font-semibold flex items-center gap-2 text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Changes
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── DocManager ───────────────────────────────────────────────────────────────
const DocManager: React.FC<{ project: ProjectSummary; onClose: () => void; onSave: (updates: Partial<Project>) => Promise<void> }> = ({ project, onClose, onSave }) => {
  const [docs, setDocs] = useState({ brief_document_url: project.brief_document_url, purchase_order_document_url: project.purchase_order_document_url, cost_estimate_document_url: project.cost_estimate_document_url });
  const [pilotInstallation, setPilotInstallation] = useState<any>(project.pilot_installation_required || { required: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [targets, setTargets] = useState<{ target_id: number; name: string }[]>([]);
  const [targetSearch, setTargetSearch] = useState(pilotInstallation?.target_store || '');
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  // Load targets for this project
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('targets').select('target_id, name').eq('project_id', project.project_id).order('name', { ascending: true });
      setTargets(data || []);
    };
    load();
  }, [project.project_id]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (targetRef.current && !targetRef.current.contains(e.target as Node)) setShowTargetDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredTargets = targetSearch.trim()
    ? targets.filter(t => t.name.toLowerCase().includes(targetSearch.toLowerCase()))
    : targets;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, docType: keyof typeof docs) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(docType);
    try {
      const { data, error } = await supabase.storage.from('project-documents').upload(`${project.project_id}/${String(docType)}-${file.name}`, file, { cacheControl: '3600', upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('project-documents').getPublicUrl(data.path);
      setDocs(prev => ({ ...prev, [docType]: publicUrl }));
    } catch (e) { console.error(e); } finally { setUploading(null); }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave({ ...docs, pilot_installation_required: pilotInstallation?.required ? pilotInstallation : null });
      onClose();
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0">
      <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={onClose}></div>
      <div className="relative bg-[#fbfbfa] w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[3px] overflow-hidden">
        <header className="px-8 h-20 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div><h3 className="text-lg font-semibold text-[#1e2a32] mt-10">Project Documents and Pilot Installation</h3>
              <p className="text-sm text-slate-500 mt-1">{project.name}</p></div></div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 text-slate-300"><X className="w-5 h-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {(['brief_document_url', 'purchase_order_document_url', 'cost_estimate_document_url'] as const).map(docType => (
            <div key={docType}>
              <label className="text-[13px] font-semibold text-slate-700 ml-1">
                {docType
                  .replace(/_/g, ' ')
                  .replace('document url', '')
                  .trim()
                  .replace(/\b\w/g, char => char.toUpperCase())}
              </label>
              <div className="mt-2 flex items-center gap-4">
                <input type="file" id={docType} className="hidden" onChange={(e) => handleFileChange(e, docType)} disabled={!!uploading} />
                <label htmlFor={docType} className={`flex-1 h-12 bg-slate-50  rounded-xl text-sm font-medium flex items-center justify-between cursor-pointer hover:bg-slate-100 ${uploading === docType ? 'opacity-50' : ''}`}>
                  <span className="text-slate-400 inline-flex items-center gap-[6px] w-[100%] h-[45px] pl-[15px] bg-[#fbfbfa] border border-[#e9e9e9] rounded-[9px] text-[13px] font-medium text-[#111111] transition-all duration-150 outline-none shadow-none focus:border-[#111111] placeholder:text-[#9C9C9A]">{docs[docType] ? 'File Uploaded' : 'Choose a file...'}</span>
                  {uploading === docType && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </label>
                {docs[docType] && <a href={docs[docType]} target="_blank" rel="noopener noreferrer" className="p-3 rounded-[3px] bg-slate-50 text-slate-400 hover:bg-[#96c36d] hover:text-white hover:border-[#96c36d]"><ExternalLink className="w-4 h-4" /></a>}
              </div>
            </div>
          ))}
          <div className="pt-6 border-t border-slate-100">
            <p className="text-[14px] font-semibold ml-1 mb-4">Pilot Installation</p>
            <button
              type="button"
              onClick={() =>
                setPilotInstallation((prev: any) => ({
                  ...prev,
                  required: !prev?.required,
                }))
              }
              className={`ml-0 flex items-center gap-2 px-3 py-2 rounded-[9px] text-[12px] font-medium transition-all ${pilotInstallation?.required
                ? "bg-[#96c36d]/10 text-[#6d9f48] border border-[#96c36d]/30"
                : "bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100"
                }`}
            >
              <div
                className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${pilotInstallation?.required
                  ? "bg-[#96c36d] border-[#96c36d]"
                  : "border-slate-300"
                  }`}
              >
                {pilotInstallation?.required && (
                  <Check className="w-2.5 h-2.5 text-white" />
                )}
              </div>

              Pilot installation required
            </button>
            {pilotInstallation?.required && (
              <div className="mt-5 space-y-5">
                {/* Target Store — searchable dropdown from project targets */}
                <div ref={targetRef}>
                  <label className="text-[11px] font-semibold text-slate-400 ml-1">Target Store</label>
                  <p className="text-[10px] text-slate-300 ml-1 mt-0.5 mb-2">{targets.length} stores available in this project</p>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none" />
                    <input
                      type="text"
                      className="mt-0 w-full h-12 bg-slate-50 rounded-xl pl-11 pr-4 text-sm font-medium text-[#1e2a32] focus:ring-1 focus:ring-[#96c36d] focus:border-[#96c36d] outline-none transition-all"
                      placeholder="Search stores..."
                      value={targetSearch}
                      onChange={(e) => { setTargetSearch(e.target.value); setShowTargetDropdown(true); setPilotInstallation((prev: any) => ({ ...prev, target_store: e.target.value })); }}
                      onFocus={() => setShowTargetDropdown(true)}
                    />
                    {targetSearch && (
                      <button onClick={() => { setTargetSearch(''); setPilotInstallation((prev: any) => ({ ...prev, target_store: '' })); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {showTargetDropdown && filteredTargets.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#fbfbfa] rounded-[3px] overflow-hidden z-50 max-h-48 overflow-y-auto custom-scrollbar">
                        {filteredTargets.map(t => (
                          <button
                            key={t.target_id}
                            onClick={() => { setTargetSearch(t.name); setPilotInstallation((prev: any) => ({ ...prev, target_store: t.name })); setShowTargetDropdown(false); }}
                            className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors ${pilotInstallation?.target_store === t.name ? 'text-[#5a8b34] font-semibold bg-[#96c36d]/5' : 'text-[#1e2a32] font-medium'}`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{t.name}</span>
                              {pilotInstallation?.target_store === t.name && <Check className="w-3.5 h-3.5 text-[#96c36d]" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showTargetDropdown && filteredTargets.length === 0 && targetSearch.trim() && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#fbfbfa] rounded-[3px] overflow-hidden z-50">
                        <div className="px-4 py-3 text-sm text-slate-400">No stores found matching "{targetSearch}"</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Proposed Date/Time */}
                <div>
                  <label className="text-[11px] font-semibold text-slate-400  ml-1">Proposed Date/Time</label>
                  <input type="datetime-local" className="mt-2 w-full h-12 bg-slate-50 rounded-xl px-4 text-sm font-medium text-[#1e2a32] focus:ring-1 focus:ring-[#96c36d] outline-none" value={pilotInstallation.proposed_date || ''} onChange={(e) => setPilotInstallation((prev: any) => ({ ...prev, proposed_date: e.target.value }))} />
                </div>
              </div>
            )}
          </div>
        </div>
        <footer className="p-6 bg-slate-50/50 border-t border-slate-50 shrink-0 flex justify-end gap-4">
          <button onClick={onClose} className="bg-[#fbfbfa] text-slate-500 px-8 h-11 rounded-[3px] font-semibold text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !!uploading} className="bg-[#192229] hover:bg-[#000] disabled:opacity-50 text-white px-8 h-11 rounded-[3px] font-semibold flex items-center gap-2 text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Changes
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── UpdatePasswordPage ───────────────────────────────────────────────────────
const UpdatePasswordPage: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true); setTimeout(onComplete, 2000);
    } catch (err: any) { setError(err.message || 'Failed to update password.'); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#fbfbfa] flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-[420px] bg-[#fff] rounded-2xl p-10 flex flex-col items-start">
        <div className="mb-8"><Logo className="w-12 h-12" /></div>
        <div className="text-left mb-10"><h2 className="text-2xl font-semibold text-[#1e2a32] tracking-tight mb-2">New Password</h2><p className="text-sm text-slate-400">Please enter your new secure password</p></div>
        {error && <div className="w-full mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3"><AlertCircle className="w-4 h-4 text-red-500 shrink-0" /><p className="text-xs font-medium text-red-600">{error}</p></div>}
        {success ? (
          <div className="w-full p-4 bg-[#96c36d]/10 border border-[#96c36d]/20 rounded-xl flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-[#96c36d] shrink-0" /><p className="text-xs font-medium text-[#5a8b34]">Password updated! Redirecting...</p></div>
        ) : (
          <form onSubmit={handleUpdate} className="w-full space-y-6">
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-slate-400  ml-1">New Password</label>
              <div className="relative"><Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" /><input type="password" required placeholder="*******" className="w-full h-12 bg-slate-50 rounded-xl pl-12 pr-4 text-sm font-medium focus:ring-1 focus:ring-[#96c36d] outline-none" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            </div>
            <button type="submit" disabled={loading} className="w-full h-12 bg-[#000] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set New Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── LoginPage ────────────────────────────────────────────────────────────────
const LoginPage: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'reset'>('login');
  const [success, setSuccess] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      onLogin();
    } catch (err: any) { setError(err.message || 'Invalid email or password.'); } finally { setLoading(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null); setSuccess(false);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
      if (error) throw error;
      setSuccess(true);
    } catch (err: any) { setError(err.message || 'Failed to send reset email.'); } finally { setLoading(false); }
  };

  if (mode === 'reset') {
    return (
      <div className="min-h-screen bg-[#fbfbfa] flex items-center justify-center p-6 font-sans">
        <div className="w-full max-w-[420px] bg-[#fbfbfa] rounded-2xl p-10 flex flex-col items-start">
          <div className="mb-8"><Logo className="w-12 h-12" /></div>
          <div className="text-left mb-10"><h2 className="text-2xl font-semibold text-[#1e2a32] tracking-tight mb-2">Reset Password</h2><p className="text-sm text-slate-400">Enter your email and we'll send you a recovery link</p></div>
          {error && <div className="w-full mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3"><AlertCircle className="w-4 h-4 text-red-500 shrink-0" /><p className="text-xs font-medium text-red-600">{error}</p></div>}
          {success ? (
            <div className="w-full space-y-6 text-center">
              <div className="p-4 bg-[#96c36d]/10 border border-[#96c36d]/20 rounded-xl flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-[#96c36d] shrink-0" /><p className="text-xs font-medium text-[#5a8b34]">Reset link sent!</p></div>
              <button onClick={() => setMode('login')} className="text-sm font-semibold text-[#96c36d] hover:underline">Back to Sign In</button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="w-full space-y-6">
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-slate-400  ml-1">Email Address</label>
                <div className="relative"><User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" /><input type="email" required placeholder="name@company.com" className="w-full h-12 bg-slate-50 rounded-xl pl-12 pr-4 text-sm font-medium focus:ring-1 focus:ring-[#96c36d] outline-none" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-12 bg-[#000] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}</button>
              <button type="button" onClick={() => setMode('login')} className="w-full text-center text-xs font-semibold text-slate-400 hover:text-[#1e2a32]">Cancel and go back</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfbfa] flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-[420px] bg-[#fff] rounded-2xl p-10 flex flex-col items-start">
        <div className="mb-8"><Logo className="w-12 h-12" /></div>
        <div className="text-left mb-10"><h2 className="text-2xl font-semibold text-[#1e2a32] tracking-tight mb-2">Welcome Back</h2><p className="text-sm text-slate-400">Sign in to manage your field reports</p></div>
        {error && <div className="w-full mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3"><AlertCircle className="w-4 h-4 text-red-500 shrink-0" /><p className="text-xs font-medium text-red-600">{error}</p></div>}
        <form onSubmit={handleSignIn} className="w-full space-y-6">
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-slate-400  ml-1">Email Address</label>
            <div className="relative " style={{ border: '1px solid #ececec', borderRadius: '9px' }}><User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" /><input type="email" required placeholder="name@company.com" className="w-full h-12 bg-slate-50 rounded-xl pl-12 pr-4 text-sm font-medium focus:ring-1 focus:ring-[#96c36d] outline-none placeholder:text-slate-300" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center ml-1">
              <label className="text-[11px] font-semibold text-slate-400 ">Password</label>
              <button type="button" onClick={() => setMode('reset')} className="text-[10px] font-semibold text-[#96c36d]  hover:underline">Forgot password?</button>
            </div>
            <div className="relative" style={{ border: '1px solid #ececec', borderRadius: '9px' }}><Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" /><input type="password" required placeholder="*******" className="w-full h-12 bg-slate-50 rounded-xl pl-12 pr-4 text-sm font-medium focus:ring-1 focus:ring-[#96c36d] outline-none placeholder:text-slate-300" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          </div>
          <button type="submit" disabled={loading} className="w-full h-12 bg-[#000] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 group">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Sign In</span><ArrowRight className="w-4 h-4 opacity-40 group-hover:translate-x-1 group-hover:opacity-100 transition-all" /></>}
          </button>
        </form>
        <div className="mt-12 w-full"><p className="text-[10px] text-slate-300 font-semibold">1.0.3</p></div>
      </div>
    </div>
  );
};

// ─── ProjectTimingAnalysis ────────────────────────────────────────────────────
const ProjectTimingAnalysis: React.FC<{ reportData: ReportData }> = ({ reportData }) => {
  const timingData = useMemo(() => {
    const callsWithTiming = reportData.storeBreakdown.filter(s => s.callData && s.callData.earliest_date && s.callData.latest_date).map(s => s.callData);
    if (callsWithTiming.length === 0) return null;
    const allEarliest = callsWithTiming.map(c => new Date(c.earliest_date).getTime());
    const allLatest = callsWithTiming.map(c => new Date(c.latest_date).getTime());
    const projectStart = new Date(Math.min(...allEarliest));
    const projectEnd = new Date(Math.max(...allLatest));
    const totalDurationMs = projectEnd.getTime() - projectStart.getTime();
    const now = new Date();
    const elapsedMs = Math.max(0, Math.min(now.getTime() - projectStart.getTime(), totalDurationMs));
    const timeElapsedPerc = totalDurationMs > 0 ? Math.round((elapsedMs / totalDurationMs) * 100) : 0;
    const totalDays = Math.ceil(totalDurationMs / (1000 * 60 * 60 * 24));
    const completedCount = reportData.stats.completed;
    const totalCount = reportData.stats.totalCalls;
    const workCompletedPerc = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const variance = workCompletedPerc - timeElapsedPerc;
    return { projectStart, projectEnd, totalDays, timeElapsedPerc, workCompletedPerc, variance, completedCount, totalCount };
  }, [reportData]);

  if (!timingData) return null;

  return (
    <div className="bg-[#fbfbfa] rounded-[9px]">
      <div className="px-[15px] pt-[30px] pb-6">
        <h3 className="text-lg font-semibold text-[#1e2a32]">Project execution rate</h3>
      </div>
      <hr className="border-[#e9e9e9] mx-[15px]" />
      <div className="px-[15px] pt-[24px] pb-[15px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-10">
            {[
              { label: 'Time elapsed', perc: timingData.timeElapsedPerc, sub: `(${timingData.totalDays} total days)`, color: 'bg-[rgb(25,34,41)]', textColor: 'text-[#1e2a32]' },
              { label: 'Work completed', perc: timingData.workCompletedPerc, sub: `(${timingData.completedCount} / ${timingData.totalCount} stores)`, color: 'bg-[#96c36d]', textColor: 'text-[#96c36d]' },
            ].map(m => (
              <div key={m.label} className="space-y-4">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-[12px] font-medium text-slate-400 mb-1.5">{m.label}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-medium tracking-tighter ${m.textColor}`}>{m.perc}%</span>
                      <span className="text-[11px] text-slate-400 font-normal">{m.sub}</span>
                    </div>
                  </div>
                </div>
                <div className="h-2 w-full bg-[#eaeaea] rounded-full overflow-hidden">
                  <div className={`h-full ${m.color} rounded-full transition-all duration-1000 ease-out`} style={{ width: `${m.perc}%` }}></div>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-[9px] p-8 flex flex-col justify-center">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-1 h-8 bg-[#96c36d] rounded-full"></div>
              <div><p className="text-[12px] font-medium text-slate-400">Efficiency metrics</p><h4 className="text-sm font-medium text-[#1e2a32] mt-0.5">Operational velocity</h4></div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2.5 border-b border-[#e9e9e9]"><span className="text-xs text-slate-500 font-medium">Daily target</span><span className="text-sm font-bold text-[#1e2a32]">{timingData.totalDays > 0 ? (timingData.totalCount / timingData.totalDays).toFixed(1) : 0} stores / day</span></div>
              <div className="flex justify-between items-center py-2.5 border-b border-[#e9e9e9]"><span className="text-xs text-slate-500 font-medium">Capture velocity</span><span className={`text-sm font-bold ${timingData.variance >= 0 ? 'text-[#5a8b34]' : 'text-red-500'}`}>{timingData.variance >= 0 ? '+' : ''}{timingData.variance}% variance</span></div>
              <div className="flex justify-between items-center pt-2.5"><span className="text-xs text-slate-500 font-medium">Estimated status</span><span className={`text-xs font-bold ${timingData.variance >= 0 ? 'text-[#5a8b34]' : 'text-red-500'}`}>{timingData.variance >= 0 ? 'On schedule' : 'Slowing down'}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── QuestionnaireTab — reads questionnaires from Supabase ────────────────────
const QuestionnaireTab: React.FC<{ projectId: number; onCountLoaded?: (count: number) => void }> = ({ projectId, onCountLoaded }) => {
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [editingMode, setEditingMode] = useState(false);
  const [selectedQ, setSelectedQ] = useState<any>(null);
  const [teamhavenId, setTeamhavenId] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // 1. First, get the TeamHaven ID for this project from Supabase
        const { data: proj } = await supabase.from('projects').select('project_id').eq('project_id', projectId).single();
        const thId = proj?.project_id;
        setTeamhavenId(thId);

        // 2. Fetch calls for this project to find questionnaire IDs used locally
        const { data: targets } = await supabase.from('targets').select('target_id').eq('project_id', projectId);

        let qIds: number[] = [];
        if (targets && targets.length > 0) {
          const targetIds = targets.map((t: any) => t.target_id);
          const { data: calls } = await supabase.from('calls').select('questionnaire_id').in('target_id', targetIds).not('questionnaire_id', 'is', null);
          if (calls && calls.length > 0) {
            qIds = Array.from(new Set(calls.map((c: any) => c.questionnaire_id)));
          }
        }

        // 3. Fallback: If no local questionnaires found, try to fetch from TeamHaven directly using the project ID
        if (qIds.length === 0 && thId) {
          const allQs = await getAllProjectQuestionnaires(thId);
          if (allQs && allQs.length > 0) {
            // Fetch all full questionnaires
            const fullQs = await Promise.all(allQs.map((q: any) => getProjectQuestionnaire(thId, q.QuestionnaireID || q.ID)));
            const validQs = fullQs.filter(Boolean);
            if (validQs.length > 0) {
              setQuestionnaires(validQs);
              onCountLoaded?.(validQs.length);
            }
            setLoading(false);
            return;
          }
        }

        if (qIds.length > 0) {
          const { data: qs } = await supabase.from('questionnaires').select('*').in('questionnaire_id', qIds);
          setQuestionnaires(qs || []);
          onCountLoaded?.((qs || []).length);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const parseForms = (raw: any) => {
    try {
      let forms = raw;
      if (typeof forms === 'string') { forms = JSON.parse(forms); if (typeof forms === 'string') forms = JSON.parse(forms); }
      return Array.isArray(forms) ? forms : [];
    } catch { return []; }
  };

  const countQuestions = (q: any) => {
    const forms = parseForms(q.forms);
    return forms.reduce((acc: number, f: any) => acc + (f.Questions?.length || 0), 0);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 space-y-4">
      <Loader2 className="w-8 h-8 animate-spin text-[#96c36d] opacity-30" />
      <p className="text-[12px] font-medium text-slate-400">Loading questionnaires</p>
    </div>
  );

  if (questionnaires.length === 0) return (
    <div className="bg-[#fbfbfa] rounded-[9px] p-20 text-center">
      <ClipboardList className="w-14 h-14 text-slate-200 mx-auto mb-4" />
      <p className="text-sm font-semibold text-slate-500 mb-2">No questionnaires found</p>
      <p className="text-xs text-slate-400 mb-6">No questionnaires are linked to calls within this project.</p>
      <button
        onClick={async () => {
          if (!teamhavenId) return;
          setLoading(true);
          const all = await getAllProjectQuestionnaires(teamhavenId);
          if (all && all.length > 0) {
            console.log('[QuestionnaireTab] Fetching full designs for all questionnaires');
            const fullQs = await Promise.all(all.map((q: any) => getProjectQuestionnaire(teamhavenId, q.QuestionnaireID || q.ID)));
            const validQs = fullQs.filter(Boolean);
            console.log('[QuestionnaireTab] Full design responses:', validQs);
            if (validQs.length > 0) setQuestionnaires(validQs);
          }
          setLoading(false);
        }}
        className="px-6 py-3 bg-[#1e2a32] hover:bg-[#2f3d47] text-white text-[12px] font-bold rounded-[9px] transition-all"
      >
        Scan TeamHaven for setup
      </button>
    </div>
  );

  if (editingMode && selectedQ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => { setEditingMode(false); setSelectedQ(null); }}
            className="flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-[#1e2a32] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
          <div className="text-right">
            <p className="text-[11px] font-medium text-slate-400">Editing questionnaire</p>
            <p className="text-sm font-bold text-[#1e2a32]">{selectedQ.ID} - {selectedQ.Name || 'Main Design'}</p>
          </div>
        </div>

        <QuestionnaireBuilder
          projectId={teamhavenId!}
          questionnaire={selectedQ}
          onUpdate={(newQ) => setSelectedQ(newQ)}
          onSave={async () => {
            const saveId = selectedQ.ID || selectedQ.QuestionnaireID;
            const res = await updateProjectQuestionnaire(teamhavenId!, saveId, selectedQ);

            if (res && res.QuestionnaireID) {
              let finalQ = res;
              if (selectedQ.Status === 3 && res.Status !== 3) {
                // TeamHaven created a new draft due to structural changes. Double-pass to activate it.
                if (saveId !== res.QuestionnaireID) {
                  try {
                    const oldQ = await getProjectQuestionnaire(teamhavenId!, saveId);
                    if (oldQ) {
                      await updateProjectQuestionnaire(teamhavenId!, saveId, { ...oldQ, Status: 4 });
                    }
                  } catch (e) {
                    console.error('Failed to deactivate old version:', e);
                  }
                }
                finalQ = await updateProjectQuestionnaire(teamhavenId!, res.QuestionnaireID, { ...res, Status: 3 });
              }
              // Update local state too
              setSelectedQ(finalQ);
              setQuestionnaires(prev => prev.map(q => (q?.ID || q?.QuestionnaireID) === saveId ? finalQ : q));
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-[#fbfbfa] rounded-[9px]">
      <div className="px-[15px] pt-[30px] pb-6">
        <h3 className="text-lg font-semibold text-[#1e2a32]">Project questionnaires</h3>
      </div>
      <hr className="border-[#e9e9e9] mx-[15px]" />

      <div className="px-[15px] pt-[24px] pb-[15px]">
        <div className="bg-white rounded-[9px] overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#fbfbfa]">
                  <th className="px-6 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4]">Questionnaire</th>
                  <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-center w-[130px]">Questions</th>
                  <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-center w-[110px]">Sections</th>
                  <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-right w-[220px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {questionnaires.filter(Boolean).map((q, qIndex) => {
                  if (!q) return null;
                  // Support both TeamHaven format (q.ID/q.QuestionnaireID, q.Forms) and Supabase format (q.questionnaire_id, q.forms)
                  const qId = q.QuestionnaireID || q.ID || q.questionnaire_id || qIndex;
                  const qForms = q.Forms || parseForms(q.forms);
                  const qTotalQ = qForms.reduce((acc: number, f: any) => acc + (f.Questions?.length || 0), 0);
                  const isOpen = expanded === qId;
                  const qName = q.Name || q.name || `Questionnaire #${qId}`;

                  return (
                    <React.Fragment key={qId}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : qId)}
                        className={`cursor-pointer group transition-colors ${qIndex % 2 === 0 ? 'bg-[#fbfbfa] hover:bg-slate-50/50' : 'bg-slate-50/30 hover:bg-slate-50/50'}`}
                      >
                        <td className="px-6 py-5 border-b border-[rgb(226,231,240)]">
                          <div className="flex items-center gap-4">
                            {/* <div className="w-10 h-10 rounded-[9px] bg-[#fbfbfa] flex items-center justify-center shrink-0">
                              <ClipboardList className="w-5 h-5 text-slate-400" /> 
                            </div> */}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className="text-sm font-bold text-[#1e2a32] truncate">{qName}</p>
                                <span className="text-slate-300 text-xs font-normal shrink-0">#{qId}</span>
                                {q.Status === 3 && <span className="px-2 py-0.5 rounded-[9px] bg-[#96c36d]/10 text-[#5a8b34] text-[10px] font-bold shrink-0">Active</span>}
                                {q.Status === 4 && <span className="px-2 py-0.5 rounded-[9px] bg-slate-100 text-slate-500 text-[10px] font-bold shrink-0">Used</span>}
                                {q.Status === 2 && <span className="px-2 py-0.5 rounded-[9px] bg-blue-100 text-blue-600 text-[10px] font-bold shrink-0">Checked</span>}
                                {q.Status === 1 && <span className="px-2 py-0.5 rounded-[9px] bg-amber-100 text-amber-600 text-[10px] font-bold shrink-0">Draft</span>}
                              </div>
                              <p className="text-[12px] text-slate-400 font-medium truncate">
                                {qForms.map((f: any) => f.Caption?.DefaultCaption || `Form ${f.FormID}`).join(' · ')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-5 text-center border-b border-[rgb(226,231,240)]">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[9px] bg-[#96c36d]/10 text-[#5a8b34] text-[11px] font-bold">
                            <Hash className="w-3 h-3" /> {qTotalQ}
                          </span>
                        </td>
                        <td className="px-4 py-5 text-center border-b border-[rgb(226,231,240)]">
                          <span className="text-sm font-semibold text-[#1e2a32]">{qForms.length}</span>
                        </td>
                        <td className="px-4 py-5 border-b border-[rgb(226,231,240)]">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedQ(q); setEditingMode(true); }}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-[9px] text-[12px] font-semibold text-slate-500 hover:text-[#96c36d] hover:bg-[#96c36d]/5 transition-all"
                            >
                              <EditIcon className="w-3.5 h-3.5" /> Manage setup
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setExpanded(isOpen ? null : qId); }} className="p-1.5 text-slate-300 hover:text-slate-500">
                              <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded: forms & questions */}
                      {isOpen && (
                        <tr className={qIndex % 2 === 0 ? 'bg-[#fbfbfa]' : 'bg-slate-50/30'}>
                          <td colSpan={4} className="px-6 pb-8 border-b border-[rgb(226,231,240)]">
                            {qForms.map((form: any, fi: number) => {
                              const formTitle = form.Caption?.DefaultCaption || `Form ${form.FormID || fi + 1}`;
                              const questions = form.Questions || [];
                              return (
                                <div key={fi} className={fi > 0 ? 'mt-8 pt-8 border-t border-[#e9e9e9]' : 'pt-2'}>
                                  <div className="flex items-center gap-3 mb-5">
                                    <h4 className="text-sm font-semibold text-[#1e2a32]">{formTitle}</h4>
                                    <span className="text-[11px] text-slate-500 font-medium">{questions.length} question{questions.length !== 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="max-w-2xl flex flex-col gap-2">
                                    {questions.map((question: any, qi: number) => {
                                      const caption = question.caption || question.Caption?.DefaultCaption || 'Untitled';
                                      const typeLabels: Record<number, string> = { 1: 'Text', 2: 'Text (Multi)', 3: 'Choice', 4: 'Multi Choice', 5: 'Numeric', 6: 'Yes/No', 10: 'Photo', 11: 'Date/Time', 12: 'Signature' };
                                      const typeLabel = typeLabels[question.Type] || `Type ${question.Type}`;
                                      return (
                                        <div key={qi} className="bg-white rounded-[9px] px-5 py-4 flex items-start gap-4">
                                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                                            <span className="text-[9px] font-bold text-slate-600">{qi + 1}</span>
                                          </div>
                                          <div className="flex-1 min-w-0 text-left">
                                            <p className="text-sm font-medium text-[#1e2a32] leading-snug">{caption}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                              <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-[9px] bg-[#fbfbfa] text-slate-600">{typeLabel}</span>
                                              {question.Required && <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-[9px] bg-red-50 text-red-500">Required</span>}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {questions.length === 0 && (
                                      <div className="text-center py-8 text-[12px] font-medium text-slate-400 rounded-[9px] bg-white">
                                        No questions in this section
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CallDetailsPage — improved Field Report grid ─────────────────────────────
const CallDetailsPage: React.FC<{
  storeName: string; projectId: number; projectName: string; onBack: () => void;
}> = ({ storeName, projectId, projectName, onBack }) => {
  const [callData, setCallData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<{ before: string | null; after: string | null }>({ before: null, after: null });
  const [watermarkData, setWatermarkData] = useState<{ url: string; ts: string; store: string; gpsCoordinates?: any } | null>(null);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<any[]>([]);
  const [rawQuestionnaire, setRawQuestionnaire] = useState<any>(null);
  const [rawAnswers, setRawAnswers] = useState<any[]>([]);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [visitIssueReason, setVisitIssueReason] = useState<string | null>(null);

  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setLoading(true);
        const { data: targets } = await supabase.from('targets').select('*').eq('project_id', projectId).eq('name', storeName);
        if (!targets || targets.length === 0) { setCallData(null); setLoading(false); return; }
        const target = targets[0];
        const { data: calls } = await supabase.from('calls').select('*').eq('target_id', target.target_id).order('actual_start', { ascending: false, nullsFirst: false });
        if (!calls || calls.length === 0) { setCallData({ target, call: null }); setLoading(false); return; }
        const latestCall = calls[0];
        let questionMap: { beforeIds: number[], afterIds: number[] } = { beforeIds: [], afterIds: [] };
        let formsWithAnswers: any[] = [];
        if (latestCall.questionnaire_id) {
          const { data: qData } = await supabase.from('questionnaires').select('*').eq('questionnaire_id', latestCall.questionnaire_id).single();
          if (qData) {
            setRawQuestionnaire(qData);
            let forms = qData.forms;
            if (typeof forms === 'string') forms = JSON.parse(forms);
            if (typeof forms === 'string') forms = JSON.parse(forms);
            const { data: answers } = await supabase.from('answers').select('*').eq('call_id', latestCall.call_id);
            setRawAnswers(answers || []);
            const answersMap = (answers || []).reduce((acc: any, ans: any) => {
              const current = acc[ans.question_id];
              const hasContent = (a: any) => String(a?.text || a?.value || '').trim().length > 0;
              if (!current || (!hasContent(current) && hasContent(ans))) acc[ans.question_id] = ans;
              return acc;
            }, {});
            if (Array.isArray(forms)) {
              let detectedIssue = null;
              formsWithAnswers = forms.map((form: any) => {
                const questionsWithAnswers = (form.Questions || []).map((question: any) => {
                  const answer = answersMap[question.QuestionID];
                  const caption = question.caption || question.Caption?.DefaultCaption || 'Untitled Question';
                  if (caption.toLowerCase().includes('photo before work')) questionMap.beforeIds.push(question.QuestionID);
                  else if (caption.toLowerCase().includes('photo of after work') || caption.toLowerCase().includes('photo after work')) questionMap.afterIds.push(question.QuestionID);
                  const isIssueQuestion = (caption.toLowerCase().includes('why') && caption.toLowerCase().includes('task') && caption.toLowerCase().includes('not be completed')) || (caption.toLowerCase().includes('reason') && caption.toLowerCase().includes('return'));
                  const isReturnReqQuestion = caption.toLowerCase().includes('required') && caption.toLowerCase().includes('return') && caption.toLowerCase().includes('store');
                  if (isIssueQuestion) {
                    const val = (answer?.text || answer?.value || '').trim();
                    const lowVal = val.toLowerCase();
                    if (val.length > 0 && !lowVal.includes('n/a') && lowVal !== 'yes' && lowVal !== 'no') detectedIssue = val;
                  } else if (isReturnReqQuestion && (answer?.yes_no === true || String(answer?.text || '').toLowerCase() === 'yes')) {
                    if (!detectedIssue || detectedIssue === 'Return to store required') detectedIssue = 'Return to store required';
                  }
                  return { ...question, answer: answer || null, caption };
                });
                return { formId: form.FormID, caption: form.Caption?.DefaultCaption || 'Untitled Form', questions: questionsWithAnswers };
              });
              setQuestionsAndAnswers(formsWithAnswers);
              setVisitIssueReason(detectedIssue);
            }
          }
        }
        const { data: photoAnswers } = await supabase.from('answers').select('*').eq('call_id', latestCall.call_id).not('picture', 'is', null);
        if (photoAnswers && photoAnswers.length > 0) {
          const beforeAnswer = photoAnswers.find(a => questionMap.beforeIds.includes(a.question_id));
          const afterAnswer = photoAnswers.find(a => questionMap.afterIds.includes(a.question_id));
          setPhotos({
            before: beforeAnswer ? `https://test.visibleworx.com/testproxy/one/pictures/${beforeAnswer.picture}.jpg` : null,
            after: afterAnswer ? `https://test.visibleworx.com/testproxy/one/pictures/${afterAnswer.picture}.jpg` : null
          });
        }
        setCallData({ target, call: latestCall, allCalls: calls });
      } catch (error) { console.error(error); } finally { setLoading(false); }
    };
    fetchCallDetails();
  }, [storeName, projectId]);

  const getStatusText = (status: string | number | undefined) => {
    switch (String(status)) { case '1': return 'Planned'; case '2': return 'In Progress'; case '3': return 'Completed'; default: return status ? String(status) : 'Planned'; }
  };

  const getStatusColor = (status: string | number | undefined) => {
    switch (String(status)) { case '3': return 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20'; case '2': return 'bg-blue-50 text-blue-600 border-blue-200'; default: return 'bg-slate-100 text-slate-500 border-slate-200'; }
  };

  // ── Improved answer renderer ──────────────────────────────────────────────
  const renderAnswer = (question: any) => {
    if (!question.answer) return <span className="text-slate-300 text-sm italic">No answer provided</span>;
    const { Type, answer } = question;

    if (Type === 1) return <p className="text-sm text-[rgb(50,65,80)] leading-relaxed">{answer.text || answer.value || '—'}</p>;
    if (Type === 2) { const suffix = question.Suffix || ''; return <p className="text-sm font-semibold text-[rgb(50,65,80)]">{answer.number ?? answer.value ?? '—'}{suffix}</p>; }
    if (Type === 5) {
      const v = answer.datetime || answer.value;
      return v ? <p className="text-sm text-[rgb(50,65,80)]">{new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p> : <span className="text-slate-300 text-sm italic">No date</span>;
    }
    if (Type === 6) {
      const isTrue = answer.yes_no === true || answer.text === 'Yes';
      return <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${isTrue ? 'bg-[#96c36d]/10 text-[#5a8b34]' : 'bg-red-50 text-red-600'}`}>{isTrue ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}{isTrue ? 'Yes' : 'No'}</span>;
    }
    if (Type === 7) return <p className="text-sm text-[rgb(50,65,80)] font-mono bg-slate-100 px-3 py-2 rounded-[3px] break-all">{answer.text || answer.value || '—'}</p>;
    if (Type === 8) {
      const v = answer.date || answer.value;
      return v ? <p className="text-sm text-[rgb(50,65,80)]">{new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p> : <span className="text-slate-300 text-sm italic">No date</span>;
    }
    if (Type === 10 && answer.picture) {
      const photoUrl = `https://test.visibleworx.com/testproxy/one/pictures/${answer.picture}.jpg`;
      return (
        <div className="mt-1 cursor-pointer group inline-block" onClick={() => setWatermarkData({ url: photoUrl, ts: callData.call.actual_start || new Date().toISOString(), store: storeName })}>
          <div className="relative w-64 rounded-[3px] overflow-hidden bg-slate-50" style={{ aspectRatio: '4/3' }}>
            <img src={photoUrl} alt={question.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-center justify-center">
              <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>
      );
    }
    if (Type === 11 && answer.signature) {
      return <div className="mt-1 w-40 h-20 rounded-[3px] overflow-hidden bg-[#fbfbfa] p-2"><img src={`https://test.visibleworx.com/testproxy/one/signatures/${answer.signature}.jpg`} alt="Signature" className="w-full h-full object-contain" /></div>;
    }
    return <p className="text-sm text-[rgb(50,65,80)]">{JSON.stringify(answer.value || answer)}</p>;
  };

  return (
    <>
      <div className="animate-in fade-in duration-500 space-y-8 pb-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-40 space-y-5">
            <Loader2 className="w-12 h-12 animate-spin text-[#96c36d] opacity-20" />
            <p className="text-[10px] font-semibold text-slate-300 ">Loading Call Data</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-[#fbfbfa] rounded-[9px] overflow-hidden">
              <div className="px-8 py-4 border-b border-slate-50 bg-slate-50/30">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>{projectName}</span><ChevronRight className="w-3 h-3" /><span className="text-[#1e2a32] font-medium">Target Details</span>
                </div>
              </div>
              <div className="p-10">
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <div className="flex items-center gap-3 mb-2"><h1 className="text-3xl font-semibold text-[#1e2a32] tracking-tight">{storeName}</h1></div>
                    <p className="text-sm text-slate-400">Call responses captured during the visit</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <button
                      onClick={async () => {
                        if (!callData || !rawQuestionnaire) { alert('Call data or questionnaire not yet loaded.'); return; }
                        const btn = document.activeElement as HTMLButtonElement;
                        btn.disabled = true; btn.innerText = 'Generating...';
                        try {
                          const storeExportData: SingleStoreExportData = { storeName, projectName, callData: callData.call ?? callData, questionnaire: rawQuestionnaire, answers: rawAnswers };
                          await exportSingleStorePDF(storeExportData);
                        } catch (err: any) { alert(err.message || 'Failed to generate PDF'); }
                        finally { btn.disabled = false; btn.innerText = 'Export PDF'; }
                      }}
                      className="bg-[#000] text-white text-[12px] h-[40px] px-6 rounded-[3px] font-semibold flex items-center gap-2 active:scale-[0.98]"
                    >
                      <FileText className="w-4 h-4" /> Export PDF
                    </button>
                  </div>
                </div>
                {callData?.call && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { icon: Hash, bg: 'bg-blue-50', iconColor: 'text-blue-600', label: 'Target ID', value: callData.target.target_id },
                      { icon: Database, bg: 'bg-slate-100', iconColor: 'text-slate-600', label: 'Call ID', value: callData.call.call_id },
                      { icon: FileText, bg: 'bg-orange-50', iconColor: 'text-orange-600', label: 'Visit Count', value: callData.allCalls?.length || 1, clickable: true },
                      { icon: CheckCircle2, bg: String(callData.call.status) === '3' ? 'bg-green-50' : 'bg-slate-100', iconColor: String(callData.call.status) === '3' ? 'text-green-600' : 'text-slate-400', label: 'Status', value: getStatusText(callData.call.status) },
                    ].map((item, i) => (
                      <div key={i} className={`bg-slate-50/50 rounded-[12px] p-5  ${item.clickable ? 'cursor-pointer hover:border-[#96c36d]/30 hover:bg-[#96c36d]/5' : ''}`} onClick={item.clickable ? () => setShowTimelineModal(true) : undefined}>
                        <div className="flex items-center gap-3">
                          <div><p className="text-[9px] font-semibold text-slate-400 ">{item.label}</p><p className="text-lg font-semibold text-[#1e2a32] mt-0.5 flex items-center gap-1">{item.value}{item.clickable && <ChevronRight className="w-4 h-4 text-slate-300" />}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {callData?.call ? (
              <>
                {callData.target.address && (
                  <div className="bg-[#fbfbfa] rounded-[9px] p-10">
                    <div className="flex items-center gap-3 mb-4"><h2 className="text-xl font-semibold text-[#1e2a32]">Location</h2></div>
                    <p className="text-sm text-slate-600 leading-relaxed">{callData.target.address}</p>
                  </div>
                )}

                {/* ── IMPROVED FIELD REPORT ── */}
                {questionsAndAnswers.length > 0 && (
                  <div className="bg-[#fbfbfa] rounded-[9px] p-10">
                    <div className="flex items-center gap-3 mb-8">
                      <h2 className="text-xl font-semibold text-[#1e2a32]">Activity Feedback</h2>
                    </div>

                    {visitIssueReason && (
                      <div className="mb-8 p-6 bg-red-50 border border-red-100 rounded-[12px] flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                        <div>
                          <p className="text-[10px] font-bold text-red-400  mb-1">Issue Reported</p>
                          <p className="text-base font-semibold text-red-700">{visitIssueReason}</p>
                          <p className="text-xs text-red-500/70 mt-1">Captured during the field visit as the primary reason for non-completion.</p>
                        </div>
                      </div>
                    )}

                    {questionsAndAnswers.map((form, fi) => (
                      <div key={form.formId} className={fi > 0 ? 'mt-10 pt-10 border-t border-slate-100' : ''}>
                        <div className="flex items-center gap-2 mb-6">
                          <ChevronRight className="w-4 h-4 text-[#96c36d]" />
                          <h3 className="font-semibold text-[#1e2a32] text-base">{form.caption}</h3>
                          <span className="text-[10px] text-slate-400 font-medium">{form.questions.length} question{form.questions.length !== 1 ? 's' : ''}</span>
                        </div>

                        {/* List of answer cards */}
                        <div className="flex flex-col gap-3">
                          {form.questions.map((question: any, qi: number) => {
                            const getQuestionTypeText = (type: number) => {
                              switch (type) {
                                case 1: return 'Text';
                                case 2: return 'Numeric';
                                case 3: return 'Single Choice';
                                case 4: return 'Multi Choice';
                                case 5: return 'Date & Time';
                                case 6: return 'Yes / No';
                                case 7: return 'Type 7';
                                case 8: return 'Date';
                                case 9: return 'Time';
                                case 10: return 'Photo';
                                case 11: return 'Signature';
                                case 12: return 'Text (Multi)';
                                default: return `Type ${type}`;
                              }
                            };

                            return (
                              <div
                                key={question.QuestionID}
                                className="bg-white rounded-[9px] p-5 flex flex-col gap-4"
                              >
                                <div className="flex items-start gap-4">
                                  <div className="w-8 h-8 rounded-full bg-[#f1f5f9] flex items-center justify-center shrink-0">
                                    <span className="text-xs font-semibold text-slate-500">{qi + 1}</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-[15px] font-medium text-[#1e2a32] mb-1.5">
                                      {question.caption}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <span className="px-2.5 py-1 bg-slate-50 text-slate-500 rounded-full text-[10px] font-semibold">
                                        {getQuestionTypeText(question.Type)}
                                      </span>
                                      {question.Required && (
                                        <span className="px-2.5 py-1 bg-red-50 text-red-500 rounded-full text-[10px] font-semibold">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Answer section */}
                                <div className="ml-12 pl-2 border-l-2 border-slate-50 py-1">
                                  {renderAnswer(question)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Photos */}
                {(photos.before || photos.after) && (
                  <div className="bg-[#fbfbfa] rounded-[9px] p-10">
                    <div className="flex items-center gap-3 mb-8"><ImageIcon className="w-5 h-5 text-[#96c36d]" /><h2 className="text-xl font-semibold text-[#1e2a32]">Visual Evidence</h2></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {[{ url: photos.before, label: 'Before Work' }, { url: photos.after, label: 'After Work' }].map(({ url, label }) => url && (
                        <div key={label} className="group rounded-[14px] overflow-hidden bg-slate-50">
                          <div className="aspect-video overflow-hidden bg-slate-100 cursor-pointer" onClick={() => setWatermarkData({ url, ts: callData.call.actual_start || new Date().toISOString(), store: storeName, gpsCoordinates: callData.call.position })}>
                            <img src={url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={label} />
                          </div>
                          <div className="p-4 bg-[#fbfbfa]"><p className="text-xs font-semibold text-slate-400 ">{label}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {callData.allCalls && callData.allCalls.length > 1 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-[3px] p-6">
                    <div className="flex items-start gap-4"><AlertCircle className="w-6 h-6 text-amber-600 shrink-0" /><div><p className="font-semibold text-amber-900 mb-1">Multiple Visits Detected</p><p className="text-sm text-amber-700">This target has {callData.allCalls.length} visit records. Showing the most recent visit.</p></div></div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-[#fbfbfa] rounded-[9px] p-20 text-center">
                <Database className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-sm font-semibold text-slate-400  mb-2">No Call Data Available</p>
                <p className="text-xs text-slate-300">This target has not been visited yet.</p>
              </div>
            )}
          </>
        )}
      </div>

      {watermarkData && (
        <WatermarkViewer imageUrl={watermarkData.url} timestamp={watermarkData.ts} storeName={watermarkData.store} gpsCoordinates={watermarkData.gpsCoordinates} onClose={() => setWatermarkData(null)} />
      )}
      {showTimelineModal && callData && <TimelineModal callData={callData} onClose={() => setShowTimelineModal(false)} />}
    </>
  );
};

// ─── OverviewPage — grid cards with scrollable project tables ─────────────────
const OverviewPage: React.FC<{
  summaries: ProjectSummary[];
  loading: boolean;
  onNavigateToProjects: () => void;
  onOpenProject: (projectId: number) => void;
  stockCache?: Record<string, Map<string, number>>;
}> = ({ summaries, loading, onNavigateToProjects, onOpenProject, stockCache }) => {

  const isStockLoading = false; // We use the global cache now

  const getStockPercSync = (warehouseCode: string, branchName: string, watchedProducts: any[], receivedElementIds?: any[]) => {
    if (!watchedProducts || watchedProducts.length === 0) return 0;
    const key = `${branchName}-${warehouseCode}`;
    const availableSKUs = stockCache ? stockCache[key] : null;
    const safeAvailableSKUs = availableSKUs || new Map();
    const receivedIdsSet = new Set((receivedElementIds || []).map(id => String(id)));

    let availableCount = 0;
    watchedProducts.forEach(wp => {
      const isManual = receivedIdsSet.has(String(wp.ProductID));
      const cleanSku = (wp.SKU || '').trim();
      if (isManual || safeAvailableSKUs.has(cleanSku)) {
        availableCount++;
      }
    });
    return Math.round((availableCount / watchedProducts.length) * 100);
  };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const now = new Date();
  const [activeTab, setActiveTab] = useState<'brief' | 'ce' | 'po' | 'deliveredPos' | 'ready'>('brief');
  const [projectSearch, setProjectSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [missingDocs, setMissingDocs] = useState(false);
  const [missingDelivery, setMissingDelivery] = useState(false);
  const [delivered, setDelivered] = useState(false);
  const isAll = !missingDocs && !missingDelivery && !delivered;

  const uniqueClients = useMemo(() => {
    const clients = new Set<string>();
    summaries.forEach(p => {
      const c = (p as any).client || p.description;
      if (c) clients.add(c);
    });
    return Array.from(clients).sort();
  }, [summaries]);

  // ── Search & Filter ──────────────────────────────────────────────────────
  const filteredSummaries = useMemo(() => {
    return summaries.filter(p => {
      if (projectSearch && !p.name.toLowerCase().includes(projectSearch.toLowerCase()) && !p.reference?.toLowerCase().includes(projectSearch.toLowerCase())) {
        return false;
      }

      if (clientFilter !== 'All') {
        const c = (p as any).client || p.description;
        if (c !== clientFilter) return false;
      }

      const createdDate = new Date(p.created_at);

      if (dateFrom) {
        const dFrom = new Date(dateFrom);
        dFrom.setHours(0, 0, 0, 0);
        if (createdDate < dFrom) return false;
      }

      if (dateTo) {
        const dTo = new Date(dateTo);
        dTo.setHours(23, 59, 59, 999);
        if (createdDate > dTo) return false;
      }

      if (missingDocs) {
        const hasAllDocs = p.brief_document_url && p.cost_estimate_document_url && p.purchase_order_document_url;
        if (hasAllDocs) return false;
      }

      if (missingDelivery) {
        const isMissingDeliv = p.warehouse_availability && Array.isArray(p.warehouse_availability) && p.warehouse_availability.some(w => w.is_expecting);
        if (!isMissingDeliv) return false;
      }

      if (delivered) {
        const wa = p.warehouse_availability;
        if (!wa || !Array.isArray(wa)) return false;
        const exp = wa.filter((w: any) => w.is_expecting);
        if (exp.length === 0) return false;
        const isDeliv = exp.every((w: any) => {
          const bName = p.client || p.description || '';
          if (w.watched_products && w.watched_products.length > 0) {
            return getStockPercSync(w.warehouse_code, bName, w.watched_products, w.received_element_ids) === 100;
          }
          return w.received_element_ids && w.received_element_ids.length > 0;
        });
        if (!isDeliv) return false;
      }

      return true;
    });
  }, [summaries, projectSearch, clientFilter, dateFrom, dateTo, missingDocs, missingDelivery, delivered]);

  // ── Core counts ──────────────────────────────────────────────────────────
  const totalProjects = filteredSummaries.length;
  const totalTargets = filteredSummaries.reduce((a, p) => a + p.stats.totalCalls, 0);
  const totalCompleted = filteredSummaries.reduce((a, p) => a + p.stats.completed, 0);
  const totalInProgress = filteredSummaries.reduce((a, p) => a + p.stats.inProgress, 0);
  const totalIssues = filteredSummaries.reduce((a, p) => a + p.stats.issues, 0);
  const overallPct = totalTargets > 0 ? Math.round((totalCompleted / totalTargets) * 100) : 0;

  // ── Lists ────────────────────────────────────────────────────────────────
  const pendingBriefList = filteredSummaries.filter(p => !p.brief_document_url);
  const pendingCEList = filteredSummaries.filter(p => !p.cost_estimate_document_url);
  const pendingPOList = filteredSummaries.filter(p => !p.purchase_order_document_url);
  const pendingDelivList = filteredSummaries.filter(p => {
    const wa = p.warehouse_availability;
    if (!wa || !Array.isArray(wa)) return false;
    const expecting = wa.filter((w: any) => w.is_expecting);
    if (expecting.length === 0) return false;
    return expecting.some((w: any) => {
      const bName = w.branch_name || '';
      if (w.watched_products && w.watched_products.length > 0) {
        return getStockPercSync(w.warehouse_code, bName, w.watched_products, w.received_element_ids) < 100;
      }
      return !w.received_element_ids || w.received_element_ids.length === 0;
    });
  });

  const completedDelivList = filteredSummaries.filter(p => {
    const wa = p.warehouse_availability;
    if (!wa || !Array.isArray(wa)) return false;
    const exp = wa.filter((w: any) => w.is_expecting);
    if (exp.length === 0) return false;
    return exp.every((w: any) => {
      const bName = w.branch_name || '';
      if (w.watched_products && w.watched_products.length > 0) {
        return getStockPercSync(w.warehouse_code, bName, w.watched_products, w.received_element_ids) === 100;
      }
      return w.received_element_ids && w.received_element_ids.length > 0;
    });
  });
  const readyForExecutionList = completedDelivList.filter(p => p.brief_document_url && p.cost_estimate_document_url && p.purchase_order_document_url);

  const pilotRequired = filteredSummaries.filter(p => {
    let data = p.pilot_installation_required;
    if (typeof data === 'string') { try { data = JSON.parse(data); } catch (e) { return false; } }
    const req = data?.required;
    return req === true || req === 'true' || req === 1 || String(req).toLowerCase() === 'required' || req === 'yes';
  });
  const pilotPending = pilotRequired.filter(p => !p.pilot_installation_required?.proposed_date);
  const pilotUpcoming = pilotRequired
    .filter(p => {
      let data = p.pilot_installation_required;
      if (typeof data === 'string') { try { data = JSON.parse(data); } catch (e) { return false; } }
      return !!data?.proposed_date;
    })
    .map(p => {
      let data = p.pilot_installation_required;
      if (typeof data === 'string') { try { data = JSON.parse(data); } catch (e) { } }
      // Ensure we use the parsed data for everything below
      const proposedDate = new Date(data!.proposed_date!);
      return { ...p, pilot_installation_required: data, proposedDate };
    })
    .filter(p => p.proposedDate >= startOfToday)
    .sort((a, b) => a.proposedDate.getTime() - b.proposedDate.getTime());

  if (loading && summaries.length === 0) return (
    <div className="flex flex-col items-center justify-center py-40 space-y-4">
      <Loader2 className="w-8 h-8 animate-spin text-[#96c36d]" />
      <p className="text-[10px] font-semibold text-slate-300 ">Loading Data</p>
    </div>
  );

  // ── Scrollable project-name list card ─────────────────────────────────────
  const ListCard: React.FC<{
    icon: React.ElementType;
    iconColor: string;
    label: string;
    sublabel: string;
    count: number;
    projects: ProjectSummary[];
    emptyText: string;
  }> = ({ icon: Icon, iconColor, label, sublabel, count, projects, emptyText }) => (
    <div className="bg-[#fbfbfa] rounded-[3px] bg-[#f9fafc] flex flex-col overflow-hidden" style={{ height: '420px' }}>
      {/* Card header — matches screenshot: project name area style */}
      <div className="px-8 pt-7 pb-5 shrink-0">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-semibold text-slate-400  mb-2">{sublabel}</p>
            <h3 className="text-lg font-semibold text-[#1e2a32] leading-tight">{label}</h3>
          </div>
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 flex items-center justify-center shrink-0 ml-4">
            <span className="text-[11px] font-bold text-slate-500">{count}</span>
          </div>
        </div>

        {/* Two stat boxes*/}
        <div className="grid grid-cols-2 gap-3 mt-5">
          {/* <div className="bg-[#f9fafc] rounded-[3px] px-4 py-3">
            <p className="text-[9px] font-semibold text-slate-400  mb-1">Projects</p>
            <p className="text-2xl font-semibold text-[#1e2a32] tracking-tight">{count}</p>
          </div> */}
          {/* <div className="bg-[#f9fafc] rounded-[3px] px-4 py-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-[3px] flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${iconColor}12`, color: iconColor }}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[9px] font-semibold text-slate-400  leading-tight">Status</p>
              <p className="text-[11px] font-bold" style={{ color: iconColor }}>
                {count === 0 ? 'Clear' : 'Pending'}
              </p>
            </div>
          </div> */}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-100 mx-8 shrink-0" />

      {/* Scrollable project list */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-3">
        {projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-2">
            <CheckCircle2 className="w-7 h-7 text-[#96c36d]/30" />
            <p className="text-xs text-slate-400">{emptyText}</p>
          </div>
        ) : (
          <div>
            {projects.map((p, i) => (
              <div key={p.project_id} className="flex items-center gap-3 py-[15px] odd:bg-slate-50 even:bg-[#efefef]">
                <span className="text-[9px] font-bold text-slate-300 w-5 shrink-0 text-right">{i + 1}</span>
                <p className="text-xs font-medium text-[#1e2a32] leading-snug">{p.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">

      {/* ── Top stat row — flat cards matching active projects ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Pending briefs', value: pendingBriefList.length, sub: 'Needs attention', icon: FileText },
          { label: 'Pending quotes', value: pendingCEList.length, sub: 'Awaiting approval', icon: BarChart3 },
          { label: 'Pending PO', value: pendingPOList.length, sub: 'Requires purchase order', icon: Briefcase },
          // { label: 'Delivered POS', value: completedDelivList.length, sub: 'Stock arrived', icon: Package },
          { label: 'Ready for execution', value: readyForExecutionList.length, sub: 'Fully approved & delivered', icon: PackageCheck },
        ].map((s, i) => (
          <div key={i} className="bg-[#fbfbfa] p-6 rounded-[9px] flex flex-col justify-between h-[130px]">
            <div className="flex justify-between items-start">
              <span className="text-[13px] font-semibold text-[#5C5D60]">{s.label}</span>
              <s.icon className="w-4 h-4 text-[#9C9C9A]" strokeWidth={2} />
            </div>
            <div>
              <span className="text-4xl font-bold text-[#141518] tracking-tight">{String(s.value)}</span>
            </div>
            <div>
              <span className="text-[11px] font-medium text-[#9C9C9A]">{s.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Visualisation & Tabs ── */}
      <div className="bg-[#fbfbfa] rounded-[9px] mb-6 p-8">
        {/* <h3 className="text-lg font-semibold text-[#1e2a32] mb-10">Status</h3> */}
        <div className="px-0">
          <div className="flex justify-between items-end w-full px-4 gap-4">
            {[
              { id: 'brief', label: 'Project Briefs', total: pendingBriefList.length + (totalProjects - pendingBriefList.length), outstanding: pendingBriefList.length, ready: totalProjects - pendingBriefList.length },
              { id: 'ce', label: 'Cost Estimates', total: pendingCEList.length + (totalProjects - pendingCEList.length), outstanding: pendingCEList.length, ready: totalProjects - pendingCEList.length },
              { id: 'po', label: 'Purchase Orders', total: pendingPOList.length + (totalProjects - pendingPOList.length), outstanding: pendingPOList.length, ready: totalProjects - pendingPOList.length },
              { id: 'deliveredPos', label: 'Delivered POS', total: pendingDelivList.length + completedDelivList.length, outstanding: pendingDelivList.length, ready: completedDelivList.length },
              { id: 'ready', label: 'Ready for Execution', total: totalProjects - readyForExecutionList.length + readyForExecutionList.length, outstanding: totalProjects - readyForExecutionList.length, ready: readyForExecutionList.length },
            ].map(stage => {
              const pctReady = stage.total > 0 ? (stage.ready / stage.total) * 100 : 0;
              const pctOutstanding = stage.total > 0 ? (stage.outstanding / stage.total) * 100 : 0;

              return (
                <div
                  key={stage.id}
                  className="flex flex-col items-center flex-1 cursor-pointer group"
                  onClick={() => setActiveTab(stage.id as any)}
                >
                  <div className="text-[13px] font-bold text-[#444] text-center px-1">
                    {stage.label}
                  </div>

                  {/* Top Label */}
                  <div className="text-[9px] font-medium text-slate-500 mb-0 mt-2">Expected: {stage.total}</div>

                  {/* Dashed Line and Bars Container */}
                  <div className="w-full max-w-[120px] border-t border-dashed border-slate-300 relative flex justify-center h-48">
                    <div className="flex items-end gap-2.5 h-full w-full justify-center">
                      {/* Ready Bar (Green) */}
                      <div className="relative h-full flex items-end">
                        <div
                          className="w-4 bg-[#9cc16b] rounded-t-sm transition-all duration-1000 group-hover:brightness-110"
                          style={{ height: `${pctReady}%` }}
                        ></div>
                      </div>
                      {/* Outstanding Bar (Orange -> Red) */}
                      <div className="relative h-full flex items-end">
                        <div
                          className="w-4 bg-[rgb(232,68,66)] rounded-t-sm transition-all duration-1000 group-hover:brightness-110"
                          style={{ height: `${pctOutstanding}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Stats */}
                  <div className="mt-3 flex flex-col items-center text-[11px] text-slate-500 font-medium leading-[1.4]">
                    <div>Ready: {stage.ready}</div>
                    <div>Outstanding: {stage.outstanding}</div>
                  </div>

                  {/* Stage Name */}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Unified Card for Filters, Tabs & Selected List ── */}
      <div id="project-filters-card" className="bg-[#fbfbfa] rounded-[9px]">
        <div className="px-[15px] pt-[30px]">
          {/* <h3 className="text-lg font-semibold text-[#1e2a32] mb-6">Projects</h3> */}
          {/* Header Filters */}
          <div className="flex flex-wrap items-center gap-[12px] mb-[24px]">
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C9A]" />
              <input
                type="text"
                placeholder="Search project..."
                value={projectSearch}
                onChange={e => setProjectSearch(e.target.value)}
                className="inline-flex items-center gap-[6px] min-w-[150px] w-[240px] h-[45px] pl-[36px] pr-[12px] bg-[#fbfbfa] border border-[#e9e9e9] rounded-[9px] text-[13px] font-medium text-[#111111] transition-all duration-150 outline-none shadow-none focus:border-[#111111] placeholder:text-[#9C9C9A]"
              />
            </div>
            <div className="w-[200px] h-[45px] shrink-0">
              <SearchableDropdown
                options={uniqueClients}
                value={clientFilter}
                onChange={setClientFilter}
                placeholder="Search..."
                defaultLabel="All"
                className="w-full h-full"
              />
            </div>

            {/* Date Range Filters */}
            <div className="shrink-0">
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            </div>

            {/* Toggle Check Buttons */}
            <div className="flex items-center gap-2 h-[45px] shrink-0 ml-auto">
              <button
                onClick={() => {
                  setMissingDocs(false);
                  setMissingDelivery(false);
                  setDelivered(false);
                }}
                className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${isAll ? 'bg-slate-100 text-slate-800 border border-slate-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                  }`}
              >
                <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${isAll ? 'bg-slate-500 border-slate-500' : 'border-slate-300'}`}>
                  {isAll && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                All
              </button>
              <button
                onClick={() => {
                  const val = !missingDocs;
                  setMissingDocs(val);
                  if (val) { setMissingDelivery(false); setDelivered(false); }
                }}
                className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${missingDocs ? 'bg-orange-50 text-orange-600 border border-orange-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                  }`}
              >
                <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${missingDocs ? 'bg-orange-500 border-orange-500' : 'border-slate-300'}`}>
                  {missingDocs && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                Missing docs
              </button>
              <button
                onClick={() => {
                  const val = !missingDelivery;
                  setMissingDelivery(val);
                  if (val) { setMissingDocs(false); setDelivered(false); }
                }}
                className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${missingDelivery ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                  }`}
              >
                <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${missingDelivery ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
                  {missingDelivery && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                Missing delivery
              </button>
              <button
                onClick={() => {
                  const val = !delivered;
                  setDelivered(val);
                  if (val) { setMissingDocs(false); setMissingDelivery(false); }
                }}
                className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${delivered ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                  }`}
              >
                <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${delivered ? 'bg-green-500 border-green-500' : 'border-slate-300'}`}>
                  {delivered && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                Delivered
              </button>
            </div>
          </div>

          <hr className="border-[#e9e9e9] mb-[24px]" />

        </div>

        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 px-[15px] pb-[15px]">
          <ProjectTable
            projects={filteredSummaries}
            onOpenProject={onOpenProject}
            maxHeight=""
            loading={loading}
          />
        </div>
      </div>

      {/* ── Upcoming Pilot Installations — full width, 1 col ── */}
      <div className="bg-[#f9fafc] rounded-[3px] overflow-hidden">
        {/* Card header */}
        <div className="px-8 pt-7 pb-5 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-[8px] bg-amber-50 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-2xl font-semibold tracking-tight text-[#1e2a32]">{pilotUpcoming.length}</span>
          </div>
          <p className="text-[13px] font-semibold text-slate-500 mt-2">Upcoming Pilot Installations</p>
        </div>

        {/* Table */}
        {pilotUpcoming.length === 0 ? (
          <div className="px-8 py-10 text-center">
            <p className="text-sm text-slate-400">No upcoming pilot installations scheduled</p>
          </div>
        ) : (
          <>
            {/* Column headers */}
            <div className="grid px-8 py-3 bg-slate-50/80 border-b border-slate-100" style={{ gridTemplateColumns: '1fr auto auto' }}>
              <span className="text-[10px] font-semibold text-slate-400 ">Project · Target Store</span>
              <span className="text-[10px] font-semibold text-slate-400  text-right pr-16">Date &amp; Time</span>
              <span className="text-[10px] font-semibold text-slate-400  text-right w-20">Due</span>
            </div>
            <div className="divide-y divide-slate-100">
              {pilotUpcoming.map(p => {
                const daysUntil = Math.ceil((p.proposedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={p.project_id} className="grid items-center gap-6 px-8 py-4 hover:bg-[#fbfbfa] transition-colors" style={{ gridTemplateColumns: '1fr auto auto' }}>
                    {/* Project + target */}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1e2a32] truncate">{p.name}</p>
                      {p.pilot_installation_required?.target_store ? (
                        <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <span className="text-slate-300">—</span>
                          <MapPin className="w-3 h-3 shrink-0" />
                          {p.pilot_installation_required.target_store}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-300 mt-0.5 italic">No target store set</p>
                      )}
                    </div>
                    {/* Date + time */}
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-[#1e2a32]">
                        {p.proposedDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {p.proposedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {/* Due badge */}
                    <div className="flex justify-end w-20 shrink-0">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${daysUntil <= 3
                        ? 'bg-red-50 text-red-600 border-red-100'
                        : daysUntil <= 7
                          ? 'bg-amber-50 text-amber-600 border-amber-100'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                        }`}>
                        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

    </div>
  );
};

// ─── StoreTimeline — fully typed, no IIFE ─────────────────────────────────────
type StoreRow = { name: string; status: string; date: string;[key: string]: any };

const StoreTimeline: React.FC<{
  storeBreakdown: StoreRow[];
  projectId: number;
  projectName: string;
  onOpenStore: (name: string, projectId: number, projectName: string) => void;
}> = ({ storeBreakdown, projectId, projectName, onOpenStore }) => {
  type EnrichedStore = StoreRow & { dateObj: Date };

  const storesWithDates: EnrichedStore[] = storeBreakdown
    .filter((s) => s.date && s.date !== 'Not visited')
    .map((s) => ({
      ...s,
      dateObj: new Date(s.date.includes('/') ? s.date.split('/').reverse().join('-') : s.date),
    }))
    .filter((s) => !isNaN(s.dateObj.getTime()))
    .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());

  const unvisited: StoreRow[] = storeBreakdown.filter((s) => !s.date || s.date === 'Not visited');

  if (storesWithDates.length === 0 && unvisited.length === 0) {
    return (
      <div className="py-20 text-center">
        <Calendar className="w-10 h-10 text-slate-200 mx-auto mb-3" />
        <p className="text-sm text-slate-400 font-medium">No visit dates available</p>
      </div>
    );
  }

  // Group by formatted day label
  const byDay: Record<string, EnrichedStore[]> = storesWithDates.reduce(
    (acc: Record<string, EnrichedStore[]>, s: EnrichedStore) => {
      const key = s.dateObj.toLocaleDateString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
      });
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
      return acc;
    },
    {}
  );

  const days: [string, EnrichedStore[]][] = Object.entries(byDay);
  const totalVisited = storesWithDates.length;
  const maxPerDay = days.length > 0 ? Math.max(...days.map(([, stores]) => stores.length)) : 1;

  return (
    <div className="space-y-1 animate-in fade-in duration-300">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-5 pb-4 border-b border-slate-100">
        <span className="text-[10px] font-semibold text-slate-400 ">
          {totalVisited} stores visited across {days.length} day{days.length !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-3 ml-auto">
          {[
            { color: 'bg-[#96c36d]', label: 'Completed' },
            { color: 'bg-blue-400', label: 'Planned' },
            { color: 'bg-slate-300', label: 'Unplanned' },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${l.color}`}></div>
              <span className="text-[10px] text-slate-400 font-medium">{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Day rows */}
      {days.map(([dayLabel, dayStores]: [string, EnrichedStore[]]) => {
        const barWidth = maxPerDay > 0 ? Math.round((dayStores.length / maxPerDay) * 100) : 0;
        const completedCount = dayStores.filter((s: EnrichedStore) => s.status === 'Completed').length;
        const plannedCount = dayStores.filter((s: EnrichedStore) => s.status === 'Planned').length;
        const unplannedCount = dayStores.filter((s: EnrichedStore) => s.status === 'Unplanned').length;
        const parts = dayLabel.split(', ');

        return (
          <div key={dayLabel} className="group">
            <div className="grid items-start gap-4" style={{ gridTemplateColumns: '160px 1fr' }}>
              {/* Date label */}
              <div className="pt-2.5 pr-4">
                <p className="text-xs font-semibold text-[#1e2a32]">{parts.slice(0, 2).join(', ')}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{parts[2] || ''}</p>
              </div>
              {/* Bar + chips */}
              <div className="py-2.5 border-l border-slate-100 pl-5">
                <div className="flex items-center gap-2 mb-2.5">
                  <div
                    className="flex h-2 rounded-full overflow-hidden bg-slate-100"
                    style={{ width: `${Math.max(barWidth, 4)}%`, minWidth: '40px', maxWidth: '300px' }}
                  >
                    {completedCount > 0 && (
                      <div className="bg-[#96c36d] h-full" style={{ width: `${(completedCount / dayStores.length) * 100}%` }} />
                    )}
                    {plannedCount > 0 && (
                      <div className="bg-blue-400 h-full" style={{ width: `${(plannedCount / dayStores.length) * 100}%` }} />
                    )}
                    {unplannedCount > 0 && (
                      <div className="bg-slate-300 h-full" style={{ width: `${(unplannedCount / dayStores.length) * 100}%` }} />
                    )}
                  </div>
                  <span className="text-[10px] font-bold text-slate-500">
                    {dayStores.length} store{dayStores.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {dayStores.map((s: EnrichedStore, si: number) => (
                    <button
                      key={si}
                      onClick={(e) => { e.stopPropagation(); onOpenStore(s.name, projectId, projectName); }}
                      className={`inline-flex items-center px-2.5 py-1 rounded-[3px] text-[11px] font-medium border transition-all hover:opacity-80 ${s.status === 'Completed'
                        ? 'bg-[#96c36d]/10 text-[#5a8b34] border-[#96c36d]/20'
                        : s.status === 'Planned'
                          ? 'bg-blue-50 text-blue-700 border-blue-100'
                          : 'bg-slate-50 text-slate-500 border-slate-200'
                        }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-b border-slate-50 my-1 ml-[160px]"></div>
          </div>
        );
      })}

      {/* Unvisited */}
      {unvisited.length > 0 && (
        <div className="mt-6 pt-6 border-t border-solid border-slate-200">
          <div className="grid items-start gap-4" style={{ gridTemplateColumns: '160px 1fr' }}>
            <div className="pt-2">
              <p className="text-xs font-semibold text-slate-400">Not Visited</p>
              <p className="text-[10px] text-slate-300 mt-0.5">{unvisited.length} stores</p>
            </div>
            <div className="pl-5 border-l border-slate-100 pt-2">
              <div className="flex flex-wrap gap-1.5">
                {unvisited.map((s: StoreRow, si: number) => (
                  <button
                    key={si}
                    onClick={(e) => { e.stopPropagation(); onOpenStore(s.name, projectId, projectName); }}
                    className="inline-flex items-center px-2.5 py-1 rounded-[3px] text-[11px] font-medium bg-slate-50 text-slate-400 hover:bg-slate-100 transition-all"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 1000;


// ─── Role-Based Access Control Helpers ────────────────────────────────────────
const isAdmin = (role: string | undefined) => {
  if (!role) return false;
  const normalizedRole = role.toLowerCase().trim();
  return normalizedRole === 'admin' || normalizedRole === 'project manager';
};

const canAccessManagementFeatures = (userProfile: UserProfile | null) => {
  if (!userProfile) return false;
  return isAdmin(userProfile.role);
};

const canAccessExecutiveReport = (userProfile: UserProfile | null) => {
  if (!userProfile) return false;
  const role = userProfile.role?.toLowerCase().trim();
  // Role 'user' should not see executive report
  return role !== 'user';
};

const hasClientAccess = (userProfile: UserProfile | null) => {
  if (!userProfile) return false;
  // Admins and Project Managers have access to all clients
  if (isAdmin(userProfile.role)) return true;
  // Regular users must have linked_clients array with at least one client
  return Array.isArray(userProfile.linked_clients) && userProfile.linked_clients.length > 0;
};

// ─── Main App ─────────────────────────────────────────────────────────────────
const App: React.FC = () => {
  const [globalPosModalProject, setGlobalPosModalProject] = useState<any>(null);
  useEffect(() => {
    globalOpenPosModal = setGlobalPosModalProject;
    return () => { globalOpenPosModal = null; };
  }, []);

  const [view, setView] = useState<any>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view');
      if (v) return v;
    }
    return 'overview';
  });

  const [session, setSession] = useState<any>(() => {
    const cached = localStorage.getItem('vworx_session_cache');
    if (cached) {
      try { return JSON.parse(cached); } catch (e) { return null; }
    }
    return null;
  });
  const [initializing, setInitializing] = useState(() => {
    // If we have a cached session, we can start in a non-initializing state
    return !localStorage.getItem('vworx_session_cache');
  });
  // Default to list (table) view
  const [displayMode, setDisplayMode] = useState<'card' | 'list'>('list');
  const [summaries, setSummaries] = useState<ProjectSummary[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(() => {
    return !localStorage.getItem('vworx_summaries_cache');
  });
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [pdfExportProgress, setPdfExportProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [newIssue, setNewIssue] = useState('');
  const [addingIssue, setAddingIssue] = useState(false);
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);
  const [isPosManagerOpen, setIsPosManagerOpen] = useState(false);
  const [isDocManagerOpen, setIsDocManagerOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isReportsMenuOpen, setIsReportsMenuOpen] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  // Global Stock Cache: branchName-warehouseCode -> Set of available SKUs
  const [globalStockCache, setGlobalStockCache] = useState<Record<string, Map<string, number>>>({});
  const [isStockSyncing, setIsStockSyncing] = useState(false);

  const fetchGlobalStock = async (summariesToSync: ProjectSummary[]) => {
    const branchNames = [...new Set(summariesToSync.flatMap(p =>
      (p.warehouse_availability || []).map(w => (w as any).branch_name).filter(Boolean)
    ))];

    if (branchNames.length === 0) return;

    setIsStockSyncing(true);
    try {
      const { data, error } = await supabase
        .from('warehouse_inventory')
        .select('branch_name, warehouse_code, warehouse_name, sku, qty')
        .in('branch_name', branchNames)
        .gt('qty', 0);

      if (!error && data) {
        const newMap: Record<string, Map<string, number>> = {};
        data.forEach(item => {
          if (!item.branch_name) return;
          // Normalize: "MARS South Africa + 47" -> "MARS South Africa"
          const cleanBranch = item.branch_name.split(' + ')[0].trim();

          // Helper to add to map
          const addToMap = (code: string) => {
            const key = `${cleanBranch}-${code}`;
            if (!newMap[key]) newMap[key] = new Map();
            newMap[key].set(item.sku, item.qty);
          };

          if (item.warehouse_code) addToMap(item.warehouse_code);
          if (item.warehouse_name) addToMap(item.warehouse_name);

          // Also add standardized code
          const mapped = mapWarehouseToCode(item.warehouse_code || item.warehouse_name || '');
          if (mapped) addToMap(mapped);
        });
        setGlobalStockCache(newMap);
      }
    } catch (e) {
      console.error("[StockBatch] Failed:", e);
    } finally {
      setIsStockSyncing(false);
    }
  };

  const SyncIndicator = () => {
    const isCalculating = allSummaries.length > 0 && allSummaries.some(p => p.isStatsLoading);
    if (loading || isCalculating) {
      return (
        <div className="flex items-center gap-2 bg-[#96c36d]/5 px-2.5 py-1 rounded-[3px] border border-[#96c36d]/10 animate-pulse">
          <Loader2 className="w-3 h-3 animate-spin text-[#96c36d]" />
          <span className="text-[9px] font-bold text-[#5a8b34]">{loading ? 'Syncing' : 'Syncing'}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center">
        <div className="w-1.5 h-1.5 rounded-full bg-[#96c36d]" />
      </div>
    );
  };
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    const cached = localStorage.getItem('vworx_profile_cache');
    return cached ? JSON.parse(cached) : null;
  });
  const [watermarkData, setWatermarkData] = useState<{ url: string; ts: string; store: string; gpsCoordinates?: any } | null>(null);
  const [selectedStore, setSelectedStore] = useState<{ name: string; projectId: number; projectName: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'planned' | 'unplanned' | 'timeline'>('all');
  const [urgencyModalTarget, setUrgencyModalTarget] = useState<any>(null);
  const [urgencyReasonText, setUrgencyReasonText] = useState('');
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [availableTargets, setAvailableTargets] = useState<any[]>([]);
  const [detailTab, setDetailTab] = useState<'breakdown' | 'velocity' | 'visuals' | 'questionnaire' | 'readiness'>('breakdown');
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 0. Hydrate IndexedDB caches
  useEffect(() => {
    const hydrate = async () => {
      // We only hydrate project details now; snapshots are handled live via DB
    };
    hydrate();
  }, []);

  // ── Pagination state ──────────────────────────────────────────────────────
  const [allSummaries, setAllSummaries] = useState<ProjectSummary[]>(() => {
    const params = new URLSearchParams(window.location.search);
    const initialView = params.get('view') || 'dashboard';
    const key = initialView === 'pastProjects' ? 'vworx_past_summaries' : 'vworx_active_summaries';
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : [];
  }); // full data store
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);

  // ── Dashboard filter state ────────────────────────────────────────────────
  const [dashSearch, setDashSearch] = useState(() => localStorage.getItem('reportpro_search_query') || '');
  const [dashClientFilter, setDashClientFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterMissingDocs, setFilterMissingDocs] = useState(false);
  const [filterMissingDelivery, setFilterMissingDelivery] = useState(false);
  const [filterDelivered, setFilterDelivered] = useState(false);
  const isDashAll = !filterMissingDocs && !filterMissingDelivery && !filterDelivered;
  const [showFilters, setShowFilters] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('vworx_sidebar_collapsed') === 'true';
  });
  const [isWHSMenuOpen, setIsWHSMenuOpen] = useState(false);
  const [isPerfToolsMenuOpen, setIsPerfToolsMenuOpen] = useState(false);
  const [isClientMetricsMenuOpen, setIsClientMetricsMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsWHSMenuOpen(false);
        setIsPerfToolsMenuOpen(false);
        setIsClientMetricsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync sidebar collapse to localStorage
  useEffect(() => {
    localStorage.setItem('vworx_sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // ─── Three-month date helper ───────────────────────────────────────────────
  const threeMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d;
  }, []);

  // 1. Session + URL deep-link parsing (once on mount)
  useEffect(() => {
    const syncProfile = async (u: any) => {
      const profile = await getCurrentUserProfile();
      if (profile) {
        setUserProfile(profile);
        safeSetItem('vworx_profile_cache', JSON.stringify(profile));
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        safeSetItem('vworx_auth_hint', 'true');
        safeSetItem('vworx_session_cache', JSON.stringify(session));
        syncProfile(session.user);
      } else {
        localStorage.removeItem('vworx_auth_hint');
        localStorage.removeItem('vworx_profile_cache');
        localStorage.removeItem('vworx_session_cache');
      }
      setInitializing(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session) {
        safeSetItem('vworx_auth_hint', 'true');
        safeSetItem('vworx_session_cache', JSON.stringify(session));
        syncProfile(session.user);
      } else {
        localStorage.removeItem('vworx_auth_hint');
        localStorage.removeItem('vworx_profile_cache');
        localStorage.removeItem('vworx_session_cache');
        setUserProfile(null);
      }
      if (event === 'PASSWORD_RECOVERY') setIsRecoveryMode(true);
    });
    const params = new URLSearchParams(window.location.search);
    const viewImg = params.get('viewImage');
    const ts = params.get('ts');
    const store = params.get('store');
    const gps = params.get('gps');
    if (viewImg) {
      setWatermarkData({
        url: decodeURIComponent(viewImg),
        ts: ts ? decodeURIComponent(ts) : '',
        store: store ? decodeURIComponent(store) : 'Image Verification',
        gpsCoordinates: gps ? decodeURIComponent(gps) : undefined
      });
    }
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const urlView = params.get('view');
      const urlProjectId = params.get('projectId');
      const urlStatusFilter = params.get('statusFilter');
      const urlStoreName = params.get('storeName');
      const urlProjectName = params.get('projectName');
      
      if (urlView === 'detail' && urlProjectId) { 
        setView('detail'); 
        handleOpenProject(parseInt(urlProjectId), false); 
        if (urlStatusFilter) setStatusFilter(urlStatusFilter as any); 
      }
      else if (urlView === 'callDetail' && urlStoreName && urlProjectId && urlProjectName) { 
        setView('callDetail'); 
        setSelectedStore({ name: decodeURIComponent(urlStoreName), projectId: parseInt(urlProjectId), projectName: decodeURIComponent(urlProjectName) }); 
      }
      else if (urlView) {
        setView(urlView as any);
      } else { 
        setView('dashboard'); 
        setSelectedStore(null); 
        setStatusFilter('all'); 
      }
    };

    const initialParams = new URLSearchParams(window.location.search);
    const initialView = initialParams.get('view');
    if (initialView === 'detail' && initialParams.get('projectId')) {
      handleOpenProject(parseInt(initialParams.get('projectId')!), false);
      if (initialParams.get('statusFilter')) setStatusFilter(initialParams.get('statusFilter') as any);
    } else if (initialView === 'callDetail' && initialParams.get('storeName') && initialParams.get('projectId') && initialParams.get('projectName')) {
      setSelectedStore({ 
        name: decodeURIComponent(initialParams.get('storeName')!), 
        projectId: parseInt(initialParams.get('projectId')!), 
        projectName: decodeURIComponent(initialParams.get('projectName')!) 
      });
    }
    window.addEventListener('popstate', handlePopState);
    return () => { subscription.unsubscribe(); window.removeEventListener('popstate', handlePopState); };
  }, []);

  // Watermark URL sync
  useEffect(() => {
    if (watermarkData) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('viewImage') !== encodeURIComponent(watermarkData.url)) {
        const newParams = new URLSearchParams();
        newParams.set('viewImage', encodeURIComponent(watermarkData.url));
        newParams.set('ts', encodeURIComponent(watermarkData.ts));
        newParams.set('store', encodeURIComponent(watermarkData.store));
        if (watermarkData.gpsCoordinates) {
          const gpsVal = typeof watermarkData.gpsCoordinates === 'object' ? JSON.stringify(watermarkData.gpsCoordinates) : watermarkData.gpsCoordinates;
          newParams.set('gps', encodeURIComponent(gpsVal));
        }
        window.history.pushState({}, '', `${window.location.pathname}?${newParams.toString()}`);
      }
    }
  }, [watermarkData]);

  useEffect(() => {
    // Load dashboard data once upon session establishment (page refresh/mount).
    // This ensures data is ready even if the user starts on a detail page.
    if (session) {
      loadDashboard();
    }
  }, [session]);
  useEffect(() => {
    if (view === 'callDetail' && selectedStore) {
      const fetchTargets = async () => {
        const { data } = await supabase.from('targets').select('target_id, name, project_id').eq('project_id', selectedStore.projectId).order('name', { ascending: true });
        setAvailableTargets(data || []);
      };
      fetchTargets();
    }
  }, [view, selectedStore]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => { if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) setShowSearchDropdown(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync display mode to localStorage
  useEffect(() => { safeSetItem('reportpro_view_mode', displayMode); }, [displayMode]);
  useEffect(() => { safeSetItem('reportpro_search_query', dashSearch); }, [dashSearch]);

  const loadDashboard = async (isLoadMore = false) => {
    try {
      // Only show full-page loader if we have NO data at all
      if (!isLoadMore && allSummaries.length === 0) {
        setLoading(true);
      }

      setError(null);

      // Always fetch a fresh profile to ensure linked_clients filter is up-to-date
      let currentUser = await getCurrentUserProfile();
      if (currentUser) setUserProfile(currentUser);
      const currentOffset = isLoadMore ? allSummaries.length : 0;
      const fetchFilter: boolean | 'all' = 'all'; // Fetch both active and past projects on refresh

      // PHASE 1: Fast metadata load — use freshly-fetched currentUser, NOT stale userProfile state
      const result = await fetchProjectsMetadata(currentUser, currentOffset, PAGE_SIZE, fetchFilter);
      const metadata = result.projects;

      // If no data returned on load more, we're done
      if (metadata.length === 0 && isLoadMore) {
        setHasMore(false);
        return;
      }

      const newSummaries = isLoadMore ? [...allSummaries, ...metadata] : metadata;
      setAllSummaries(newSummaries);
      setSummaries(newSummaries);
      setHasMore(result.hasMore);

      // Cache based on current view
      if (!isLoadMore) {
        const cacheKey = view === 'pastProjects' ? 'vworx_past_summaries' : 'vworx_active_summaries';
        safeSetItem(cacheKey, JSON.stringify(metadata.slice(0, 50)));
        setVisibleCount(PAGE_SIZE);
      }

      setLoading(false);

      // PHASE 2: Heavy stats load (Background) - wrap in try/catch to prevent infinite streaming
      try {
        const projectsWithStats = await fetchProjectsStats(metadata);

        setAllSummaries(prev => {
          const updated = [...prev];
          projectsWithStats.forEach(stat => {
            const index = updated.findIndex(p => p.project_id === stat.project_id);
            if (index !== -1) {
              updated[index] = { ...updated[index], ...stat, isStatsLoading: false };
            }
          });
          return updated;
        });

        setSummaries(prev => {
          const updated = [...prev];
          projectsWithStats.forEach(stat => {
            const index = updated.findIndex(p => p.project_id === stat.project_id);
            if (index !== -1) {
              updated[index] = { ...updated[index], ...stat, isStatsLoading: false };
            }
          });
          return updated;
        });

        // Update cache with stats
        if (!isLoadMore) {
          const cacheKey = view === 'pastProjects' ? 'vworx_past_summaries' : 'vworx_active_summaries';
          safeSetItem(cacheKey, JSON.stringify(projectsWithStats.slice(0, 50)));
        }


        // Batch fetch stock for all projects in background
        fetchGlobalStock(projectsWithStats);

      } catch (statsError) {
        console.error('Failed to load project stats:', statsError);
        // Mark all projects as not loading even if stats failed
        setAllSummaries(prev => prev.map(p => ({ ...p, isStatsLoading: false })));
        setSummaries(prev => prev.map(p => ({ ...p, isStatsLoading: false })));
      }

    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError(err?.message || "Failed to synchronize with server.");
      setLoading(false);
      setHasMore(false);
      // Ensure we stop the loading indicator
      setAllSummaries(prev => prev.map(p => ({ ...p, isStatsLoading: false })));
      setSummaries(prev => prev.map(p => ({ ...p, isStatsLoading: false })));
    }
  };
  // Update loadMore to fetch from DB if needed
  const loadMore = () => {
    if (visibleCount + PAGE_SIZE > allSummaries.length && hasMore) {
      loadDashboard(true);
    }
    setVisibleCount(c => c + PAGE_SIZE);
  };

  const handleOpenProject = async (projectId: number, updateUrl: boolean = true) => {
    setView('detail');
    setQuestionnaireCount(null); // reset badge while loading
    setDetailTab('breakdown');   // reset to first tab
    if (updateUrl) { const params = new URLSearchParams(); params.set('view', 'detail'); params.set('projectId', projectId.toString()); params.set('statusFilter', statusFilter); window.history.pushState({}, '', `?${params.toString()}`); }

    try {
      const cacheKey = `project_${projectId}`;
      const cachedData = await dbGet(STORES.PROJECT_DETAILS, cacheKey);

      if (cachedData) {
        setReportData(cachedData);
        setLoading(false);
      } else {
        setLoading(true);
      }

      setError(null);
      const data = await fetchProjectData(projectId);
      setReportData(data);
      dbSet(STORES.PROJECT_DETAILS, cacheKey, data);

    } catch (err: any) {
      setError(err?.message || "Critical data retrieval error.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    localStorage.removeItem('vworx_auth_hint');
    localStorage.removeItem('vworx_profile_cache');
    localStorage.removeItem('vworx_session_cache');
    localStorage.removeItem('vworx_summaries_cache');
    dbClear();
  };

  const getStockPercSync = (warehouseCode: string, branchName: string, watchedProducts: any[], receivedElementIds?: any[]) => {
    if (!watchedProducts || watchedProducts.length === 0) return 0;
    const key = `${branchName}-${warehouseCode}`;
    const availableSKUs = globalStockCache ? globalStockCache[key] : null;
    const safeAvailableSKUs = availableSKUs || new Map();
    const receivedIdsSet = new Set((receivedElementIds || []).map(id => String(id)));

    let availableCount = 0;
    watchedProducts.forEach(wp => {
      const isManual = receivedIdsSet.has(String(wp.ProductID));
      const cleanSku = (wp.SKU || '').trim();
      if (isManual || safeAvailableSKUs.has(cleanSku)) {
        availableCount++;
      }
    });
    return Math.round((availableCount / watchedProducts.length) * 100);
  };

  // ── Dashboard filter + search ─────────────────────────────────────────────
  const filteredSummaries = useMemo(() => {
    let result = allSummaries;

    // Filter by project_hidden status
    if (view === 'pastProjects') {
      result = result.filter(p => p.project_hidden === true);
    } else {
      // For Active projects and Overview, show only non-hidden
      result = result.filter(p => p.project_hidden !== true);
    }

    if (dashSearch.trim()) {
      const term = dashSearch.toLowerCase();
      result = result.filter(p => (p.name?.toLowerCase() || '').includes(term) || (p.reference?.toLowerCase() || '').includes(term) || (p.description?.toLowerCase() || '').includes(term));
    }

    if (dateFrom) result = result.filter(p => p.created_at && new Date(p.created_at) >= new Date(dateFrom));
    if (dateTo) result = result.filter(p => p.created_at && new Date(p.created_at) <= new Date(dateTo + 'T23:59:59'));

    if (filterMissingDocs) {
      result = result.filter(p => !p.brief_document_url || !p.purchase_order_document_url || !p.cost_estimate_document_url);
    }

    if (filterMissingDelivery) {
      result = result.filter(p => {
        const wa = p.warehouse_availability;
        if (!wa || !Array.isArray(wa)) return true;
        const expecting = wa.filter(w => w.is_expecting);
        if (expecting.length === 0) return true;
        return expecting.some(w => !w.received_element_ids || w.received_element_ids.length === 0);
      });
    }

    if (filterDelivered) {
      result = result.filter(p => {
        const wa = p.warehouse_availability;
        if (!wa || !Array.isArray(wa)) return false;
        const exp = wa.filter((w: any) => w.is_expecting);
        if (exp.length === 0) return false;
        const isDeliv = exp.every((w: any) => {
          const bName = p.client || p.description || '';
          if (w.watched_products && w.watched_products.length > 0) {
            return getStockPercSync(w.warehouse_code, bName, w.watched_products, w.received_element_ids) === 100;
          }
          return w.received_element_ids && w.received_element_ids.length > 0;
        });
        return isDeliv;
      });
    }

    if (dashClientFilter !== 'All') {
      result = result.filter(p => {
        const c = p.client || p.description;
        return c === dashClientFilter;
      });
    }

    return result;
  }, [allSummaries, dashSearch, dashClientFilter, dateFrom, dateTo, filterMissingDocs, filterMissingDelivery, filterDelivered, view]);

  const uniqueDashClients = useMemo(() => {
    const clients = new Set<string>();
    allSummaries.forEach(p => {
      const c = p.client || p.description;
      if (c) clients.add(c);
    });
    return Array.from(clients).sort();
  }, [allSummaries]);

  // Pagination slice
  const visibleSummaries = useMemo(() => filteredSummaries.slice(0, visibleCount), [filteredSummaries, visibleCount]);
  const canLoadMore = visibleCount < filteredSummaries.length || hasMore;

  // ── Infinity scroll: trigger loadMore when sentinel enters viewport ──────
  useEffect(() => {
    const sentinel = infiniteScrollSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && canLoadMore) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, visibleCount, filteredSummaries.length]);

  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filteredSummaries.length]);


  const handleOpenCallDetails = (storeName: string, projectId: number, projectName: string) => {
    setSelectedStore({ name: storeName, projectId, projectName });
    setView('callDetail');
    const params = new URLSearchParams();
    params.set('view', 'callDetail');
    params.set('storeName', encodeURIComponent(storeName));
    params.set('projectId', projectId.toString());
    params.set('projectName', encodeURIComponent(projectName));
    window.history.pushState({}, '', `?${params.toString()}`);
  };

  const handleBackToDetail = () => {
    setView('detail');
    setSelectedStore(null);
    if (reportData) { const params = new URLSearchParams(); params.set('view', 'detail'); params.set('projectId', reportData.project.project_id.toString()); params.set('statusFilter', statusFilter); window.history.pushState({}, '', `?${params.toString()}`); }
  };

  const updateStatusFilter = (filter: typeof statusFilter) => {
    setStatusFilter(filter);
    if (reportData) { const params = new URLSearchParams(); params.set('view', 'detail'); params.set('projectId', reportData.project.project_id.toString()); params.set('statusFilter', filter); window.history.pushState({}, '', `?${params.toString()}`); }
  };

  const handleExport = async () => {
    if (!reportData) return;
    try {
      setExporting(true);
      const result = await exportToExcel(reportData);
      if (result.error) throw result.error;
      if (result.jobId) alert(`Export job started!\n\nJob ID: ${result.jobId}\n\nCheck the "Export Archive" tab to monitor progress.`);
    } catch (err: any) { setError(err?.message || "Export process failed."); } finally { setExporting(false); }
  };

  const handleAddIssue = async () => {
    if (!newIssue.trim() || !reportData) return;
    try {
      setAddingIssue(true);
      const issue: ProjectIssue = { id: crypto.randomUUID(), text: newIssue, created_at: new Date().toISOString(), status: 'open' };
      await updateProjectIssues(reportData.project.project_id, [...(reportData.project.project_issues || []), issue]);
      const refreshedData = await fetchProjectData(reportData.project.project_id);
      setReportData(refreshedData); setNewIssue('');
    } catch (err: any) { setError(err?.message || "Incident logging failed."); } finally { setAddingIssue(false); }
  };

  const toggleIssueStatus = async (issueId: string) => {
    if (!reportData) return;
    try {
      const updated = (reportData.project.project_issues || []).map(issue => issue.id === issueId ? { ...issue, status: issue.status === 'open' ? 'resolved' : 'open' as const } : issue);
      await updateProjectIssues(reportData.project.project_id, updated as ProjectIssue[]);
      setReportData(await fetchProjectData(reportData.project.project_id));
    } catch (err: any) { setError(err?.message || "Status update failed."); }
  };

  const handleSavePosAvailability = async (availability: WarehouseAvailabilityType[]) => {
    if (!reportData) return;
    await updateProjectWarehouseAvailability(reportData.project.project_id, availability);
    setReportData(await fetchProjectData(reportData.project.project_id));
  };

  const handleToggleUrgent = async (e: React.MouseEvent, s: any) => {
    e.stopPropagation();

    // Always use the latest version of the store from reportData to avoid stale cache issues
    const latestStore = reportData?.storeBreakdown.find(st => st.name === s.name) || s;
    const targetId = latestStore.target_id || s.callData?.target_id || s.callData?.allCalls?.[0]?.target_id;

    if (!targetId) {
      alert("Missing target_id! latestStore keys: " + Object.keys(latestStore).join(', ') + "\ncallData keys: " + (latestStore.callData ? Object.keys(latestStore.callData).join(', ') : 'none'));
      return;
    }

    if (latestStore.urgent) {
      try {
        await supabase.from('targets').update({ urgent: false, urgency_reason: null, time_marked_urgernt: null }).eq('target_id', targetId);
        if (reportData) {
          setReportData(await fetchProjectData(reportData.project.project_id));
        }
      } catch (err) {
        console.error('Failed to remove urgency', err);
      }
    } else {
      setUrgencyModalTarget(latestStore);
      setUrgencyReasonText('');
    }
  };

  const handleSaveUrgent = async () => {
    if (!urgencyModalTarget || !reportData) return;
    const targetId = urgencyModalTarget.target_id || urgencyModalTarget.callData?.target_id || urgencyModalTarget.callData?.allCalls?.[0]?.target_id;

    if (!targetId) {
      alert("Missing target ID! Cannot save urgency.");
      return;
    }

    try {
      await supabase.from('targets').update({ urgent: true, urgency_reason: urgencyReasonText, time_marked_urgernt: new Date().toISOString() }).eq('target_id', targetId);
      setUrgencyModalTarget(null);
      setReportData(await fetchProjectData(reportData.project.project_id));
    } catch (err) {
      console.error('Failed to set urgency', err);
    }
  };

  const handleToggleProjectHidden = async (projectId: number, hide: boolean) => {
    try {
      setAddingIssue(true); // Using addingIssue as a simple loading state here
      await updateProjectDetails(projectId, { project_hidden: hide });
      // Refresh summaries
      const data = await fetchAllProjectsSummary(userProfile);
      setAllSummaries(data.projects);
      // If we are in detail view, refresh reportData
      if (reportData && reportData.project.project_id === projectId) {
        setReportData(await fetchProjectData(projectId));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to update project status.");
    } finally {
      setAddingIssue(false);
    }
  };

  const handleToggleProjectInvoiced = async (projectId: number, invoiced: boolean) => {
    try {
      setAddingIssue(true);
      await updateProjectDetails(projectId, { invoiced });
      // Refresh summaries
      const data = await fetchAllProjectsSummary(userProfile);
      setAllSummaries(data.projects);
      // If we are in detail view, refresh reportData
      if (reportData && reportData.project.project_id === projectId) {
        setReportData(await fetchProjectData(projectId));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to update invoice status.");
    } finally {
      setAddingIssue(false);
    }
  };

  const handleSaveDocuments = async (updates: Partial<Project>) => {
    if (!reportData) return;
    await updateProjectDetails(reportData.project.project_id, updates);
    setReportData(await fetchProjectData(reportData.project.project_id));
  };

  const dashboardStats = useMemo(() => {
    const totalCalls = filteredSummaries.reduce((acc, curr) => acc + curr.stats.totalCalls, 0);
    const completed = filteredSummaries.reduce((acc, curr) => acc + curr.stats.completed, 0);
    const active = filteredSummaries.reduce((acc, curr) => acc + curr.stats.inProgress, 0);
    const issues = filteredSummaries.reduce((acc, curr) => acc + curr.stats.issues, 0);
    const avg = totalCalls > 0 ? Math.round((completed / totalCalls) * 100) : 0;

    // Pace calculations
    let ahead = 0;
    let behind = 0;
    const nowTime = new Date().getTime();

    filteredSummaries.forEach(p => {
      if (!p.stats.minEarliest || !p.stats.maxLatest) return;
      const start = new Date(p.stats.minEarliest).getTime();
      const end = new Date(p.stats.maxLatest).getTime();
      const duration = end - start;
      if (duration <= 0) return;

      const elapsed = Math.max(0, Math.min(nowTime - start, duration));
      const timePerc = (elapsed / duration) * 100;
      const workPerc = p.stats.totalCalls > 0 ? (p.stats.completed / p.stats.totalCalls) * 100 : 0;

      if (workPerc >= timePerc) ahead++;
      else behind++;
    });

    return { totalCalls, completed, active, issues, avg, ahead, behind };
  }, [filteredSummaries]);


  const currentProjectSummary = useMemo(() => {
    if (!reportData) return null;
    const fromList = allSummaries.find(p => p.project_id === reportData.project.project_id);
    if (fromList) return fromList;
    return { ...reportData.project, stats: reportData.stats } as ProjectSummary;
  }, [reportData, allSummaries]);


  const filteredStoreBreakdown = useMemo(() => {
    if (!reportData) return { data: [], counts: { all: 0, completed: 0, planned: 0, unplanned: 0 } };
    const counts = { all: reportData.storeBreakdown.length, completed: reportData.storeBreakdown.filter(s => s.status === 'Completed').length, planned: reportData.storeBreakdown.filter(s => s.status === 'Planned').length, unplanned: reportData.storeBreakdown.filter(s => s.status === 'Unplanned').length };
    let filteredData = reportData.storeBreakdown;
    if (statusFilter !== 'all' && statusFilter !== 'timeline') { const statusMap: Record<'completed' | 'planned' | 'unplanned', string> = { completed: 'Completed', planned: 'Planned', unplanned: 'Unplanned' }; filteredData = filteredData.filter(s => s.status === statusMap[statusFilter as 'completed' | 'planned' | 'unplanned']); }
    if (storeSearchQuery.trim()) { const sl = storeSearchQuery.toLowerCase(); filteredData = filteredData.filter(s => s.name.toLowerCase().includes(sl)); }
    return { data: filteredData, counts };
  }, [reportData, statusFilter, storeSearchQuery]);

  // Count questionnaire questions for badge — set by QuestionnaireTab via callback
  const [questionnaireCount, setQuestionnaireCount] = useState<number | null>(null);

  // Infinity scroll sentinel ref
  const infiniteScrollSentinelRef = useRef<HTMLDivElement>(null);

  const searchResults = useMemo(() => {
    if (!search.trim()) return { projects: [], targets: [] };
    const term = search.toLowerCase();
    const projects = (view === 'detail' || view === 'callDetail') ? allSummaries.filter(p => (p.name?.toLowerCase() || '').includes(term) || (p.reference?.toLowerCase() || '').includes(term)).slice(0, 10) : [];
    const targets = view === 'callDetail' ? availableTargets.filter(t => (t.name?.toLowerCase() || '').includes(term)).slice(0, 10) : [];
    return { projects, targets };
  }, [search, allSummaries, availableTargets, view]);

  // ── Active filter count ────────────────────────────────────────────────────
  const activeFilterCount = [!!dateFrom, !!dateTo, filterMissingDocs, filterMissingDelivery, filterDelivered].filter(Boolean).length;

  if (initializing) {
    return (
      <div className="fixed inset-0 bg-[#fbfbfa] flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-[#96c36d]/10 border-t-[#96c36d] rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center"><Logo className="w-6 h-6 animate-pulse" /></div>
        </div>
        <div className="mt-8 space-y-2 text-center">
          <div className="text-[12px] font-bold text-slate-300 animate-pulse">Establishing Secure Session</div>
          <div className="text-[9px] text-slate-400 font-medium">Please wait while we verify your identity...</div>
        </div>
      </div>
    );
  }

  if (isRecoveryMode) return <UpdatePasswordPage onComplete={() => { setIsRecoveryMode(false); window.history.replaceState({}, document.title, window.location.pathname); }} />;

  if (!session) {
    if (watermarkData) return <div className="w-full h-screen bg-black"><WatermarkViewer imageUrl={watermarkData.url} timestamp={watermarkData.ts} storeName={watermarkData.store} gpsCoordinates={watermarkData.gpsCoordinates} onClose={() => { window.location.href = window.location.origin; }} /></div>;
    return <LoginPage onLogin={() => { }} />;
  }

  // Check if user has access to any clients
  if (!hasClientAccess(userProfile)) {
    return (
      <div className="min-h-screen bg-[#fbfbfa] flex items-center justify-center p-6 font-sans">
        <div className="w-full max-w-[520px] bg-[#fbfbfa] rounded-2xl p-12 ">
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-[#1e2a32] tracking-tight mb-2">No Client Access</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                Your account is not currently linked to any clients. Please contact your system administrator to request access.
              </p>
            </div>
            <div className="w-full pt-4 border-t border-slate-100">
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <p className="text-xs text-slate-500 mb-2">
                  <span className="font-semibold">Account:</span> {userProfile?.email || 'Unknown'}
                </p>
                <p className="text-xs text-slate-500">
                  <span className="font-semibold">Role:</span> {userProfile?.role || 'User'}
                </p>
              </div>
              <button
                onClick={handleSignOut}
                className="w-full h-12 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="app">
        {/* SIDE NAV */}
        <aside id="sidebar" className="sidebar" ref={sidebarRef}>
          <style>{`
            #sidebar { background: #121517; overflow-y: auto !important; overflow-x: hidden !important; scrollbar-width: none; -ms-overflow-style: none; }
            #sidebar::-webkit-scrollbar { display: none; }
            #sidebar .nav { overflow-y: auto !important; overflow-x: hidden !important; scrollbar-width: none; -ms-overflow-style: none; }
            #sidebar .nav::-webkit-scrollbar { display: none; }
            #sidebar .nav a { color: #bfbfbf; }
            #sidebar .nav-label { color: #626262; }
            #sidebar .nav a.active { background: #000000; font-weight: 600; }
            #sidebar .nav-dropdown { position: relative; }
            #sidebar .nav-dropdown-content {
              background: transparent;
              min-width: 100%;
              padding: 8px;
              border-radius: 6px;
              margin-top: 4px;
              display: flex;
              flex-direction: column;
            }
            #sidebar .nav-dropdown-content a { 
              padding: 8px 12px 8px 30px !important; 
              margin: 0;
              border-radius: 6px;
              color: #a3a3a3 !important;
              transition: all 0.2s ease;
              display: flex;
              align-items: center;
              gap: 10px;
              font-weight: 500;
              white-space: nowrap;
              position: relative;
            }
            #sidebar .nav-dropdown-content a::before {
              content: '';
              position: absolute;
              left: 14px;
              top: 0;
              height: 100%;
              border-left: 1px solid #404040;
            }
            #sidebar .nav-dropdown-content a::after {
              content: '';
              position: absolute;
              left: 14px;
              top: 50%;
              width: 10px;
              border-top: 1px solid #404040;
            }
            #sidebar .nav-dropdown-content a span {
              font-size: 12px;
            }
            #sidebar .nav-dropdown-content a:hover, #sidebar .nav-dropdown-content a:focus {
              background: transparent !important;
              color: #96c36d !important;
            }
            #sidebar .nav-dropdown-content a.active {
              background: transparent !important;
              color: #96c36d !important;
              font-weight: 600;
            }
            #sidebar .foot { border-top: 0px solid #000000; padding: 14px 20px; display: flex; align-items: center; gap: 10px; }
          `}</style>
          <div className="brand">
            <div className="">
              <svg id="Component_1_1" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 80 80" style={{ borderRadius: '3px', background: 'rgb(255 255 255 / 4%)', padding: '3px', height: '45px', width: '45px' }}>
                <rect id="Rectangle_1" data-name="Rectangle 1" fill="none" width="80" height="80"></rect>
                <path id="Path_1" data-name="Path 1" fill="#96c36d" stroke="#96c46d" strokeWidth="1" d="M276.077,242.234,298.6,280.492l22.206-38.258H309.215L298.6,260.248l-10.88-18.014Z" transform="translate(-269 -219)"></path>
                <path id="Path_2" data-name="Path 2" fill="rgb(81, 94, 101)" stroke="rgb(81, 94, 101)" strokeWidth="1" d="M313.4,270.533l6.352,10.066,22.279-38.421H330.019Z" transform="translate(-269 -219)"></path>
              </svg>
            </div>
            <div className="brand-word">
              Visible <span>Worx</span>
            </div>
          </div>

          <div className="nav">
            <div className="nav-label">Project Management</div>

            <a href="#" onClick={(e) => { e.preventDefault(); setView('overview'); window.history.pushState({}, '', window.location.pathname); }}
             
              className={view === 'overview' ? 'active' : ''}>
              <LayoutDashboard />
              <span>Overview</span>
            </a>

            <a href="#" onClick={(e) => { e.preventDefault(); setView('dashboard'); setStatusFilter('all'); window.history.pushState({}, '', window.location.pathname); if (allSummaries.length === 0) loadDashboard(); }}
             
              className={view === 'dashboard' ? 'active' : ''}>
              <Briefcase />
              <span>Active Projects</span>
            </a>

            {canAccessManagementFeatures(userProfile) && (
              <a href="#" onClick={(e) => { e.preventDefault(); setView('newProject'); window.history.pushState({}, '', window.location.pathname); }}
               
                className={view === 'newProject' ? 'active' : ''}>
                <PlusCircle />
                <span>New Project</span>
              </a>
            )}

            <a href="#" onClick={(e) => { e.preventDefault(); setView('pastProjects'); setStatusFilter('all'); window.history.pushState({}, '', window.location.pathname); if (allSummaries.length === 0) loadDashboard(); }}
             
              className={view === 'pastProjects' ? 'active' : ''}>
              <Clock />
              <span>Past Projects</span>
            </a>


            {canAccessManagementFeatures(userProfile) && (
              <>
                <div className="nav-label">Field Management</div>
                <a href="#" onClick={(e) => { e.preventDefault(); setView('callPlanning'); const p = new URLSearchParams(); p.set('view', 'callPlanning'); window.history.pushState({}, '', `?${p.toString()}`); }}
                 
                  className={view === 'callPlanning' ? 'active' : ''}>
                  <Calendar />
                  <span>Call Planning</span>
                </a>

                <div className="nav-label">WHS Management</div>
                <div className="nav-dropdown">
                  <a href="#" onClick={(e) => { e.preventDefault(); setIsWHSMenuOpen(!isWHSMenuOpen); setIsPerfToolsMenuOpen(false); }} className={`flex items-center w-full ${isWHSMenuOpen ? 'active' : ''}`}>
                    <Warehouse />
                    <span style={{ flex: 1 }}>Inventory</span>
                    {isWHSMenuOpen ? <Minus className="w-4 h-4 ml-auto" /> : <Plus className="w-4 h-4 ml-auto" />}
                  </a>
                  {isWHSMenuOpen && (
                    <div className="nav-dropdown-content">
                      <div className="text-[10px] font-bold text-[#626262] uppercase tracking-wider px-3 py-1 mb-1 mt-1">Stock on Hand</div>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('stockOnHandReport'); window.history.pushState({}, '', window.location.pathname + '?view=stockOnHandReport'); }} className={view === 'stockOnHandReport' ? 'active' : ''}>
                        <span>Stock Availability</span>
                      </a>
                      
                      <div className="text-[10px] font-bold text-[#626262] uppercase tracking-wider px-3 py-1 mb-1 mt-2">Movement History</div>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('stockMovementInReport'); window.history.pushState({}, '', window.location.pathname + '?view=stockMovementInReport'); }} className={view === 'stockMovementInReport' ? 'active' : ''}>
                        <span>Stock Movement In</span>
                      </a>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('stockMovementOutReport'); window.history.pushState({}, '', window.location.pathname + '?view=stockMovementOutReport'); }} className={view === 'stockMovementOutReport' ? 'active' : ''}>
                        <span>Stock Movement Out</span>
                      </a>
                    </div>
                  )}
                </div>
                <div className="nav-label">Performance & Tools</div>
                <a href="#" onClick={(e) => { e.preventDefault(); setView('issueTracking'); const p = new URLSearchParams(); p.set('view', 'issueTracking'); window.history.pushState({}, '', `?${p.toString()}`); setIsPerfToolsMenuOpen(false); }} className={`flex items-center w-full ${view === 'issueTracking' ? 'active' : ''}`}>
                  <AlertCircle />
                  <span style={{ flex: 1 }}>Issue Tracking</span>
                </a>
                <div className="nav-dropdown">
                  <a href="#" onClick={(e) => { e.preventDefault(); setIsPerfToolsMenuOpen(!isPerfToolsMenuOpen); setIsWHSMenuOpen(false); }} className={`flex items-center w-full ${isPerfToolsMenuOpen ? 'active' : ''}`}>
                    <LineChart />
                    <span style={{ flex: 1 }}>Performance</span>
                    {isPerfToolsMenuOpen ? <Minus className="w-4 h-4 ml-auto" /> : <Plus className="w-4 h-4 ml-auto" />}
                  </a>
                  {isPerfToolsMenuOpen && (
                    <div className="nav-dropdown-content">
                      <div className="text-[10px] font-bold text-[#626262] uppercase tracking-wider px-3 py-1 mb-1 mt-1">Internal Metrics</div>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('salesCompliance'); const p = new URLSearchParams(); p.set('view', 'salesCompliance'); window.history.pushState({}, '', `?${p.toString()}`); }} className={view === 'salesCompliance' ? 'active' : ''}>
                        <span>Non Compliance: Projects</span>
                      </a>
                      
                      <div className="text-[10px] font-bold text-[#626262] uppercase tracking-wider px-3 py-1 mb-1 mt-2">Client Metrics</div>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('clientCompliance'); const p = new URLSearchParams(); p.set('view', 'clientCompliance'); window.history.pushState({}, '', `?${p.toString()}`); }} className={view === 'clientCompliance' ? 'active' : ''}>
                        <span>Non Compliance: Orders</span>
                      </a>
                      <a href="#" onClick={(e) => { e.preventDefault(); setView('losCompliance'); const p = new URLSearchParams(); p.set('view', 'losCompliance'); window.history.pushState({}, '', `?${p.toString()}`); }} className={view === 'losCompliance' ? 'active' : ''}>
                        <span>Non Compliance: LOS</span>
                      </a>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="nav-label">Reports</div>

            <a href="#" onClick={(e) => { e.preventDefault(); setView('reportsHub'); const p = new URLSearchParams(); p.set('view', 'reportsHub'); window.history.pushState({}, '', `?${p.toString()}`); }}
             
              className={(view === 'reportsHub' || view === 'dynamicReports' || view === 'executiveReport' || view === 'popReport' || view === 'popReportCopy') ? 'active' : ''}>
              <BarChart3 />
              <span>Reports</span>
            </a>

            <a href="#" onClick={(e) => { e.preventDefault(); setView('dataExports'); const p = new URLSearchParams(); p.set('view', 'dataExports'); window.history.pushState({}, '', `?${p.toString()}`); }}
             
              className={view === 'dataExports' ? 'active' : ''}>
              <FileSpreadsheet />
              <span>Data Exports</span>
            </a>

            {canAccessManagementFeatures(userProfile) && (
              <>
                <div className="nav-label">System</div>

                <a href="#" onClick={(e) => { e.preventDefault(); setView('userManagement'); window.history.pushState({}, '', window.location.pathname); }}
                 
                  className={view === 'userManagement' ? 'active' : ''}>
                  <User />
                  <span>User Management</span>
                </a>

                <a href="#" onClick={(e) => { e.preventDefault(); setView('clientPreferences'); window.history.pushState({}, '', window.location.pathname); }}
                 
                  className={view === 'clientPreferences' ? 'active' : ''}>
                  <Settings />
                  <span>Client Preferences</span>
                </a>

              </>
            )}
          </div>


          <div className="foot">
            <div className="avatar">
              {(userProfile?.email?.[0] || 'U').toUpperCase()}
            </div>
            <div className="who">
              <b>{userProfile?.email?.split('@')[0] || 'User'}</b>
              <span>{userProfile?.role || 'Operator'}</span>
            </div>
            <button
              onClick={handleSignOut}
             
              className="text-[#eee] hover:text-slate-600"
              style={{ marginLeft: 'auto', padding: '6px' }}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="shell">
          <header id="topbar" className="topbar">
            <div className="right" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div className="text-right"><p className="crumb" style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>{session?.user?.email?.split('@')[0] || 'System Operator'}</p></div>
              <div className="avatar">{(session?.user?.email?.[0] || 'S').toUpperCase()}</div>
            </div>
          </header>

          <main className="content" style={{ zIndex: view === 'losCompliance' ? 4 : undefined }}>
            <Suspense fallback={<div className="flex flex-col items-center justify-center py-40 space-y-4"><Loader2 className="w-8 h-8 animate-spin text-[#96c36d]" /><p className="text-[10px] font-semibold text-slate-300 ">Loading Data</p></div>}>
              {view === 'reportsHub' ? (
                <div className="space-y-8 animate-in fade-in duration-500">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1e2a32] tracking-tight">Reports</h2>
                    <p className="text-sm text-slate-500 mt-1">Select a report type to generate and view insights.</p>
                  </div>

                  {/* Client Service Category */}
                  <div className="mb-10">
                    <h3 className="text-[15px] px-[10px] font-semibold text-[#1e2a32] tracking-tight mb-5">Client Service Reports</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {/* Dynamic Report Card */}
                      <button
                        onClick={() => { setView('dynamicReports'); const p = new URLSearchParams(); p.set('view', 'dynamicReports'); window.history.pushState({}, '', `?${p.toString()}`); }}
                        className="group relative bg-[#fbfbfa] /80 rounded-xl p-6 text-left transition-all duration-200 hover: hover:/50 hover:border-slate-400/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                          <BarChart3 className="w-5 h-5 text-slate-500" />
                        </div>
                        <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">Dynamic Report</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">Customisable, filter-driven reports with real-time project data and progress tracking.</p>
                        <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </button>

                      {/* Executive Report Card */}
                      {canAccessExecutiveReport(userProfile) && (
                        <button
                          onClick={() => { setView('executiveReport'); const p = new URLSearchParams(); p.set('view', 'executiveReport'); window.history.pushState({}, '', `?${p.toString()}`); }}
                          className="group relative bg-[#fbfbfa] /80 rounded-xl p-6 text-left transition-all duration-200 hover: hover:/50 hover:border-slate-400/30 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                        >
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                            <ShieldCheck className="w-5 h-5 text-slate-500" />
                          </div>
                          <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">Executive Report</h3>
                          <p className="text-xs text-slate-500 leading-relaxed">High-level executive summaries with KPIs, trends and project performance overview.</p>
                          <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                        </button>
                      )}

                      {/* Stock Executive Report Card */}
                      {canAccessExecutiveReport(userProfile) && (
                        <button
                          onClick={() => { setView('popReportCopy'); const p = new URLSearchParams(); p.set('view', 'popReportCopy'); window.history.pushState({}, '', `?${p.toString()}`); }}
                          className="group relative bg-[#fbfbfa] /80 rounded-xl p-6 text-left transition-all duration-200 hover: hover:/50 hover:border-slate-400/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
                        >
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                            <LayoutGrid className="w-5 h-5 text-slate-500" />
                          </div>
                          <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">POP Report (Pdf)</h3>
                          <p className="text-xs text-slate-500 leading-relaxed">Store-level POP insights with audit results and availability metrics.</p>
                          <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                        </button>
                      )}


                    </div>
                  </div>

                  {/* Warehouse Operations Category */}
                  <div>
                    <h3 className="text-[15px] px-[10px] font-semibold text-[#1e2a32] tracking-tight mb-5">Warehouse Operations</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {/* Stock on Hand Report */}
                      <button
                        onClick={() => { setView('stockOnHandReport'); const p = new URLSearchParams(); p.set('view', 'stockOnHandReport'); window.history.pushState({}, '', `?${p.toString()}`); }}
                        className="group relative bg-[#fbfbfa] rounded-xl p-6 text-left transition-all duration-200 hover:bg-white/50 hover:border-slate-400/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30 shadow-sm"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                          <Package className="w-5 h-5 text-slate-500" />
                        </div>
                        <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">Stock on Hand Report</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">Current inventory levels and stock availability across all warehouses.</p>
                        <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </button>

                      {/* Stock Movement In Report */}
                      <button
                        onClick={() => { setView('stockMovementInReport'); const p = new URLSearchParams(); p.set('view', 'stockMovementInReport'); window.history.pushState({}, '', `?${p.toString()}`); }}
                        className="group relative bg-[#fbfbfa] rounded-xl p-6 text-left transition-all duration-200 hover:bg-white/50 hover:border-slate-400/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30 shadow-sm"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                          <ArrowDownToLine className="w-5 h-5 text-slate-500" />
                        </div>
                        <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">Stock Movement In Report</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">Detailed records of all incoming stock receipts and deliveries.</p>
                        <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </button>

                      {/* Stock Movement Out Report */}
                      <button
                        onClick={() => { setView('stockMovementOutReport'); const p = new URLSearchParams(); p.set('view', 'stockMovementOutReport'); window.history.pushState({}, '', `?${p.toString()}`); }}
                        className="group relative bg-[#fbfbfa] rounded-xl p-6 text-left transition-all duration-200 hover:bg-white/50 hover:border-slate-400/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400/30 shadow-sm"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500/10 to-slate-500/5 border border-slate-400/20 flex items-center justify-center mb-4 group-hover:from-slate-500/20 group-hover:to-slate-500/10 transition-all">
                          <ArrowUpFromLine className="w-5 h-5 text-slate-500" />
                        </div>
                        <h3 className="text-sm font-semibold text-[#1e2a32] mb-1.5">Stock Movement Out Report</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">Comprehensive tracking of stock dispatches, allocations, and outbound shipments.</p>
                        <ChevronRight className="absolute top-6 right-5 w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                      </button>
                    </div>
                  </div>
                </div>
              )
                : view === 'reports' || view === 'dynamicReports' ? <Reports />
                  : (view === 'executiveReport' && canAccessExecutiveReport(userProfile)) ? <ExecutiveReport />
                    : (view === 'popReport' && canAccessExecutiveReport(userProfile)) ? <StoreExecutiveReport />
                      : (view === 'popReportCopy' && canAccessExecutiveReport(userProfile)) ? <StoreExecutiveReportCopy />
                      : view === 'stockOnHandReport' ? <StockOnHandReport onBack={() => { setView('reportsHub'); const p = new URLSearchParams(); p.set('view', 'reportsHub'); window.history.pushState({}, '', `?${p.toString()}`); }} />
                        : view === 'stockMovementInReport' ? <StockMovementInReport onBack={() => { setView('reportsHub'); const p = new URLSearchParams(); p.set('view', 'reportsHub'); window.history.pushState({}, '', `?${p.toString()}`); }} />
                          : view === 'stockMovementOutReport' ? <StockMovementOutReport onBack={() => { setView('reportsHub'); const p = new URLSearchParams(); p.set('view', 'reportsHub'); window.history.pushState({}, '', `?${p.toString()}`); }} />
                            : view === 'clientPreferences' ? <ClientPreferences />
                              : view === 'userManagement' ? <UserManagement onSaveChanges={loadDashboard} />
                                : view === 'dataExports' ? <DataExports />
                                  : view === 'callPlanning' ? <CallPlanning user={userProfile || undefined} />
                                    : view === 'newProject' ? <NewProject onBack={() => { setView('dashboard'); loadDashboard(false); window.history.pushState({}, '', window.location.pathname); }} />
                                      : view === 'clientCompliance' ? <ClientCompliance dateRange={{ startDate: '', endDate: '' }} />
                                      : view === 'losCompliance' ? <LosCompliance />
                                      : view === 'salesCompliance' ? <SalesCompliance />

                                      : view === 'issueTracking' ? <IssueReports />
                                        : view === 'callDetail' && selectedStore ? (
                                          <CallDetailsPage storeName={selectedStore.name} projectId={selectedStore.projectId} projectName={selectedStore.projectName} onBack={handleBackToDetail} />
                                        ) : view === 'overview' ? (
                                      <div className="space-y-8 animate-in fade-in duration-500">
                                        <div>
                                          <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-semibold text-[#1e2a32] tracking-tight">Overview</h2>
                                            <SyncIndicator />
                                          </div>
                                          <p className="text-sm text-slate-500 mt-1">A complete overview of your projects, progress, and performance.</p>
                                        </div>
                                        <OverviewPage
                                          summaries={allSummaries.filter(p => !p.project_hidden)}
                                          loading={loading}
                                          onNavigateToProjects={() => { setView('dashboard'); if (allSummaries.length === 0) loadDashboard(); }}
                                          onOpenProject={handleOpenProject}
                                          stockCache={globalStockCache}
                                        />
                                      </div>
                                    ) : (view === 'dashboard' || view === 'pastProjects') ? (
                                      <div className="space-y-10 animate-in fade-in duration-500">
                                        <div>
                                          <div className="flex items-center gap-3">
                                            <h2 className="text-xl font-semibold text-[#1e2a32] tracking-tight">{view === 'dashboard' ? 'Active Projects' : 'Past Projects'}</h2>
                                            <SyncIndicator />
                                          </div>
                                          <p className="text-sm text-slate-500 mt-1">A complete overview of your {view === 'dashboard' ? 'active' : 'past'} projects, progress, and performance.</p>
                                        </div>
                                        {/* Stats row */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                          {[
                                            { label: view === 'dashboard' ? 'Active Projects' : 'Past Projects', value: filteredSummaries.length, icon: Database, color: '#475763' },
                                            { label: 'Avg Progress', value: `${dashboardStats.avg}%`, icon: CheckCircle2, color: '#96c36d' },
                                            { label: 'Projects Ahead', value: dashboardStats.ahead, icon: TrendingUp, color: '#5a8b34' },
                                            { label: 'Projects Behind', value: dashboardStats.behind, icon: TrendingDown, color: '#ef4444' }
                                          ].map((stat, i) => (
                                            <div key={i} className="bg-[#fbfbfa] p-6 rounded-[9px] flex flex-col justify-between h-[130px]">
                                              <div className="flex justify-between items-start">
                                                <span className="text-[13px] font-semibold text-[#5C5D60]">{stat.label}</span>
                                                <stat.icon className="w-4 h-4 text-[#9C9C9A]" strokeWidth={2} />
                                              </div>
                                              <div>
                                                <span className="text-4xl font-bold text-[#141518] tracking-tight">
                                                  {loading && allSummaries.length === 0 ? <Loader2 className="w-8 h-8 animate-spin opacity-20" /> : String(stat.value)}
                                                </span>
                                              </div>
                                              <div>
                                                {/* Subtext can go here if needed in the future, empty for now to match structure */}
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        {/* ── Unified Card for Filters & Controls ── */}
                                        <div className="bg-[#fbfbfa] rounded-[9px]">
                                          <div className="px-[15px] pt-[30px]">
                                            <h3 className="text-lg font-semibold text-[#1e2a32] mb-6">{view === 'dashboard' ? 'Projects' : 'Past Projects'}</h3>

                                            {/* Header Filters */}
                                            <div className="flex flex-wrap items-center gap-[12px] mb-[24px]">
                                              {/* Search */}
                                              <div className="relative shrink-0">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9C9C9A]" />
                                                <input
                                                  type="text"
                                                  placeholder="Search project..."
                                                  value={dashSearch}
                                                  onChange={(e) => { setDashSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
                                                  className="inline-flex items-center gap-[6px] min-w-[150px] w-[240px] h-[45px] pl-[36px] pr-[12px] bg-[#fbfbfa] border border-[#e9e9e9] rounded-[9px] text-[13px] font-medium text-[#111111] transition-all duration-150 outline-none shadow-none focus:border-[#111111] placeholder:text-[#9C9C9A]"
                                                />
                                              </div>

                                              {/* Dropdown */}
                                              <div className="w-[200px] h-[45px] shrink-0">
                                                <SearchableDropdown
                                                  options={uniqueDashClients}
                                                  value={dashClientFilter}
                                                  onChange={(val) => { setDashClientFilter(val); setVisibleCount(PAGE_SIZE); }}
                                                  placeholder="Search..."
                                                  defaultLabel="All"
                                                  className="w-full h-full"
                                                />
                                              </div>

                                              {/* Date Range Picker */}
                                              <div className="shrink-0">
                                                <DateRangePicker
                                                  dateFrom={dateFrom}
                                                  dateTo={dateTo}
                                                  onDateFromChange={(val) => { setDateFrom(val); setVisibleCount(PAGE_SIZE); }}
                                                  onDateToChange={(val) => { setDateTo(val); setVisibleCount(PAGE_SIZE); }}
                                                />
                                              </div>

                                              {/* Checkboxes (Right aligned) */}
                                              <div className="flex items-center gap-2 h-[45px] shrink-0 ml-auto">
                                                <button
                                                  onClick={() => {
                                                    setFilterMissingDocs(false);
                                                    setFilterMissingDelivery(false);
                                                    setFilterDelivered(false);
                                                    setVisibleCount(PAGE_SIZE);
                                                  }}
                                                  className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${isDashAll ? 'bg-slate-100 text-slate-800 border border-slate-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                                                    }`}
                                                >
                                                  <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${isDashAll ? 'bg-slate-500 border-slate-500' : 'border-slate-300'}`}>
                                                    {isDashAll && <Check className="w-2.5 h-2.5 text-white" />}
                                                  </div>
                                                  All
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const val = !filterMissingDocs;
                                                    setFilterMissingDocs(val);
                                                    if (val) { setFilterMissingDelivery(false); setFilterDelivered(false); }
                                                    setVisibleCount(PAGE_SIZE);
                                                  }}
                                                  className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${filterMissingDocs ? 'bg-orange-50 text-orange-600 border border-orange-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                                                    }`}
                                                >
                                                  <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${filterMissingDocs ? 'bg-orange-500 border-orange-500' : 'border-slate-300'}`}>
                                                    {filterMissingDocs && <Check className="w-2.5 h-2.5 text-white" />}
                                                  </div>
                                                  Missing docs
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const val = !filterMissingDelivery;
                                                    setFilterMissingDelivery(val);
                                                    if (val) { setFilterMissingDocs(false); setFilterDelivered(false); }
                                                    setVisibleCount(PAGE_SIZE);
                                                  }}
                                                  className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${filterMissingDelivery ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                                                    }`}
                                                >
                                                  <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${filterMissingDelivery ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
                                                    {filterMissingDelivery && <Check className="w-2.5 h-2.5 text-white" />}
                                                  </div>
                                                  Missing delivery
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    const val = !filterDelivered;
                                                    setFilterDelivered(val);
                                                    if (val) { setFilterMissingDocs(false); setFilterMissingDelivery(false); }
                                                    setVisibleCount(PAGE_SIZE);
                                                  }}
                                                  className={`flex items-center gap-2 px-3 h-full rounded-[9px] text-[12px] font-medium transition-all ${filterDelivered ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                                                    }`}
                                                >
                                                  <div className={`w-3 h-3 rounded-[3px] border flex items-center justify-center ${filterDelivered ? 'bg-green-500 border-green-500' : 'border-slate-300'}`}>
                                                    {filterDelivered && <Check className="w-2.5 h-2.5 text-white" />}
                                                  </div>
                                                  Delivered
                                                </button>
                                              </div>
                                            </div>

                                            <hr className="border-[#e9e9e9] mb-[15px]" />

                                            {/* Toggle removed per user request (Table view is now default and only view) */}
                                          </div>

                                          <div className="p-[15px]">
                                            {loading && visibleSummaries.length === 0 ? (
                                              <div className="flex flex-col items-center justify-center py-32 space-y-4">
                                                <Loader2 className="w-8 h-8 animate-spin text-[#96c36d]" />
                                                <p className="text-[10px] font-semibold text-slate-300 ">Loading Data</p>
                                              </div>
                                            ) : displayMode === 'card' ? (
                                              <>
                                                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-8">
                                                  {visibleSummaries.map((p) => (
                                                    <div key={p.project_id} onClick={() => handleOpenProject(p.project_id)} className="group bg-white border border-[#e9e9e9] rounded-[9px] hover:border-[#96c36d]/30 cursor-pointer flex flex-col transition-all">
                                                      <div className="p-8 pb-6 flex-1">
                                                        <div className="flex justify-between items-start mb-6">
                                                          <div className="flex-1">
                                                            <h3 className="font-semibold text-[#1e2a32] text-lg leading-tight group-hover:text-[#96c36d]">{p.name}</h3>
                                                            <div className="mt-2 text-[9px] font-semibold text-slate-500 ">{p.description || 'Not Specified'}</div>
                                                          </div>
                                                        </div>
                                                        <div className="mb-6">
                                                          <div className="flex items-center justify-between mb-2">
                                                            <span className="text-[10px] font-semibold text-slate-400 ">Progress</span>
                                                            <span className="text-xs font-bold text-[#1e2a32]">{p.stats.totalPerc}%</span>
                                                          </div>
                                                          {p.isStatsLoading
                                                            ? <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full w-1/3 bg-slate-200 rounded-full animate-pulse" /></div>
                                                            : <LineProgress percentage={parseInt(p.stats.totalPerc)} />
                                                          }
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4 mb-8">
                                                          <div className="bg-[#fbfbfa] p-5 rounded-[3px] flex flex-col items-center justify-center"><span className="text-[10px] font-semibold text-slate-900  mb-2">Targets</span>{p.isStatsLoading ? <span className="w-10 h-7 bg-slate-200 rounded animate-pulse" /> : <span className="text-2xl font-bold text-[#475763]">{p.stats.totalCalls}</span>}</div>
                                                          <div className="bg-[#fbfbfa] p-5 rounded-[3px] flex flex-col items-center justify-center"><span className="text-[10px] font-semibold text-slate-900  mb-2">Completed</span>{p.isStatsLoading ? <span className="w-10 h-7 bg-slate-200 rounded animate-pulse" /> : <span className="text-2xl font-bold text-[#96c36d]">{p.stats.completed}</span>}</div>
                                                        </div>
                                                        <div className="pt-6 border-t border-slate-50 flex items-start justify-between gap-4">
                                                          <div className="flex-1"><DocumentStatus project={p} /></div>
                                                          <div className="shrink-0 text-right">{!p.isStatsLoading && <PaceIndicator stats={p.stats} />}</div>
                                                        </div>
                                                        <div className="mt-6"><WarehouseAvailability availability={p.warehouse_availability} cache={globalStockCache} /></div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                                {/* Infinity scroll sentinel */}
                                                <div ref={infiniteScrollSentinelRef} className="h-4" />
                                                {canLoadMore && loading && (
                                                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#96c36d] opacity-40" /></div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="space-y-4">
                                                <ProjectTable
                                                  projects={visibleSummaries}
                                                  onOpenProject={handleOpenProject}
                                                  loading={loading}
                                                  stockCache={globalStockCache}
                                                />
                                                {/* Infinity scroll sentinel */}
                                                <div ref={infiniteScrollSentinelRef} className="h-4" />
                                                {canLoadMore && (
                                                  <div className="flex justify-center py-6 text-[12px] text-slate-400 font-bold">
                                                    <Loader2 className="w-4 h-4 animate-spin text-[#96c36d] opacity-50 mr-3" /> Fetching more records…
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ) : view === 'detail' ? (
                                      <div className="animate-in fade-in duration-500 space-y-12 pb-20">
                                        {loading ? (
                                          <div className="flex flex-col items-center justify-center py-40 space-y-5">
                                            <Loader2 className="w-12 h-12 animate-spin text-[#96c36d] opacity-20" />
                                            <p className="text-[10px] font-semibold text-slate-300 ">Generating Field Intelligence Report</p>
                                          </div>
                                        ) : reportData ? (
                                          <div className="grid grid-cols-1 xl:grid-cols-4 gap-12 items-start">
                                            <div className="xl:col-span-4 space-y-12">
                                              {/* Project header card */}
                                              <div className="bg-[#fbfbfa] rounded-[9px] p-12 overflow-hidden">

                                                {/* ── Row 1: Action buttons ── */}
                                                <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
                                                  {/* Left Group */}
                                                  <div className="flex items-center gap-3">
                                                    <button
                                                      onClick={() => { setView('dashboard'); setStatusFilter('all'); window.history.pushState({}, '', window.location.pathname); if (allSummaries.length === 0) loadDashboard(); }}
                                                      className="bg-[#1f2a32] text-white hover:bg-[#2f3d47] w-[45px] h-[45px] rounded-[9px] flex items-center justify-center transition-all active:scale-[0.98] mr-4"
                                                     
                                                    >
                                                      <ArrowLeft className="w-5 h-5 text-white" />
                                                    </button>
                                                    {/* Archived badge inline if deactivated */}
                                                    {reportData.project.project_hidden && (
                                                      <span className="inline-flex items-center h-[45px] px-4 bg-amber-50 text-amber-600 text-[11px] font-bold rounded-[9px]">Archived</span>
                                                    )}
                                                  </div>

                                                  {/* Right Group */}
                                                  <div className="flex flex-wrap items-center gap-3">
                                                    {/* Invoiced Toggle — only for managers/admins */}
                                                    {(userProfile?.role === 'Project Manager' || userProfile?.role === 'admin') && (
                                                      <button
                                                        onClick={() => handleToggleProjectInvoiced(reportData.project.project_id, !reportData.project.invoiced)}
                                                        className={`text-[12px] h-[45px] px-5 rounded-[9px] font-semibold flex items-center gap-2 transition-all active:scale-[0.98] ${reportData.project.invoiced ? 'bg-[#9cc16b]/20 text-[#5a8b34] hover:bg-[#9cc16b]/30' : 'bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
                                                      >
                                                        {reportData.project.invoiced
                                                          ? <><CheckCircle2 className="w-4 h-4" /> Invoiced</>
                                                          : <><FileText className="w-4 h-4 opacity-60" /> Mark as Invoiced</>
                                                        }
                                                      </button>
                                                    )}
                                                    {[
                                                      { icon: Briefcase, label: 'Manage Docs', onClick: () => setIsDocManagerOpen(true) },
                                                    ].map(btn => (
                                                      <button key={btn.label} onClick={btn.onClick} className="bg-white text-slate-500 hover:text-[#1e2a32] text-[12px] h-[45px] px-5 rounded-[9px] font-semibold flex items-center gap-2 transition-all active:scale-[0.98]">
                                                        <btn.icon className="w-4 h-4 opacity-60" /> {btn.label}
                                                      </button>
                                                    ))}
                                                    <button onClick={handleExport} disabled={exporting} className="bg-[#1e2a32] hover:bg-[#2f3d47] text-white text-[12px] h-[45px] px-5 rounded-[9px] font-semibold flex items-center gap-2 active:scale-[0.98] disabled:opacity-50">
                                                      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{exporting ? 'Compiling' : 'Export Excel'}
                                                    </button>
                                                    <button
                                                      onClick={async () => {
                                                        if (!reportData) return;
                                                        setExportingPDF(true); setPdfExportProgress('Connecting...');
                                                        try {
                                                          const storeList = filteredStoreBreakdown?.data ?? [];
                                                          if (storeList.length === 0) { alert('No stores found to export.'); return; }
                                                          const storeNames = storeList.map(s => s.name);
                                                          setPdfExportProgress('Fetching stores...');
                                                          const { data: allTargets } = await supabase.from('targets').select('target_id, name').eq('project_id', reportData.project.project_id).in('name', storeNames);
                                                          if (!allTargets || allTargets.length === 0) { alert('Could not find target records.'); return; }
                                                          const targetIds = allTargets.map(t => t.target_id);
                                                          const targetMap = allTargets.reduce((acc: any, t) => { acc[t.target_id] = t.name; return acc; }, {});
                                                          setPdfExportProgress('Fetching reports...');
                                                          const { data: allCalls } = await supabase.from('calls').select('*').in('target_id', targetIds).order('actual_start', { ascending: false });
                                                          if (!allCalls || allCalls.length === 0) { alert('No visit records found.'); return; }
                                                          const latestCallsMap = new Map();
                                                          allCalls.forEach(call => { if (!latestCallsMap.has(call.target_id)) latestCallsMap.set(call.target_id, call); });
                                                          const relevantCalls = Array.from(latestCallsMap.values()).filter(call => call.actual_start);
                                                          const callIds = relevantCalls.map(c => c.call_id);
                                                          const questionnaireIds = Array.from(new Set(relevantCalls.map(c => c.questionnaire_id).filter(id => id)));
                                                          setPdfExportProgress('Loading logic...');
                                                          const { data: allQuestionnaires } = await supabase.from('questionnaires').select('*').in('questionnaire_id', questionnaireIds);
                                                          const qMap = (allQuestionnaires || []).reduce((acc: any, q) => { acc[q.questionnaire_id] = q; return acc; }, {});
                                                          setPdfExportProgress('Fetching data...');
                                                          const { data: allAnswers } = await supabase.from('answers').select('*').in('call_id', callIds);
                                                          const answersMap = (allAnswers || []).reduce((acc: any, ans) => { if (!acc[ans.call_id]) acc[ans.call_id] = []; acc[ans.call_id].push(ans); return acc; }, {});
                                                          const storePages: SingleStoreExportData[] = [];
                                                          relevantCalls.forEach(call => {
                                                            const sn = targetMap[call.target_id];
                                                            const qData = qMap[call.questionnaire_id];
                                                            if (sn && qData) storePages.push({ storeName: sn, projectName: reportData.project.name, callData: call, questionnaire: qData, answers: answersMap[call.call_id] ?? [] });
                                                          });
                                                          if (storePages.length === 0) { alert('No completed reports found.'); return; }
                                                          await exportProjectPDF({ projectName: reportData.project.name, stores: storePages }, (current, total) => { setPdfExportProgress(`Compiling (${current}/${total})`); });
                                                        } catch (err: any) { setError(err.message || 'Failed to generate PDF'); } finally { setExportingPDF(false); setPdfExportProgress(''); }
                                                      }}
                                                      disabled={exportingPDF}
                                                      className="bg-[#E2E8F0] hover:bg-[#CBD5E1] text-[#475763] text-[12px] h-[45px] px-5 rounded-[9px] font-semibold flex items-center gap-2 active:scale-[0.98] disabled:opacity-50"
                                                    >
                                                      {exportingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}{exportingPDF ? pdfExportProgress || 'Compiling' : 'Export PDF'}
                                                    </button>
                                                    {/* Deactivate / Reactivate — only for managers/admins */}
                                                    {(userProfile?.role === 'Project Manager' || userProfile?.role === 'admin') && (
                                                      <button
                                                        onClick={() => handleToggleProjectHidden(reportData.project.project_id, !reportData.project.project_hidden)}
                                                        className={`text-[12px] h-[45px] px-5 rounded-[9px] font-semibold flex items-center gap-2 transition-all active:scale-[0.98] ${reportData.project.project_hidden ? 'bg-blue-50 text-blue-600 hover:bg-blue-100' : 'bg-white text-slate-500 hover:text-red-500 hover:bg-red-50'}`}
                                                      >
                                                        {reportData.project.project_hidden
                                                          ? <><CheckCircle2 className="w-4 h-4" /> Reactivate Project</>
                                                          : <><X className="w-4 h-4 opacity-60" /> Deactivate Project</>
                                                        }
                                                      </button>
                                                    )}
                                                  </div>
                                                  <div className="flex-1" />
                                                  <div className="shrink-0 h-[45px] flex items-center"><PaceIndicator stats={reportData.stats} /></div>
                                                </div>

                                                {/* ── Row 2: Project ID + Name + Description ── */}
                                                <p className="text-slate-400 font-normal text-[10px] leading-relaxed mb-1">Project ID: {reportData.project.project_id}</p>
                                                <h2 className="text-3xl font-semibold text-[#1e2a32] tracking-tight mb-3">{reportData.project.name}</h2>
                                                <p className="text-slate-400 font-normal text-[11px] leading-relaxed">{reportData.project.description || 'Campaign validation metrics and field evidence summary.'}</p>

                                                {/* ── Row 3: Stats ── */}
                                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-8 pt-8 border-t border-slate-100">
                                                  {[
                                                    { l: 'Targets', v: reportData.stats.totalCalls, icon: MapPin },
                                                    { l: 'Verified Visits', v: reportData.stats.visited, icon: Clock },
                                                    { l: 'Completed', v: reportData.stats.completed, icon: CheckCircle2 },
                                                    { l: 'Progress', v: reportData.stats.totalPerc, icon: BarChart3 },
                                                  ].map((stat, i) => (
                                                    <div key={i} className="flex items-center gap-5">
                                                      <div className="w-10 h-10 rounded-[9px] bg-[#2f3d47] flex items-center justify-center text-slate-300">
                                                        <stat.icon className="w-4 h-4 text-[#fff]" />
                                                      </div>
                                                      <div>
                                                        <p className="text-[11px] font-semibold text-slate-900 mb-1">{stat.l}</p>
                                                        <p className="text-xl font-semibold tracking-tight text-[#1e2a32]">{stat.v}</p>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>

                                                {/* ── Row 4: Pilot Installation + Pace Indicator ── */}
                                                <div className="pt-8 flex items-end justify-between gap-6">
                                                  <div>
                                                    <p className="text-[14px] font-semibold text-slate-900">Pilot Installation</p>
                                                    <div className="flex flex-wrap items-center gap-2.5">
                                                      <div className={`flex items-center gap-2 text-[10px] font-bold ${reportData.project.pilot_installation_required?.required ? 'text-orange-600' : 'text-slate-500'}`}>
                                                        <span>{reportData.project.pilot_installation_required?.required ? 'Required' : 'Not Required'}</span>
                                                      </div>
                                                      {reportData.project.pilot_installation_required?.required && reportData.project.pilot_installation_required.target_store && (
                                                        <div className="flex items-center gap-2 text-[10px] font-bold text-blue-600">
                                                          <MapPin className="w-3 h-3" /><span>{reportData.project.pilot_installation_required.target_store}</span>
                                                        </div>
                                                      )}
                                                      {reportData.project.pilot_installation_required?.required && reportData.project.pilot_installation_required.proposed_date && (() => {
                                                        const pd = new Date(reportData.project.pilot_installation_required.proposed_date);
                                                        const dateStr = pd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                                                        const timeStr = pd.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                                                        const projectName = reportData.project.name;
                                                        const targetStore = reportData.project.pilot_installation_required.target_store || '';
                                                        const eventTitle = `Pilot Installation: ${projectName}${targetStore ? ` @ ${targetStore}` : ''}`;
                                                        const eventDetails = `Pilot installation for ${projectName}${targetStore ? `\nLocation: ${targetStore}` : ''}`;
                                                        const toIcal = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                                                        const endDate = new Date(pd.getTime() + 60 * 60 * 1000);
                                                        const icalContent = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VMS//EN', 'BEGIN:VEVENT', `DTSTART:${toIcal(pd)}`, `DTEND:${toIcal(endDate)}`, `SUMMARY:${eventTitle}`, `DESCRIPTION:${eventDetails.replace(/\n/g, '\\n')}`, 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
                                                        const handleDownloadIcal = () => { const blob = new Blob([icalContent], { type: 'text/calendar' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'pilot-installation.ics'; a.click(); URL.revokeObjectURL(url); };
                                                        const handleEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent(eventTitle)}&body=${encodeURIComponent(`${eventDetails}\n\nDate: ${dateStr}\nTime: ${timeStr}`)}`; };
                                                        const handleWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(`📅 *${eventTitle}*\n📆 ${dateStr} at ${timeStr}${targetStore ? `\n📍 ${targetStore}` : ''}`)}`, '_blank'); };
                                                        return (
                                                          <>
                                                            <div className="flex items-center gap-2 text-[10px] font-bold text-amber-800">
                                                              <Calendar className="w-3 h-3" /><span>{dateStr}</span><span className="text-amber-600 font-semibold opacity-80">{timeStr}</span>
                                                            </div>
                                                            <div className="relative group">
                                                              <button className="flex items-center gap-1.5 pl-2 pr-3 py-1 text-[10px] font-bold rounded-[9px] bg-[#1e2a32] text-white hover:bg-[#2f3d47] transition-all">
                                                                <Plus className="w-3 h-3" /><span>Add to Calendar</span><ChevronDown className="w-3 h-3 opacity-60" />
                                                              </button>
                                                              <div className="absolute left-0 top-full mt-2 w-52 bg-white rounded-[9px] overflow-hidden z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
                                                                <button onClick={handleDownloadIcal} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-medium text-[#1e2a32] hover:bg-slate-50 text-left"><Calendar className="w-4 h-4 text-slate-400 shrink-0" /><span>Download .ics file</span></button>
                                                                <button onClick={handleEmail} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-medium text-[#1e2a32] hover:bg-slate-50 text-left"><ArrowRight className="w-4 h-4 text-slate-400 shrink-0" /><span>Share via Email</span></button>
                                                                <button onClick={handleWhatsApp} className="w-full flex items-center gap-3 px-4 py-3 text-xs font-medium text-[#1e2a32] hover:bg-slate-50 text-left"><Share2 className="w-4 h-4 text-slate-400 shrink-0" /><span>Share via WhatsApp</span></button>
                                                              </div>
                                                            </div>
                                                          </>
                                                        );
                                                      })()}
                                                    </div>
                                                  </div>
                                                  <div className="pb-1 shrink-0 flex items-center gap-3">
                                                    {[
                                                      { label: 'Brief', url: reportData.project.brief_document_url },
                                                      { label: 'PO', url: reportData.project.purchase_order_document_url },
                                                      { label: 'CE', url: reportData.project.cost_estimate_document_url }
                                                    ].filter(doc => doc.url).map(doc => (
                                                      <a
                                                        key={doc.label}
                                                        href={doc.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="h-[32px] px-3 flex items-center gap-2 rounded-[9px] bg-white text-slate-500 hover:text-[#96c36d] hover:bg-[#96c36d]/5 transition-all text-[10px] font-bold"
                                                      >
                                                        <FileText className="w-3 h-3" />
                                                        {doc.label}
                                                      </a>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Tab bar */}
                                              <div className="mb-4">
                                                <div className="inline-flex bg-[#eaeaea] p-[2px] rounded-[8px] gap-[2px] flex-wrap">
                                                  {[
                                                    { key: 'breakdown' as const, label: 'Store Breakdown', badge: null },
                                                    { key: 'velocity' as const, label: 'Execution Rate', badge: null },
                                                    { key: 'visuals' as const, label: 'Visual Evidence', badge: null },
                                                    { key: 'questionnaire' as const, label: 'Questionnaire', badge: questionnaireCount },
                                                    { key: 'readiness' as const, label: 'PoS Readiness/ Availability', badge: null },
                                                  ].map(tab => (
                                                    <button key={tab.key} onClick={() => setDetailTab(tab.key)}
                                                      className={`flex items-center gap-[6px] px-[10px] py-[4px] text-[12px] font-medium rounded-[6px] cursor-pointer transition-all duration-150 outline-none ${detailTab === tab.key ? 'bg-[#fbfbfa] text-[#111827] shadow-none' : 'bg-transparent text-[#64748b] shadow-none hover:text-[#111827]'}`}>
                                                      {/* <tab.icon className={`w-3.5 h-3.5 ${detailTab === tab.key ? 'text-[#96c36d]' : 'text-[#64748b]'}`} /> */}
                                                      {tab.label}
                                                      {tab.badge !== null && tab.badge !== undefined && (
                                                        <span className={`px-[6px] py-[2px] rounded-full text-[10px] font-semibold ${detailTab === tab.key ? 'bg-[#f3f4f6] text-[#111827]' : 'bg-[#e2e8f0] text-[#64748b]'}`}>
                                                          {tab.badge}
                                                        </span>
                                                      )}
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>

                                              {/* Tab content */}
                                              {detailTab === 'velocity' && (
                                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500"><ProjectTimingAnalysis reportData={reportData} /></div>
                                              )}

                                              {detailTab === 'questionnaire' && (
                                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                                  <QuestionnaireTab projectId={reportData.project.project_id} onCountLoaded={setQuestionnaireCount} />
                                                </div>
                                              )}

                                              {detailTab === 'readiness' && (
                                                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                                  <ElementReadinessTab
                                                    project={reportData.project}
                                                    onSave={async (newAvailability) => {
                                                      await updateProjectWarehouseAvailability(reportData.project.project_id, newAvailability);
                                                      // Synchronize dashboard summaries immediately
                                                      setAllSummaries(prev => prev.map(p =>
                                                        p.project_id === reportData.project.project_id
                                                          ? { ...p, warehouse_availability: newAvailability }
                                                          : p
                                                      ));
                                                      setReportData(await fetchProjectData(reportData.project.project_id));
                                                    }}
                                                    stockCache={globalStockCache}
                                                  />
                                                </div>
                                              )}

                                              {detailTab === 'breakdown' && (
                                                <div className="bg-[#fbfbfa] rounded-[9px] animate-in fade-in slide-in-from-bottom-4 duration-500">
                                                  <div className="px-[15px] pt-[30px] pb-6">
                                                    <h3 className="text-lg font-semibold text-[#1e2a32]">Operational store breakdown</h3>
                                                  </div>
                                                  <hr className="border-[#e9e9e9] mx-[15px]" />
                                                  <div className="px-[15px] pt-[24px] pb-[15px]">
                                                    <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                                                      <div className="inline-flex bg-[#f3f4f6] p-[2px] rounded-[8px] gap-[2px] flex-wrap">
                                                        {[
                                                          { key: 'all' as const, label: 'All', count: filteredStoreBreakdown.counts.all },
                                                          { key: 'completed' as const, label: 'Completed', count: filteredStoreBreakdown.counts.completed },
                                                          { key: 'planned' as const, label: 'Planned', count: filteredStoreBreakdown.counts.planned },
                                                          { key: 'unplanned' as const, label: 'Unplanned', count: filteredStoreBreakdown.counts.unplanned },
                                                          { key: 'timeline' as const, label: 'Timeline', count: null },
                                                        ].map(tab => (
                                                          <button key={tab.key} onClick={() => updateStatusFilter(tab.key as any)}
                                                            className={`flex items-center gap-[6px] px-[10px] py-[4px] text-[12px] font-medium rounded-[6px] cursor-pointer transition-all duration-150 outline-none ${statusFilter === tab.key ? 'bg-white text-[#111827] shadow-none' : 'bg-transparent text-[#64748b] shadow-none hover:text-[#111827]'}`}>
                                                            {tab.label}
                                                            {tab.count !== null && (
                                                              <span className={`px-[6px] py-[2px] rounded-full text-[10px] font-semibold ${statusFilter === tab.key ? 'bg-[#f3f4f6] text-[#111827]' : 'bg-[#e2e8f0] text-[#64748b]'}`}>{tab.count}</span>
                                                            )}
                                                          </button>
                                                        ))}
                                                      </div>
                                                      {statusFilter !== 'timeline' && (
                                                        <div className="relative w-full sm:w-64">
                                                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                                          <input type="text" placeholder="Filter stores..." value={storeSearchQuery} onChange={(e) => setStoreSearchQuery(e.target.value)}
                                                            className="w-full h-9 bg-white rounded-[9px] pl-9 pr-3 text-xs font-normal text-[#1e2a32] focus:ring-1 focus:ring-[#96c36d]/30 outline-none placeholder:text-slate-300" />
                                                        </div>
                                                      )}
                                                    </div>

                                                    {/* ── TIMELINE VIEW ── */}
                                                    {statusFilter === 'timeline' ? (
                                                      <StoreTimeline
                                                        storeBreakdown={reportData.storeBreakdown}
                                                        projectId={reportData.project.project_id}
                                                        projectName={reportData.project.name}
                                                        onOpenStore={handleOpenCallDetails}
                                                      />
                                                    ) : (
                                                      // ── REGULAR TABLE ──
                                                      <div className="bg-white rounded-[9px] overflow-hidden">
                                                        <div className="overflow-auto custom-scrollbar">
                                                          <table className="w-full text-left border-separate border-spacing-0">
                                                            <thead className="sticky top-0 z-10">
                                                              <tr className="bg-[#fbfbfa]">
                                                                <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] w-16">#</th>
                                                                <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-left">Store Name</th>
                                                                <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-left">Status</th>
                                                                <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-left">Visit Date</th>
                                                                {isAdmin(userProfile?.role) && <th className="px-4 py-4 text-[13px] font-semibold text-[#5C5D60] border-b border-[#E7E7E4] text-center w-24">Urgent</th>}
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {filteredStoreBreakdown.data.length > 0 ? (
                                                                filteredStoreBreakdown.data.map((s: any, idx: number) => (
                                                                  <tr key={idx} onClick={() => handleOpenCallDetails(s.name, reportData.project.project_id, reportData.project.name)}
                                                                    className={`cursor-pointer group transition-colors ${idx % 2 === 0 ? 'bg-[#fbfbfa] hover:bg-slate-50/50' : 'bg-slate-50/30 hover:bg-slate-50/50'}`}>
                                                                    <td className="px-4 py-4 text-sm font-normal text-slate-400 border-b border-[rgb(226,231,240)]">{idx + 1}</td>
                                                                    <td className="px-4 py-4 text-sm font-normal text-[#1e2a32] group-hover:text-[#96c36d] border-b border-[rgb(226,231,240)]">{s.name}</td>
                                                                    <td className="px-4 py-4 text-xs border-b border-[rgb(226,231,240)]">
                                                                      <span className={`px-3 py-1 rounded-[9px] text-[11px] font-bold ${s.status === 'Completed' ? 'bg-[#96c36d]/10 text-[#5a8b34]' : s.status === 'Planned' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{s.status}</span>
                                                                    </td>
                                                                    <td className="px-4 py-4 text-xs text-slate-400 font-normal border-b border-[rgb(226,231,240)]">{s.date}</td>
                                                                    {isAdmin(userProfile?.role) && (
                                                                      <td className="px-4 py-4 text-center border-b border-[rgb(226,231,240)]">
                                                                        <button
                                                                          onClick={(e) => handleToggleUrgent(e, s)}
                                                                          title={s.urgent ? s.urgency_reason : 'Mark as urgent'}
                                                                          className={`relative inline-flex h-[18px] w-[32px] shrink-0 cursor-pointer rounded-full p-[2px] transition-colors duration-200 ease-in-out focus:outline-none shadow-inner ${s.urgent ? 'bg-[#E06666]' : 'bg-slate-200 hover:bg-slate-300'}`}
                                                                        >
                                                                          <span className="sr-only">Toggle Urgent</span>
                                                                          <span
                                                                            aria-hidden="true"
                                                                            className={`pointer-events-none inline-block h-[14px] w-[14px] transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${s.urgent ? 'translate-x-[14px]' : 'translate-x-0'}`}
                                                                          />
                                                                        </button>
                                                                      </td>
                                                                    )}
                                                                  </tr>
                                                                ))
                                                              ) : (
                                                                <tr><td colSpan={isAdmin(userProfile?.role) ? 5 : 4} className="px-6 py-12 text-center"><div className="flex flex-col items-center justify-center"><Database className="w-10 h-10 text-slate-200 mb-3" /><p className="text-sm font-medium text-slate-400">{storeSearchQuery.trim() ? 'No stores match your search' : 'No stores in this category'}</p></div></td></tr>
                                                              )}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              )}

                                              {detailTab === 'visuals' && (
                                                <div className="bg-[#fbfbfa] rounded-[9px] animate-in fade-in slide-in-from-bottom-4 duration-500">
                                                  <div className="px-[15px] pt-[30px] pb-6 flex items-center justify-between">
                                                    <h3 className="text-lg font-semibold text-[#1e2a32]">Visual evidence</h3>
                                                    <span className="text-[11px] font-semibold text-slate-400 bg-white px-4 py-2 rounded-[9px]">{reportData.detailedPhotos.length} media logs</span>
                                                  </div>
                                                  <hr className="border-[#e9e9e9] mx-[15px]" />
                                                  <div className="px-[15px] pt-[24px] pb-[15px]">
                                                    {reportData.detailedPhotos.length > 0 ? (
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                                                        {reportData.detailedPhotos.map((item, idx) => (
                                                          <div key={idx} className="group bg-white rounded-[9px] overflow-hidden">
                                                            <div className="aspect-video overflow-hidden bg-slate-50">
                                                              {item.afterPhoto ? <img src={item.afterPhoto} className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center text-slate-200">No Image</div>}
                                                            </div>
                                                            <div className="p-7 flex justify-between items-center">
                                                              <div>
                                                                <h4 className="font-medium text-[#1e2a32] text-base group-hover:text-[#96c36d]">{item.storeName}</h4>
                                                                <div className="flex items-center gap-2 mt-2"><Clock className="w-3.5 h-3.5 text-[#96c36d]" /><span className="text-[11px] font-medium text-[#96c36d]">{new Date(item.date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span></div>
                                                              </div>
                                                              <button onClick={() => setWatermarkData({ url: item.afterPhoto, ts: item.timestamp, store: item.storeName, gpsCoordinates: item.gpsCoordinates })} className="p-3 rounded-[9px] bg-[#fbfbfa] text-slate-300 hover:bg-[#96c36d] hover:text-white"><ExternalLink className="w-4 h-4" /></button>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <div className="py-40 text-center rounded-[9px] flex flex-col items-center justify-center">
                                                        <Database className="w-10 h-10 text-slate-200 mb-6" /><p className="text-slate-300 font-medium text-sm">Awaiting field media</p>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
            </Suspense>
            {/* Modals */}
            {urgencyModalTarget && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0">
                <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={() => setUrgencyModalTarget(null)}></div>
                <div className="relative bg-[#fbfbfa] w-full max-w-md flex flex-col rounded-[3px] overflow-hidden">
                  <header className="px-6 h-16 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between shrink-0">
                    <h3 className="text-lg font-semibold text-[#1e2a32]">Mark Target as Urgent</h3>
                    <button onClick={() => setUrgencyModalTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 text-slate-300"><X className="w-5 h-5" /></button>
                  </header>
                  <div className="p-6">
                    <p className="text-sm text-slate-500 mb-4">Please provide a reason why <span className="font-semibold text-[#1e2a32]">{urgencyModalTarget.name}</span> is urgent:</p>
                    <textarea
                      autoFocus
                      className="w-full h-32 p-3 border border-slate-200 rounded-[9px] text-sm focus:outline-none focus:ring-1 focus:ring-[#96c36d]/50 bg-white"
                      placeholder="e.g. Critical stock shortage"
                      value={urgencyReasonText}
                      onChange={(e) => setUrgencyReasonText(e.target.value)}
                    ></textarea>
                  </div>
                  <footer className="px-6 py-4 bg-white border-t border-slate-50 flex justify-end gap-3 shrink-0">
                    <button onClick={() => setUrgencyModalTarget(null)} className="px-5 py-2 text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors">Cancel</button>
                    <button onClick={handleSaveUrgent} disabled={!urgencyReasonText.trim()} className="px-6 py-2 bg-[#E06666] hover:bg-[#d55555] text-white text-sm font-semibold rounded-[3px] transition-all disabled:opacity-50 flex items-center gap-2">Save</button>
                  </footer>
                </div>
              </div>
            )}
            {isPosManagerOpen && currentProjectSummary && <PosManager project={currentProjectSummary} onClose={() => setIsPosManagerOpen(false)} onSave={handleSavePosAvailability} />}
            {isDocManagerOpen && currentProjectSummary && <DocManager project={currentProjectSummary} onClose={() => setIsDocManagerOpen(false)} onSave={handleSaveDocuments} />}

            {isIssueModalOpen && reportData && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 m-0">
                <div className="absolute inset-0 bg-[rgb(25,34,41)]/60 backdrop-blur-[2px]" onClick={() => setIsIssueModalOpen(false)}></div>
                <div className="relative bg-[#fbfbfa] w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[3px] overflow-hidden">
                  <header className="px-8 h-20 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3"><AlertCircle className="w-4 h-4 text-[#E06666] opacity-60" /><div><h3 className="text-lg font-semibold text-[#1e2a32]">Friction Log</h3><p className="text-[10px] font-semibold text-slate-400 ">Incident tracking for {reportData.project.name}</p></div></div>
                    <button onClick={() => setIsIssueModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 text-slate-300"><X className="w-5 h-5" /></button>
                  </header>
                  <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">
                    {reportData.project.project_issues?.length ? reportData.project.project_issues.map((issue) => (
                      <div key={issue.id} className={`p-5 rounded-[12px] border transition-all ${issue.status === 'open' ? 'bg-[#fbfbfa] border-slate-100' : 'bg-slate-50 border-slate-50 opacity-60'}`}>
                        <div className="flex justify-between gap-4">
                          <p className={`text-sm leading-relaxed flex-1 ${issue.status === 'open' ? 'text-[#1e2a32] font-medium' : 'text-slate-300 line-through font-normal'}`}>{issue.text}</p>
                          <button onClick={() => toggleIssueStatus(issue.id)} className={`shrink-0 w-8 h-8 rounded-[3px] flex items-center justify-center border transition-all ${issue.status === 'open' ? 'border-[#96c36d]/30 text-[#96c36d] hover:bg-[#96c36d] hover:text-white' : 'bg-[#96c36d] border-[#96c36d] text-white'}`}><Check className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-50">
                          <span className="text-[10px] text-slate-300 font-semibold">{new Date(issue.created_at).toLocaleString()}</span>
                          <span className={`text-[8px] font-semibold  px-2 py-0.5 rounded-[3px] ${issue.status === 'open' ? 'bg-[#E06666]/5 text-[#E06666]' : 'bg-slate-100 text-slate-400'}`}>{issue.status}</span>
                        </div>
                      </div>
                    )) : <div className="text-center py-10 opacity-20"><ShieldCheck className="w-10 h-10 mx-auto mb-4" /><p className="text-[10px] font-semibold ">No site friction points recorded</p></div>}
                  </div>
                  <footer className="p-8 bg-slate-50/50 border-t border-slate-50 shrink-0">
                    <div className="flex flex-col gap-4">
                      <textarea placeholder="Describe the friction point..." className="w-full bg-[#fbfbfa] rounded-[12px] p-4 text-sm focus:ring-1 focus:ring-[#96c36d]/30 outline-none text-[#1e2a32] placeholder:text-slate-200 min-h-[100px] resize-none" value={newIssue} onChange={(e) => setNewIssue(e.target.value)} />
                      <button onClick={handleAddIssue} disabled={addingIssue || !newIssue.trim()} className="w-full bg-[#96c36d] hover:bg-[#86b160] disabled:bg-slate-100 text-white h-11 rounded-[11px] font-semibold text-sm flex items-center justify-center gap-3 active:scale-[0.98]">{addingIssue ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Register Incident</button>
                    </div>
                  </footer>
                </div>
              </div>
            )}

            {watermarkData && (
              <WatermarkViewer
                imageUrl={watermarkData.url}
                timestamp={watermarkData.ts}
                storeName={watermarkData.store}
                gpsCoordinates={watermarkData.gpsCoordinates}
                onClose={() => setWatermarkData(null)}
              />
            )}

            <NetworkStatusToast />

            {globalPosModalProject && (
              <PosAvailabilityModal
                project={globalPosModalProject}
                cache={globalStockCache}
                onClose={() => setGlobalPosModalProject(null)}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default App;